/**
 * Fenny Answer Library (AEO)
 *
 * Central, canonical source of truth for short + long answers to the
 * questions Fenny's audience actually asks. Used to power:
 *
 *   - <QuickAnswer> block under H1 on key pages
 *   - FAQ sections (rendered + JSON-LD)
 *   - /fragor/<slug> question pages (Iter 2)
 *   - llms.txt / llms-full.txt / llms.json (regenerated from this file)
 *   - JSON-LD FAQPage / QAPage / HowTo blocks
 *
 * Quality bar:
 *   - shortAnswer: 1–2 meningar, citerbart, fakta först.
 *   - longAnswer:  3–6 stycken som rymmer kontext, exempel, nyans.
 *   - Inga osäkra konkurrentpåståenden eller påhittade siffror.
 *   - lastReviewed uppdateras varje gång svaret faktagranskas.
 */

export type Language = "sv" | "en";

export type AnswerSchemaType = "FAQPage" | "QAPage" | "HowTo";

export interface FennyAnswer {
  /** Stable id, e.g. "sv_vad_ar_film_pa_abonnemang". */
  id: string;
  language: Language;
  question: string;
  /** Sidans synliga H1 när den behöver skilja sig från frågan i FAQ och navigering. */
  heading?: string;
  /** 1–2 meningar, citerbart i AI-svar. */
  shortAnswer: string;
  /** 3–6 stycken, ren markdown (renderas som <p>...</p>). */
  longAnswer: string;
  /** Primary Fenny URL this answer points to (path, e.g. "/pulse"). */
  relatedPage: string;
  /** Service name when different from relatedPage (e.g. "Fenny Pulse"). */
  relatedService?: string;
  /** Optional comparison page this answer relates to (e.g. "/jamfor/fenny-vs-filmbyran"). */
  relatedComparison?: string;
  /** Other answer ids that belong in "Relaterade frågor". */
  relatedQuestions: string[];
  /** Topic tags — used for hub pages and llms.json grouping. */
  topics: string[];
  schemaType: AnswerSchemaType;
  priority: "high" | "medium" | "low";
  /** ISO date (YYYY-MM-DD) of last factual review. */
  lastReviewed: string;
  /** Optional SEO title override for /fragor/<slug> (CTR-optimering). */
  metaTitle?: string;
  /** Optional SEO meta description override for /fragor/<slug>. */
  metaDescription?: string;
}

const REVIEWED = "2026-05-28";

// ---------------------------------------------------------------------------
// Swedish answers (priority A)
// ---------------------------------------------------------------------------

const SV_ANSWERS: FennyAnswer[] = [
  {
    id: "sv_vad_ar_fenny",
    language: "sv",
    question: "Vad är Fenny?",
    metaTitle: "Vad är Fenny? Film varje månad, fast pris | Fenny",
    metaDescription:
      "Fenny är ett svenskt produktionsbolag för film och foto — abonnemang med första klippet inom 10–14 dagar, eller större projektproduktioner. Se hur vi jobbar.",
    shortAnswer:
      "Fenny är ett svenskt produktionsbolag som levererar premiumfilm löpande — antingen som filmabonnemang (Pulse) eller som skräddarsydda projektproduktioner (Prime).",
    longAnswer:
      "Fenny hjälper marknadsteam och varumärken att hålla en jämn produktionsrytm utan byråfriktion. Strategi, regi, foto, klipp, grading, ljud och formatanpassning sker i samma tjänst, med samma team över tid.\n\nMed Pulse får ni ett filmabonnemang till fast månadspris och första klippet inom 10–14 dagar. Med Prime gör vi enskilda större produktioner — reklamfilm, dokumentär, employer branding-film, brandfilm — med högre produktionsvärde.\n\nFenny ingår i Norrhavet-koncernen tillsammans med Hasty (marknadsavdelning som tjänst), Ellary (social och paid), Extreprenour (webb) och Swedish Frame (3D). Kontor finns i Stockholm, Västerås och Malmö, och vi producerar i hela Sverige och Norden.",
    relatedPage: "/",
    relatedQuestions: [
      "sv_vad_ar_film_pa_abonnemang",
      "sv_hur_fungerar_fenny_pulse",
      "sv_skillnaden_fenny_filmbyra",
    ],
    topics: ["fenny", "om", "positionering"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_film_pa_abonnemang",
    language: "sv",
    question: "Vad är film på abonnemang?",
    metaDescription:
      "Vad är film på abonnemang? Se hur löpande filmproduktion till fast månadspris fungerar och när det passar ert team.",
    shortAnswer:
      "Film på abonnemang innebär att ett företag får löpande produktion av rörligt innehåll till fast månadspris, istället för att köpa varje film som ett separat projekt.",
    longAnswer:
      "Modellen vänder på den traditionella byråprocessen: i stället för en lång offert per film fattar ni en gång beslut om volym, ambitionsnivå och kanaler, och produktionsteamet jobbar sedan i en återkommande rytm.\n\nMed Fenny Pulse ingår strategi, manus, inspelning, klipp, grading, ljudmix och formatanpassning för era kanaler — i en månadskostnad. Ni får en fast projektledare, ett team som lär känna ert varumärke, och en jämn leveranstakt där vi planerar innehållet kvartalsvis.\n\nDet passar marknadsteam som behöver kontinuerligt innehåll till webb, sociala medier, sälj och annonsering — och som tröttnat på att förhandla pris per produktion eller vänta veckor på första klippet.",
    relatedPage: "/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vad_kostar_foretagsfilm",
      "sv_hur_fungerar_fenny_pulse",
      "sv_skillnaden_pulse_prime",
    ],
    topics: ["pulse", "abonnemang", "modell"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_fungerar_fenny_pulse",
    language: "sv",
    question: "Hur fungerar Fenny Pulse?",
    metaTitle: "Hur fungerar Fenny Pulse? Så funkar modellen | Fenny",
    shortAnswer:
      "Pulse är ett filmabonnemang där Fenny producerar och levererar film löpande till fast månadspris, med första klippet inom 10–14 dagar från strategimötet.",
    longAnswer:
      "Vi startar med en kort strategiworkshop där vi sätter mål, målgrupp, kanaler och en innehållsplan för första kvartalet. Därefter spelar vi in i block, normalt 1–2 gånger per månad, och levererar löpande färdiga filmer i de format ni behöver — 16:9 för YouTube och webb, 9:16 för Reels och TikTok, 1:1 för LinkedIn.\n\nAvtalet löper per kvartal och rullar på. Inget timpris, inga dolda avgifter. Pulse Lite börjar från 14 995 kr/mån, Pulse Core ligger på 34 995 kr/mån och Pulse Pro på 45 995 kr/mån. Exakt nivå sätts utifrån månadsvolym och format.\n\nNi äger allt slutmaterial. Vi behåller rätten att visa utvalda klipp i vår portfolio om inget annat avtalas.",
    relatedPage: "/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vad_ingar_i_filmabonnemang",
      "sv_skillnaden_pulse_prime",
      "sv_hur_snabbt_producerar_fenny",
    ],
    topics: ["pulse", "process", "abonnemang"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_fungerar_fenny_prime",
    language: "sv",
    question: "Hur fungerar Fenny Prime?",
    metaTitle: "Hur fungerar Fenny Prime? Så funkar modellen | Fenny",
    shortAnswer:
      "Prime är Fennys skräddarsydda projektproduktion för större kampanjer, reklamfilm, dokumentär och brandfilm — fast pris efter brief.",
    longAnswer:
      "Prime används när ni behöver en högre produktionshöjd än en månadsleverans rymmer: en huvudkampanj, en flaggskeppsfilm, ett rekryteringskoncept som ska bära ett helt år.\n\nVi börjar med en kreativ brief och offererar fast pris per produktion. I teamet ingår regissör, fotograf, produktionsledare, klipp, grade, ljud och rätt specialister för uppdraget — exempelvis casting, motion graphics eller drönare.\n\nMånga kunder kombinerar Prime med ett Pulse-abonnemang: Prime bär de stora momenten, Pulse håller takten löpande. Samma team, samma kvalitetsnivå.",
    relatedPage: "/prime",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "sv_skillnaden_pulse_prime",
      "sv_vad_kostar_reklamfilm",
      "sv_skillnaden_fenny_filmbyra",
    ],
    topics: ["prime", "projekt", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnaden_pulse_prime",
    language: "sv",
    question: "Vad är skillnaden mellan Pulse och Prime?",
    shortAnswer:
      "Pulse är ett löpande filmabonnemang till fast månadspris. Prime är skräddarsydd projektproduktion för enskilda större kampanjer.",
    longAnswer:
      "Pulse är byggt för marknadsteam som behöver kontinuerligt innehåll i flera kanaler varje månad. Ni får ett dedikerat team, en innehållsplan per kvartal och en förutsägbar kostnad — utan att förhandla pris per film.\n\nPrime är byggt för enskilda större produktioner: reklamfilm, varumärkesfilm, kampanj, dokumentär. Större crew, mer förproduktion, högre produktionsvärde, fast pris per projekt.\n\nMånga av våra kunder kör båda parallellt. Prime levererar flaggskeppsfilmen; Pulse skär ner, versionerar och underhåller flödet runt omkring.",
    relatedPage: "/jamfor/pulse-vs-prime",
    relatedQuestions: [
      "sv_vad_ar_film_pa_abonnemang",
      "sv_hur_fungerar_fenny_pulse",
      "sv_hur_fungerar_fenny_prime",
    ],
    topics: ["pulse", "prime", "jamfor"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_foretagsfilm",
    language: "sv",
    question: "Vad kostar en företagsfilm?",
    shortAnswer:
      "En enskild företagsfilm kostar typiskt 40 000–250 000 kr beroende på omfattning. Med Fenny Pulse får ni löpande företagsfilm från 14 995 kr/mån.",
    longAnswer:
      "Priset på en företagsfilm styrs av antalet inspelningsdagar, hur mycket förproduktion och manus som krävs, om ni behöver casting eller speakerröst, samt hur omfattande klipp och versionering är.\n\nEn enklare företagsfilm med en inspelningsdag och ett kanalformat ligger ofta i intervallet 40 000–80 000 kr. En mer producerad film med flera dagar, scenografi och flera versioner ligger ofta i intervallet 120 000–250 000 kr.\n\nMed Fenny Pulse byter ni projektoffert mot fast månadspris. Pulse Lite från 14 995 kr/mån täcker löpande social- och företagsfilm i en återkommande rytm.",
    relatedPage: "/vad-kostar-en-foretagsfilm",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vad_kostar_reklamfilm",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_hur_snabbt_producerar_fenny",
    ],
    topics: ["pris", "foretagsfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_tv_reklam",
    language: "sv",
    question: "Vad kostar en tv-reklam?",
    shortAnswer:
      "En tv-reklam på 15–30 sekunder kostar typiskt 150 000–450 000 kr i produktion. Att versionera en befintlig film till sändningsformat kostar 15 000–40 000 kr. Mediaköp tillkommer.",
    longAnswer:
      "Produktionskostnaden för tv-reklam styrs av koncept, casting, antal inspelningsdagar och hur mycket post-production som krävs. Ett spot på 15–30 sekunder landar oftast på 150 000–450 000 kr.\n\nHar ni redan en färdig reklamfilm är versionering till sändningsformat billigast: nedklipp till klockade längder, ljudmix till −23 LUFS enligt EBU R128 och export i kanalens codec kostar typiskt 15 000–40 000 kr.\n\nMediakostnaden för att sända köps separat och är i de flesta kampanjer större än produktionsbudgeten.",
    relatedPage: "/tv-produktion",
    relatedService: "Tv-produktion",
    relatedQuestions: ["sv_vad_kostar_reklamfilm", "sv_vad_kostar_foretagsfilm"],
    topics: ["pris", "tv", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_eventfilm",
    language: "sv",
    question: "Vad kostar eventfilm?",
    shortAnswer:
      "Eventfilm kostar typiskt 30 000–60 000 kr för en dag med en fotograf och klipp, och 80 000–250 000 kr för multikamera med scenljud, intervjuer och versionering.",
    longAnswer:
      "Priset styrs av antal kameror, hur länge eventet pågår, om scenljud ska kopplas in och hur många versioner ni vill ha efteråt.\n\nEn mindre kundträff med en fotograf, social-cut och en highlight-film ligger på 30 000–60 000 kr. En konferens med scen, två till tre kameror, talarintervjuer och flera leveranser ligger på 80 000–250 000 kr.\n\nSame-day social-cut ingår i Fennys upplägg: färdiga klipp publiceras medan eventet pågår, huvudfilmen levereras inom 1–2 veckor. Alla priser är exklusive moms.",
    relatedPage: "/eventfilm",
    relatedService: "Eventfilm",
    relatedQuestions: ["sv_vad_kostar_foretagsfilm"],
    topics: ["pris", "eventfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_green_screen_studio",
    metaDescription: "Så funkar chroma key, vad som krävs för en keying som håller, och de tre lägen där location slår studio. Från ett team som jobbar i båda.",
    language: "sv",
    question: "Vad är en green screen studio?",
    metaTitle: "Green screen-studio — när det lönar sig | Fenny",
    shortAnswer:
      "En green screen studio är en inspelningsplats med jämnt belyst grön fond där bakgrunden ersätts digitalt i efterhand — praktiskt när samma inspelning ska bli många versioner.",
    longAnswer:
      "Green screen (chroma key) bygger på att den gröna fonden lyses upp helt jämnt och separat från personen framför, så att färgen kan nyckas bort i post-production och bytas mot valfri bakgrund.\n\nMetoden passar när ni behöver många versioner av samma inspelning, grafiktunga förklaringsfilmer, eller när rätt miljö inte finns tillgänglig. Den passar sämre när trovärdighet i en verklig miljö är själva poängen — då är location bättre.\n\nFenny arbetar både i studio och på location och rekommenderar valet utifrån vad filmen ska åstadkomma.",
    relatedPage: "/green-screen",
    relatedService: "Green screen-produktion",
    relatedQuestions: ["sv_vad_kostar_foretagsfilm"],
    topics: ["teknik", "studio", "green screen"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_praktik_filmproduktion",
    language: "sv",
    question: "Hur får man praktik inom filmproduktion?",
    shortAnswer:
      "Sök direkt hos produktionsbolag och filmbyråer ett par månader innan perioden, med utbildning, exakta datum och länk till det du gjort. Fenny tar emot LIA, universitetspraktik och gymnasie-APL i mån av plats.",
    longAnswer:
      "De flesta praktikplatser inom film tillsätts genom direktkontakt, inte via annons. Skicka en kort ansökan med utbildning, praktikperiodens datum, ort och vad du vill lära dig — bifoga länk till råklipp eller skolprojekt.\n\nHos Fenny innebär en praktikperiod arbete på skarpa produktioner: assistans på inspelning, logg och grovklipp i post, samt eget ansvar för minst en mindre leverans. Perioder på 2–20 veckor i Stockholm, Göteborg och Malmö.",
    relatedPage: "/praktik",
    relatedService: "Karriär",
    relatedQuestions: [],
    topics: ["karriar", "praktik"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_reklamfilm",
    language: "sv",
    question: "Vad kostar en reklamfilm?",
    shortAnswer:
      "En reklamfilm kostar typiskt 150 000–800 000 kr beroende på koncept, crew, casting och produktionsdagar. Större kampanjer kan ligga betydligt högre.",
    longAnswer:
      "Reklamfilm har större spridning än någon annan filmkategori. En enklare digital reklamfilm med ett enkelt koncept och en inspelningsdag kan landa runt 150 000 kr. En tv-kampanj med regissör, casting, scenografi, flerdagsinspelning och fullt postflöde kan ligga på 600 000–1 500 000 kr eller mer.\n\nDet som driver priset är inte minuter på skärm, utan ambition: hur stort koncept, hur många människor på set, hur mycket efterarbete i klipp, grade, ljud och VFX.\n\nFenny Prime offererar fast pris per produktion efter brief. Behöver ni dessutom löpande versionering och uppföljningsfilm kompletteras Prime ofta med ett Pulse-abonnemang.",
    relatedPage: "/vad-kostar-en-reklamfilm",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "sv_vad_kostar_foretagsfilm",
      "sv_hur_fungerar_fenny_prime",
      "sv_hur_lang_reklamfilm",
    ],
    topics: ["pris", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ingar_i_filmabonnemang",
    language: "sv",
    question: "Vad ingår i ett filmabonnemang?",
    shortAnswer:
      "I Fenny Pulse ingår strategi, manus, regi, inspelning, klipp, grading, ljudmix och formatanpassning för era kanaler — i en månadskostnad.",
    longAnswer:
      "Ni får ett dedikerat team med projektledare, regissör/fotograf och redigerare som lär känna ert varumärke över tid. Inga timdebiteringar och inga dolda kostnader.\n\nKonkret levereras färdiga filmer i de format ni behöver — 16:9 för webb och YouTube, 9:16 för Reels och TikTok, 1:1 för LinkedIn — i en delad mapp redo att publicera.\n\nResor och logistik inom Sverige ingår. Behöver ni casting, animation, drönare, studio eller live-streaming kompletteras paketet utan att grundpriset ändras.",
    relatedPage: "/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_kostar_foretagsfilm",
      "sv_hur_snabbt_producerar_fenny",
    ],
    topics: ["pulse", "abonnemang", "leverans"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_snabbt_producerar_fenny",
    language: "sv",
    question: "Hur snabbt kan Fenny producera film?",
    shortAnswer:
      "Första klippet i ett Pulse-abonnemang levereras inom 10–14 dagar från strategimötet. Korta sociala filmer kan levereras på dagar.",
    longAnswer:
      "Tempo styrs av två saker: hur snabbt vi kan boka in inspelningen och hur omfattande efterarbetet är. För löpande Pulse-flöden ligger vi normalt på 10–14 dagar från start till första leverans, och sedan på rullande veckotakt.\n\nFör Prime-produktioner är cykeln längre eftersom förproduktion, casting och postproduktion tar mer tid. En reklamfilm med fullt produktionsteam ligger normalt på 4–8 veckor från grön brief till färdig film.\n\nHar ni en akut leverans hjälper vi ofta att prioritera om innehållsplanen i ett befintligt Pulse-flöde utan extra kostnad.",
    relatedPage: "/process",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_vad_kostar_foretagsfilm",
    ],
    topics: ["process", "leverans"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnaden_fenny_filmbyra",
    language: "sv",
    question: "Vad är skillnaden mellan Fenny och en traditionell filmbyrå?",
    shortAnswer:
      "Fenny ersätter offert-per-film med fast månadspris, dedikerat team och löpande leverans. En klassisk filmbyrå offererar projekt för projekt.",
    longAnswer:
      "En traditionell filmbyrå arbetar oftast per projekt: brief, offert, produktion, faktura. Det fungerar bra för enskilda större produktioner men skapar friktion när ni behöver mycket innehåll löpande.\n\nFenny Pulse är byggt för marknadsteam som behöver kontinuerlig film: ni får ett fast team, en planerad kvartalsrytm och förutsägbar månadskostnad. När ni dessutom behöver en större produktion lägger vi ovanpå med Prime — samma team, högre produktionsvärde, fast pris per projekt.\n\nDet gör att ni får både kontinuitet och toppkampanjer i samma relation, utan att starta om varje gång.",
    relatedPage: "/jamfor/filmbyra-vs-frilansare",
    relatedQuestions: [
      "sv_vad_ar_fenny",
      "sv_skillnaden_pulse_prime",
      "sv_hur_fungerar_fenny_pulse",
    ],
    topics: ["jamfor", "positionering"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vilka_foretag_passar_fenny",
    language: "sv",
    question: "Vilka företag passar Fenny för?",
    shortAnswer:
      "Fenny passar marknadsteam i tillväxtbolag, B2B-SaaS, industri, e-handel, fastighet och offentlig sektor som behöver kontinuerlig premiumfilm utan att bygga inhouse-team.",
    longAnswer:
      "Den röda tråden bland våra kunder är att de tar marknad och varumärke på allvar och behöver mer film än ett projekt per kvartal — men inte vill bygga en inhouse-redaktion med fast crew, kamerapark och fast post-pipeline.\n\nDe vanligaste profilerna är: B2B-SaaS med behov av produktfilm, kundcasefilm och LinkedIn-content; industribolag med komplexa processer som ska översättas för rekrytering och sälj; fastighet, energi och offentlig sektor som behöver storytelling över längre tid.\n\nFör mindre bolag med enstaka produktioner per år passar Prime bättre än Pulse.",
    relatedPage: "/for-vem",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_skillnaden_pulse_prime",
      "sv_hur_anvanda_video_b2b",
    ],
    topics: ["for-vem", "malgrupp"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_varfor_lopande_film",
    language: "sv",
    question: "Varför ska företag jobba löpande med film?",
    shortAnswer:
      "Löpande film ger jämn närvaro i kanalerna, lägre kostnad per minut och möjlighet att lära av vad som faktiskt fungerar — istället för att satsa allt på en enskild produktion per år.",
    longAnswer:
      "Algoritmerna i LinkedIn, YouTube, TikTok och Meta belönar konsekvens. Ett företag som publicerar film varje vecka bygger igenkänning, äger en kategori i målgruppens flöde och får data att optimera mot.\n\nLöpande produktion sänker dessutom kostnaden per minut: när teamet redan känner ert varumärke, lights, locations och tone of voice kapas tiden i förproduktion. Varje månads inspelning bär flera leveranser — en huvudfilm, klipp till sociala, råmaterial till framtida kampanjer.\n\nDet är därför film på abonnemang har vuxit som modell: marknadsteam vill ha kontinuitet, inte engångsdunder.",
    relatedPage: "/varfor-film",
    relatedQuestions: [
      "sv_hur_anvanda_video_b2b",
      "sv_hur_fungerar_fenny_pulse",
      "sv_vilka_foretag_passar_fenny",
    ],
    topics: ["strategi", "varfor-film"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_anvanda_video_b2b",
    language: "sv",
    question: "Hur använder man video i B2B-marknadsföring?",
    shortAnswer:
      "I B2B fungerar video bäst när den löser ett konkret köpsteg: förklara produkten, bevisa resultat via kundcase, sänka tröskeln för säljmöte eller stärka employer branding.",
    longAnswer:
      "Ett vanligt B2B-misstag är att producera en enda imagefilm och hoppas att den ska göra allt. Bättre angreppssätt är att kartlägga köpresan och producera en handfull formatfilmer för varje steg: en kort produktforklaring för annonsering, en längre demo för MQL, kundcasefilm för SQL, ett verkligen-möt-teamet för rekrytering.\n\nDe formaten kan sedan versioneras till LinkedIn, säljmejl, webbplats och sales decks. Ett Pulse-abonnemang är byggt för exakt den produktionstakten.\n\nMät på affärsutfall — bokade möten, MQL, närvaro hos befintliga konton — inte bara views.",
    relatedPage: "/branscher/saas",
    relatedQuestions: [
      "sv_varfor_lopande_film",
      "sv_vilka_foretag_passar_fenny",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    topics: ["b2b", "strategi"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_lang_reklamfilm",
    language: "sv",
    question: "Hur lång ska en reklamfilm vara?",
    heading: "Reklamfilmens längd: välj format efter kanal",
    shortAnswer:
      "Standardlängderna är 6, 15, 30 och 60 sekunder. För digitala kanaler vinner ofta 15–30 sekunder, medan 6 sekunder används för minnesfäste och 60 sekunder för storytelling.",
    longAnswer:
      "Längden ska styras av kanal och placering: pre-roll och bumpers tjänar på korta versioner, in-feed på 10–20 sekunder, varumärkesplattformar och YouTube på längre format som tillåter berättelse.\n\nVår rekommendation är att alltid producera en huvudfilm i ett ambitiöst koncept och sedan klippa ner i 4–6 versioner för olika placeringar. Det är där löpande film bär mest värde — varje produktionsdag ger bränsle till hela mediemixen.",
    relatedPage: "/hur-lang-ska-en-reklamfilm-vara",
    relatedQuestions: [
      "sv_vad_kostar_reklamfilm",
      "sv_hur_anvanda_video_b2b",
      "sv_hur_fungerar_fenny_prime",
    ],
    topics: ["reklamfilm", "format"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_fungerar_fenny_flow",
    language: "sv",
    question: "Hur fungerar Fenny Flow?",
    metaTitle: "Hur fungerar Fenny Flow? Så funkar modellen | Fenny",
    shortAnswer:
      "Flow är arbetsflödet kring ett Pulse-abonnemang: kvartalsplan, månadsblock med inspelning, löpande leverans i delad mapp och uppföljningsmöten.",
    longAnswer:
      "Flow är inte en separat tjänst — det är hur Pulse körs i praktiken. Varje kvartal sätter vi en innehållsplan tillsammans med er, bokar in 1–2 inspelningstillfällen per månad och levererar löpande färdiga filmer i delad mapp.\n\nVi har avstämningar månadsvis och planerar om vid behov — exempelvis när ett events- eller kampanjfönster dyker upp.\n\nNi får alltid samma projektledare och samma produktionsteam, vilket gör att tone of voice, ljus och bildspråk blir konsekvent över tid.",
    relatedPage: "/process",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_hur_snabbt_producerar_fenny",
    ],
    topics: ["process", "pulse"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_foretagsfilm",
    language: "sv",
    question: "Vad är en företagsfilm?",
    shortAnswer:
      "En företagsfilm är en film som presenterar ett företags verksamhet, värderingar eller erbjudande — ofta för webb, sälj, rekrytering eller sociala medier. Den ska bygga förtroende och förklara vad företaget gör.",
    longAnswer:
      "Företagsfilm är ett paraplybegrepp för film som ett företag använder för att kommunicera om sig självt. Det kan vara en imagefilm som fångar kultur och vision, en förklaringsfilm som beskriver en produkt, en kundcasefilm som visar resultat, eller en rekryterings-/employer branding-film.\n\nMålet är nästan alltid förtroende: en bra företagsfilm gör abstrakta värden konkreta och hjälper tittaren att förstå vad ni gör och varför det spelar roll. Den fungerar därför lika väl på startsidan, i säljmöten, på mässor och i annonsering.\n\nHos Fenny produceras företagsfilm antingen löpande via Pulse-abonnemanget — där ni får en jämn ström av film till fast månadspris — eller som enskild större produktion via Prime. Vilket som passar beror på om ni behöver kontinuerligt innehåll eller en specifik flaggskeppsfilm.",
    relatedPage: "/foretagsfilm",
    relatedQuestions: [
      "sv_vad_kostar_foretagsfilm",
      "sv_vad_ar_fenny",
      "sv_skillnaden_pulse_prime",
    ],
    topics: ["foretagsfilm", "tjanst"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_reklamfilm",
    language: "sv",
    question: "Vad är en reklamfilm?",
    shortAnswer:
      "En reklamfilm är en kort, säljande film vars syfte är att skapa uppmärksamhet och driva en handling — köp, besök eller varumärkeskännedom. Den är konceptdriven och anpassas till kanal och placering.",
    longAnswer:
      "Reklamfilm skiljer sig från annan företagsfilm genom sitt tydliga kommersiella mål och sitt konceptuella anslag. Den bygger ofta på en idé eller känsla som ska fastna, snarare än att förklara i detalj.\n\nFormatet styrs av var filmen ska visas: 6 sekunders bumpers för minnesfäste, 15–30 sekunder för digitala flöden och pre-roll, och längre versioner för storytelling på YouTube eller varumärkesplattformar. En genomtänkt produktion levererar därför oftast en huvudfilm plus flera nedklipp för olika placeringar.\n\nFenny producerar reklamfilm både som enskilda Prime-produktioner med högt produktionsvärde och som en del av ett löpande Pulse-flöde, där varje inspelningsdag ger bränsle till hela mediemixen.",
    relatedPage: "/reklamfilm",
    relatedQuestions: [
      "sv_vad_kostar_reklamfilm",
      "sv_hur_lang_reklamfilm",
      "sv_hur_fungerar_fenny_prime",
    ],
    topics: ["reklamfilm", "tjanst"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_employer_branding_film",
    language: "sv",
    question: "Vad är employer branding-film?",
    shortAnswer:
      "Employer branding-film är film som stärker ett företags arbetsgivarvarumärke — den visar kultur, medarbetare och vardag för att attrahera rätt talanger och stötta rekrytering.",
    longAnswer:
      "Employer branding-film riktar sig inåt och mot framtida medarbetare snarare än mot kunder. Den svarar på frågan \"hur är det att jobba här?\" genom att visa människorna, värderingarna och stämningen bakom varumärket.\n\nVanliga format är kulturfilmer, medarbetarporträtt, rekryteringsfilmer för specifika roller och \"en dag på jobbet\"-filmer. Det gemensamma är trovärdighet: filmen ska kännas äkta, inte iscensatt, för att fungera i en rekryteringsprocess.\n\nFenny producerar employer branding-film både löpande via Pulse — vilket passar företag som rekryterar kontinuerligt — och som enskilda Prime-produktioner när det behövs en mer påkostad flaggskeppsfilm för karriärsidan eller kampanjer.",
    relatedPage: "/employer-branding-film",
    relatedQuestions: [
      "sv_vad_ar_foretagsfilm",
      "sv_vilka_foretag_passar_fenny",
      "sv_skillnaden_pulse_prime",
    ],
    topics: ["employer-branding", "rekrytering", "tjanst"],
    metaDescription:
      "Kulturfilm, medarbetarporträtt, rollfilm eller en dag på jobbet? Se vad varje format gör för rekryteringen, hur lång tid det tar och vad ni behöver från HR.",
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_anvanda_film_industri",
    language: "sv",
    question: "Hur använder industriföretag film?",
    shortAnswer:
      "Industriföretag använder film för att förklara komplexa produkter och processer, visa kvalitet och säkerhet i produktionen, stötta säljteam med demonstrationer och attrahera kompetens till svårrekryterade roller.",
    longAnswer:
      "Industri är en bransch där film gör abstrakt teknik begriplig. En maskin, en process eller en materialfördel som är svår att förklara i ord blir tydlig på 30 sekunders film — vilket kortar säljcykeln och minskar missförstånd i offertskedet.\n\nVanliga format är produkt- och processfilmer som visar hur något fungerar, anläggnings- och säkerhetsfilmer, kundcasefilmer som bevisar resultat hos referenskunder, samt employer branding-film för att fylla svårrekryterade tekniker- och ingenjörsroller.\n\nEftersom industriföretag ofta producerar löpande behov — nya produkter, mässor, rekrytering, internkommunikation — passar ett Pulse-abonnemang väl: samma team lär känna anläggningen, säkerhetsrutinerna och tekniken, vilket gör varje ny inspelning snabbare och mer konsekvent.",
    relatedPage: "/branscher/industri",
    relatedQuestions: [
      "sv_vad_ar_foretagsfilm",
      "sv_varfor_lopande_film",
      "sv_vad_ar_employer_branding_film",
    ],
    topics: ["industri", "bransch", "b2b"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_employer_branding_film",
    language: "sv",
    question: "Vad kostar en employer branding-film?",
    heading: "Employer branding-film: vad avgör priset?",
    shortAnswer:
      "En enskild employer branding-film kostar typiskt 40 000–150 000 kr beroende på omfattning. Löpande via Pulse jämnas kostnaden ut till ett fast månadspris.",
    longAnswer:
      "Priset på en employer branding-film styrs av samma faktorer som annan film: antal inspelningsdagar, antal medverkande och platser, manusarbete och hur påkostad efterbearbetningen är. En enkel medarbetarporträtt-serie ligger i nedre delen av spannet, medan en genomarbetad rekryteringsfilm med flera platser och casting hamnar högre.\n\nFör företag som rekryterar kontinuerligt blir styckkostnaden ofta missvisande — behovet är snarare ett jämnt flöde av kultur- och rekryteringsinnehåll. Då passar ett Pulse-abonnemang bättre: ni får en planerad takt av employer branding-film till ett förutsägbart månadspris i stället för att offerera varje film för sig.\n\nVill ni ha en flaggskeppsfilm för karriärsidan kombineras detta ofta med en enskild Prime-produktion.",
    relatedPage: "/employer-branding-film",
    relatedQuestions: [
      "sv_vad_ar_employer_branding_film",
      "sv_vad_kostar_foretagsfilm",
      "sv_skillnaden_pulse_prime",
    ],
    topics: ["employer-branding", "pris", "rekrytering"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_lang_tid_tar_filmproduktion",
    language: "sv",
    question: "Hur lång tid tar en filmproduktion?",
    shortAnswer:
      "En enkel företagsfilm tar normalt 2–4 veckor från brief till leverans, medan en reklamfilm med fullt produktionsteam tar 4–8 veckor. Korta sociala filmer i ett löpande flöde kan levereras på några dagar.",
    longAnswer:
      "Tidsåtgången styrs av tre faser: förproduktion (brief, manus, planering, casting), inspelning och postproduktion (klippning, färg, ljud, grafik). Ju mer som ska planeras och efterbearbetas, desto längre tar det.\n\nEn rak företags- eller produktfilm utan stor casting ligger normalt på 2–4 veckor. En reklamfilm med manus, skådespelare, flera platser och avancerad post tar 4–8 veckor. Korta klipp till sociala medier i ett befintligt Pulse-flöde går betydligt snabbare eftersom teamet redan känner varumärket och ofta filmar flera leveranser samma dag.\n\nDet enskilt vanligaste som drar ut på tiden är godkännandeloopar internt — därför sätter vi tydliga avstämningspunkter tidigt så att produktionen håller tempo.",
    relatedPage: "/process",
    relatedQuestions: [
      "sv_hur_snabbt_producerar_fenny",
      "sv_vad_kostar_foretagsfilm",
      "sv_hur_lang_reklamfilm",
    ],
    topics: ["process", "leverans", "tidsatgang"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_bestaller_man_film",
    language: "sv",
    question: "Hur beställer man en film steg för steg?",
    shortAnswer:
      "Du beställer film i fem steg: definiera mål och målgrupp, sätt budget och format, välj produktionspartner, godkänn manus och plan, och följ upp på resultat efter publicering.",
    longAnswer:
      "Att beställa film behöver inte vara komplicerat. Det viktigaste är att börja i målet, inte i formatet — då blir alla efterföljande beslut enklare.\n\n1. Definiera mål och målgrupp: vad ska filmen åstadkomma (sälj, rekrytering, varumärke) och vem ska se den? 2. Sätt budget och format: bestäm ungefär vad ni vill investera och var filmen ska publiceras, så att längd och produktionsnivå matchar kanalen. 3. Välj produktionspartner: en enskild byrå för ett projekt, eller ett abonnemang som Pulse om behovet är löpande. 4. Godkänn manus och produktionsplan: stäm av idé, manus och inspelningsplan innan kameran rullar. 5. Följ upp: mät hur filmen presterar och låt resultatet styra nästa produktion.\n\nHos Fenny startar processen med ett kort strategimöte där vi översätter målet till en konkret plan — antingen som enskild Prime-produktion eller som ett löpande Pulse-flöde.",
    relatedPage: "/process",
    relatedQuestions: [
      "sv_hur_lang_tid_tar_filmproduktion",
      "sv_vad_kostar_foretagsfilm",
      "sv_hur_fungerar_fenny_pulse",
    ],
    topics: ["process", "guide", "bestallning"],
    schemaType: "HowTo",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vilka_filmformat_for_sociala_medier",
    language: "sv",
    question: "Vilka filmformat fungerar bäst på sociala medier?",
    shortAnswer:
      "Korta, vertikala filmer (9:16) på 6–30 sekunder fungerar bäst på Instagram, TikTok och LinkedIn. Budskapet ska landa redan i de första sekunderna och fungera utan ljud.",
    longAnswer:
      "Sociala medier belönar film som är gjord för flödet, inte återanvänd tv-reklam. Det betyder vertikalt eller kvadratiskt format, korta längder och ett tydligt budskap direkt i öppningen innan tittaren scrollar vidare.\n\nVanliga vinnande format är: hooks och bumpers på 6–15 sekunder, produkt- och demoklipp på 15–30 sekunder, samt kundröster och behind-the-scenes som bygger trovärdighet. Textning är näst intill obligatorisk eftersom en stor del tittar utan ljud.\n\nEftersom sociala kanaler kräver volym och hög frekvens passar ett Pulse-abonnemang särskilt bra — ni får ett jämnt flöde av klipp anpassade per kanal i stället för enstaka dyra produktioner som snabbt blir gamla.",
    relatedPage: "/pulse",
    relatedQuestions: [
      "sv_hur_lang_reklamfilm",
      "sv_hur_anvanda_video_b2b",
      "sv_varfor_lopande_film",
    ],
    topics: ["sociala-medier", "format", "distribution"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_en_videopodcast",
    language: "sv",
    question: "Vad kostar en videopodcast?",
    shortAnswer:
      "En videopodcast kostar typiskt 15 000–35 000 kr per avsnitt när flera avsnitt spelas in samma dag, och 35 000–70 000 kr för en enskild inspelning. Löpande produktion via abonnemang börjar på 14 995 kr/mån.",
    longAnswer:
      "Priset på en videopodcast styrs främst av hur många avsnitt som spelas in per inspelningsdag. Riggen — tre kameror, mikrofoner, ljus och ljuddämpning — kostar lika mycket att sätta upp oavsett om ni spelar in ett eller fyra avsnitt. Därför sjunker styckpriset kraftigt vid serieinspelning.\n\nEn enskild inspelning med tre kameror, klipp till fullängdsavsnitt, audio-only-version och 6–10 vertikala shorts ligger normalt på 35 000–70 000 kr. Vid serieinspelning med tre till fyra avsnitt samma dag landar per-avsnitt-kostnaden oftast på 15 000–35 000 kr.\n\nTillkommer gör extern studio, scenografi, grafikpaket samt gästbokning och research om ni vill att byrån sköter det. Behöver ni avsnitt löpande varje månad är abonnemang oftast billigare än att offerera varje inspelning för sig.",
    relatedPage: "/podcast-video",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/filmabonnemang-vs-projektfilm",
    relatedQuestions: [
      "sv_vad_kostar_videoproduktion",
      "sv_vad_kostar_foretagsfilm",
    ],
    topics: ["pris", "videopodcast", "företagspodd"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_en_livesandning",
    language: "sv",
    question: "Vad kostar en livesändning?",
    shortAnswer:
      "En livesändning kostar typiskt 25 000–60 000 kr för ett webinar med en till två kameror och 80 000–250 000 kr för ett flerkamera-event med bildregi, grafik och multistreaming.",
    longAnswer:
      "Kostnaden för en livesändning beror på riggnivå, sändningens längd och hur mycket redundans som krävs. Ett studiowebinar med en till två kameror, slides och gästfeed ligger normalt på 25 000–60 000 kr och kan bokas med cirka två veckors framförhållning.\n\nEtt event från scen med tre till sex kameror, bildregi, ljudmix, grafik och multistreaming till flera plattformar ligger normalt på 80 000–250 000 kr och bör bokas fyra till sex veckor i förväg. Hybridkonferenser med publik både på plats och online börjar oftast kring 150 000 kr.\n\nI priset ingår teknik, crew, uppsättning och sändningsdag. Location, scen, ljus och scenografi tillkommer efter behov. Master och ett kort highlight-klipp för efterspridning levereras normalt inom 48 timmar.",
    relatedPage: "/livesandning",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "sv_vad_kostar_videoproduktion",
      "sv_vad_kostar_en_videopodcast",
    ],
    topics: ["pris", "livesändning", "webinar"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_videoproduktion",
    language: "sv",
    question: "Vad kostar videoproduktion?",
    heading: "Videoproduktion: vad styr kostnaden per film?",
    shortAnswer:
      "Videoproduktion kostar typiskt 30 000–250 000 kr per film beroende på omfattning. Med ett Pulse-abonnemang får ni i stället löpande videoproduktion till fast månadspris från 14 995 kr/mån.",
    longAnswer:
      "Videoproduktion är ett brett begrepp och priset styrs av samma faktorer oavsett filmtyp: antal inspelningsdagar, förproduktion och manus, om casting eller speaker behövs, samt hur omfattande klipp, grading och versionering blir.\n\nEn enklare produktion med en inspelningsdag och ett kanalformat ligger ofta i intervallet 30 000–80 000 kr. En mer producerad film med flera dagar, scenografi och flera versioner ligger ofta i intervallet 120 000–250 000 kr. Reklamfilm med stort koncept kan ligga betydligt högre.\n\nDen stora skillnaden mot ett abonnemang är att projektpris debiteras per film, medan Fenny Pulse byter offert mot fast månadskostnad där strategi, produktion och formatanpassning ingår. För företag som behöver film löpande blir styckpriset då lägre och planeringen enklare.",
    relatedPage: "/videoproduktion",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/filmproduktion-abonnemang-vs-projektpris",
    relatedQuestions: [
      "sv_vad_kostar_foretagsfilm",
      "sv_vad_kostar_filmproduktion",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    ],
    topics: ["pris", "videoproduktion"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_filmproduktion",
    language: "sv",
    question: "Vad kostar filmproduktion?",
    heading: "Filmproduktion: vad avgör priset för ert projekt?",
    metaDescription:
      "Vad kostar filmproduktion? Se vad som påverkar priset: koncept, inspelningsdagar och efterarbete. Jämför projekt med abonnemang.",
    shortAnswer:
      "Filmproduktion kostar typiskt 40 000–800 000 kr per produktion beroende på koncept, crew och produktionsdagar. Löpande filmproduktion via abonnemang börjar i stället från 14 995 kr/mån.",
    longAnswer:
      "Priset på filmproduktion avgörs av ambitionsnivån snarare än filmens längd. En enklare produktion med ett litet team och en inspelningsdag landar ofta runt 40 000–80 000 kr, medan en producerad företags- eller varumärkesfilm med flera dagar och fullt postflöde ligger högre. Reklamfilm med regissör, casting och flerdagsinspelning kan ligga på 150 000–800 000 kr eller mer.\n\nDet som driver kostnaden är hur många personer som krävs på set, hur mycket förproduktion konceptet behöver och hur omfattande efterarbetet blir i klipp, grade, ljud och eventuell VFX.\n\nFenny erbjuder två modeller: Prime offererar fast pris per produktion efter brief, och Pulse ger löpande filmproduktion till fast månadspris. Företag som producerar film regelbundet sänker ofta sin totala kostnad genom att gå från enskilda offerter till ett abonnemang.",
    relatedPage: "/filmproduktion",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/filmproduktion-abonnemang-vs-projektpris",
    relatedQuestions: [
      "sv_vad_kostar_videoproduktion",
      "sv_vad_kostar_reklamfilm",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    ],
    topics: ["pris", "filmproduktion"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_fungerar_videoabonnemang",
    language: "sv",
    question: "Hur fungerar ett videoabonnemang?",
    shortAnswer:
      "Med ett videoabonnemang betalar företaget en fast månadskostnad och får löpande film producerad i en återkommande rytm, i stället för att beställa och prisförhandla varje film separat.",
    longAnswer:
      "Ett videoabonnemang vänder på den traditionella byråmodellen. I stället för en offert per film bestämmer ni en gång volym, ambitionsnivå och kanaler — sedan jobbar produktionsteamet i ett rullande flöde av planering, inspelning och leverans.\n\nMed Fenny Pulse sätter vi en innehållsplan kvartalsvis, bokar in inspelningstillfällen varje månad och levererar färdiga filmer i delad mapp i de format ni behöver. Ni har samma projektledare och samma team över tid, vilket gör tonläge och bildspråk konsekvent.\n\nModellen passar marknadsteam som behöver film kontinuerligt till webb, sociala medier, sälj och annonsering. Den passar mindre bra för den som bara behöver en enstaka stor produktion — då är en projektofferterad film via Prime ofta rätt väg.",
    relatedPage: "/filmabonnemang",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/videoabonnemang-vs-contentabonnemang",
    relatedQuestions: [
      "sv_vad_ar_film_pa_abonnemang",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    ],
    topics: ["abonnemang", "pulse", "modell"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    language: "sv",
    question: "Är film på abonnemang bättre än projektpris?",
    shortAnswer:
      "Abonnemang är oftast bättre för företag som behöver film löpande, eftersom styckpriset sjunker och planeringen blir enklare. Projektpris är bättre för enstaka, större produktioner.",
    longAnswer:
      "Det finns inget generellt rätt svar — det beror på hur ofta ni behöver film. Behöver ni nytt rörligt innehåll varje månad till sociala medier, webb och sälj vinner abonnemanget: ni slipper förhandla pris per film, får en jämn leveranstakt och ett team som lär känna ert varumärke över tid.\n\nBehöver ni däremot en enstaka, påkostad produktion — en reklamfilm eller en varumärkesfilm en gång om året — är projektpris ofta mer logiskt, eftersom kostnaden då är knuten till just den produktionen.\n\nMånga företag kombinerar: ett Pulse-abonnemang för det löpande flödet och Prime-produktioner för de stora satsningarna. Då får ni både kontinuitet och produktionsvärde utan att betala för kapacitet ni inte använder.",
    relatedPage: "/jamfor/filmabonnemang-vs-projektfilm",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/filmproduktion-abonnemang-vs-projektpris",
    relatedQuestions: [
      "sv_vad_ar_film_pa_abonnemang",
      "sv_skillnaden_pulse_prime",
      "sv_vad_kostar_videoproduktion",
    ],
    topics: ["abonnemang", "jamforelse", "modell"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vilka_filmer_behover_ett_tillvaxtbolag",
    language: "sv",
    question: "Vilka filmer behöver ett tillväxtbolag?",
    shortAnswer:
      "Ett tillväxtbolag behöver oftast en varumärkesfilm, kundcasefilmer, produkt- eller demofilm, employer branding-film och ett löpande flöde av korta sociala klipp för sälj och annonsering.",
    longAnswer:
      "Behovet förändras med tillväxtfaserna, men kärnan är densamma: film som bygger förtroende, förklarar erbjudandet och driver pipeline. En varumärkesfilm sätter positioneringen, kundcasefilmer bevisar effekt mot nya köpare, och produkt- eller demofilm gör det lättare för sälj att förklara värdet.\n\nFör att attrahera talang i en snabb tillväxtfas är employer branding-film ofta avgörande. Och för att hålla uppe närvaro i kanalerna behövs ett jämnt flöde av korta, vertikala klipp till LinkedIn, Instagram och annonser.\n\nDet är just kombinationen av enstaka tyngre produktioner och ett kontinuerligt socialt flöde som gör abonnemangsmodellen attraktiv för tillväxtbolag. Med Fenny Pulse får marknadsteamet kapacitet att producera löpande utan att bygga en egen inhouse-funktion.",
    relatedPage: "/branscher/saas",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_anvanda_video_b2b",
      "sv_varfor_lopande_film",
      "sv_vilka_foretag_passar_fenny",
    ],
    topics: ["tillvaxtbolag", "b2b", "strategi"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_ofta_bor_foretag_publicera_video",
    language: "sv",
    question: "Hur ofta bör företag publicera video?",
    shortAnswer:
      "De flesta B2B-företag bör publicera video varje vecka i sociala kanaler och komplettera med större produktioner några gånger per år. Regelbundenhet betyder mer än enstaka påkostade filmer.",
    longAnswer:
      "Algoritmerna i sociala kanaler belönar kontinuitet, och köpare behöver se ett varumärke flera gånger innan de agerar. Därför ger ett jämnt flöde av korta filmer normalt bättre effekt än en enstaka dyr produktion per år.\n\nEn rimlig rytm för många marknadsteam är 1–4 sociala klipp i veckan — kundröster, produktklipp, hooks och behind-the-scenes — plus ett par tyngre produktioner per år för varumärke, kampanj eller employer branding.\n\nUtmaningen är sällan idéer utan kapacitet att producera regelbundet. Det är därför abonnemangsmodellen vuxit: med Fenny Pulse planeras innehållet kvartalsvis och produceras löpande, så frekvensen blir hållbar i stället för en stress varje gång något ska publiceras.",
    relatedPage: "/varfor-film",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_varfor_lopande_film",
      "sv_vilka_filmformat_for_sociala_medier",
      "sv_hur_anvanda_video_b2b",
    ],
    topics: ["strategi", "sociala-medier", "frekvens"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_en_kundcasefilm",
    language: "sv",
    question: "Vad är en kundcasefilm?",
    shortAnswer:
      "En kundcasefilm är en kort film där en befintlig kund berättar om sitt resultat med er produkt eller tjänst. Den fungerar som socialt bevis och är ett av de starkaste säljverktygen i B2B.",
    longAnswer:
      "I en kundcasefilm får köparen höra någon som liknar dem själva beskriva problemet, lösningen och effekten. Det gör filmen mer trovärdig än ett vanligt säljbudskap, eftersom berättelsen kommer från kunden och inte från er.\n\nEn bra kundcasefilm är kort och konkret: utgångsläge, vad som gjordes och ett mätbart eller tydligt resultat. Den klipps ofta i flera längder — en längre version för webb och sälj, och korta klipp för LinkedIn och annonser.\n\nKundcasefilm passar särskilt bra i ett löpande flöde, eftersom nya case stärker pipeline över tid. Fenny producerar kundcasefilm både som enskild produktion via Prime och löpande inom ett Pulse-abonnemang.",
    relatedPage: "/kundcase-film",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_anvanda_video_b2b",
      "sv_vilka_filmer_behover_ett_tillvaxtbolag",
      "sv_vad_ar_foretagsfilm",
    ],
    topics: ["kundcase", "b2b", "tjanst"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_gor_man_en_bra_linkedin_video",
    language: "sv",
    question: "Hur gör man en bra LinkedIn-video?",
    shortAnswer:
      "En bra LinkedIn-video är kort, har en tydlig hook i första sekunderna, är textad för ljudlöst tittande och fokuserar på ett enda budskap som är relevant för en professionell målgrupp.",
    longAnswer:
      "LinkedIn är en plattform där köpare och beslutsfattare scrollar i jobbsammanhang. Det betyder att filmen ska kännas relevant och trovärdig snarare än reklamig — en insikt, ett kundresultat eller en konkret poäng landar bättre än ett brett varumärkesbudskap.\n\nTekniskt gäller samma regler som för andra sociala kanaler: börja med en hook som stoppar scrollen, håll filmen kort (ofta 15–60 sekunder), texta allt eftersom många tittar utan ljud, och avsluta med en tydlig poäng eller uppmaning. Vertikalt eller kvadratiskt format tar mer yta i flödet.\n\nEftersom LinkedIn-video kräver regelbundenhet för att bygga räckvidd passar det bra i ett löpande flöde. Med Fenny Pulse produceras LinkedIn-klipp kontinuerligt och anpassas per format utan att varje film blir ett eget projekt.",
    relatedPage: "/film-for-linkedin",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vilka_filmformat_for_sociala_medier",
      "sv_hur_anvanda_video_b2b",
      "sv_hur_ofta_bor_foretag_publicera_video",
    ],
    topics: ["linkedin", "sociala-medier", "b2b"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_ska_man_anlita_filmbyra_eller_bygga_inhouse",
    language: "sv",
    question: "Ska man anlita filmbyrå eller bygga inhouse?",
    shortAnswer:
      "En inhouse-funktion ger närhet och hög volym men kräver rekrytering, utrustning och kompetens i hela kedjan. En byrå eller ett filmabonnemang ger bredd och produktionsvärde utan fasta personalkostnader.",
    longAnswer:
      "Att bygga inhouse kan vara rätt för bolag med mycket hög och förutsägbar volym, där en eller flera heltidsanställda hela tiden är fullt belagda. Men det innebär kostnader för lön, utrustning, mjukvara och kompetensutveckling — och en ensam inhouse-producent täcker sällan hela kedjan från strategi och regi till grade och ljud.\n\nEn extern partner ger i stället tillgång till ett komplett team och högre produktionsvärde utan fasta personalkostnader. Nackdelen med en traditionell byrå är ofta långa offertprocesser och rörliga priser per produktion.\n\nFilmabonnemang är ett mellanläge som tagit fart just därför: ni får kontinuiteten och närheten som liknar inhouse, men med ett fullt team och fast månadspris. Fenny Pulse är byggt för marknadsteam som vill ha löpande kapacitet utan att rekrytera en hel produktionsfunktion.",
    relatedPage: "/jamfor/inhouse-vs-filmbyra",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/inhouse-vs-filmbyra",
    relatedQuestions: [
      "sv_skillnaden_fenny_filmbyra",
      "sv_vad_ar_film_pa_abonnemang",
      "sv_vilka_foretag_passar_fenny",
    ],
    topics: ["inhouse", "jamforelse", "modell"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_video_as_a_service",
    language: "sv",
    question: "Vad är Video as a Service?",
    shortAnswer:
      "Video as a Service (VaaS) innebär att videoproduktion levereras som en löpande tjänst till fast månadspris i stället för enskilda projekt — i praktiken samma sak som film på abonnemang.",
    longAnswer:
      "Begreppet är lånat från mjukvaruvärldens \"as a service\"-modeller och beskriver samma skifte inom film: från att köpa en produkt (en film) till att prenumerera på en kapacitet (löpande produktion). Företaget betalar en förutsägbar månadskostnad och får ett team som producerar film kontinuerligt.\n\nI praktiken är Video as a Service och filmabonnemang samma modell. Fenny Pulse är vår variant: strategi, inspelning, klipp, grading och formatanpassning ingår, och ni får färdiga filmer i en återkommande rytm utan att förhandla pris per produktion.\n\nModellen passar marknadsteam som behöver volym och regelbundenhet. För enstaka, stora produktioner kompletteras den med projektofferterad film via Prime.",
    relatedPage: "/filmabonnemang",
    relatedService: "Fenny Pulse",
    relatedComparison: "/jamfor/video-as-a-service-vs-filmbyra",
    relatedQuestions: [
      "sv_vad_ar_film_pa_abonnemang",
      "sv_hur_fungerar_videoabonnemang",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    ],
    topics: ["abonnemang", "vaas", "modell"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnaden_foretagsfilm_och_reklamfilm",
    language: "sv",
    question: "Vad är skillnaden mellan företagsfilm och reklamfilm?",
    shortAnswer:
      "Företagsfilm förklarar och bygger förtroende för ett företag eller erbjudande, ofta för webb, sälj och rekrytering. Reklamfilm är en kampanjdriven film byggd för att skapa uppmärksamhet och säljeffekt i annonsering.",
    longAnswer:
      "Företagsfilm är ett brett samlingsbegrepp för film som presenterar verksamheten: om-oss-film, kundcase, produkt- och demofilm, rekryteringsfilm och utbildningsfilm. Syftet är ofta att förklara, bygga trovärdighet och stötta sälj snarare än att driva en enskild kampanj.\n\nReklamfilm är mer kampanjorienterad och konceptdriven. Den har ofta ett tydligt kreativt grepp, högre produktionsvärde och är gjord för att synas i betalda kanaler eller tv under en avgränsad period.\n\nMånga företag behöver båda: löpande företagsfilm för det vardagliga behovet och enstaka reklamfilm för större satsningar. Hos Fenny produceras löpande företagsfilm inom Pulse, medan påkostad reklamfilm görs som projekt via Prime.",
    relatedPage: "/foretagsfilm",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "sv_vad_ar_foretagsfilm",
      "sv_vad_ar_reklamfilm",
      "sv_vad_kostar_reklamfilm",
    ],
    topics: ["foretagsfilm", "reklamfilm", "jamforelse"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnaden_filmbyra_videobyra",
    metaDescription: "Filmbyrå och videobyrå gör i praktiken samma sak. Så skiljer sig tonalitet, format och upplägg — och så väljer ni rätt partner.",
    language: "sv",
    question: "Vad är skillnaden mellan en filmbyrå och en videobyrå?",
    metaTitle: "Skillnaden mellan filmbyrå och videobyrå | Fenny",
    shortAnswer:
      "I praktiken är det samma sak: båda producerar rörlig bild för företag. \"Filmbyrå\" är det äldre, mer kreativt laddade ordet, medan \"videobyrå\" oftare används om byråer som fokuserar på sociala medier, webb och kortformat.",
    longAnswer:
      "Begreppen filmbyrå och videobyrå överlappar nästan helt. Båda gör reklamfilm, företagsfilm, employer branding och content till sociala kanaler. Skillnaden ligger mest i tonalitet och historia: en filmbyrå associeras ofta med större produktioner, regi, manus och biograf- eller tv-format, medan en videobyrå förknippas med snabbare flöden av kortare videos för digitala kanaler.\n\nFör de flesta uppdragsgivare spelar etiketten ingen roll — det viktiga är vad byrån faktiskt levererar: koncept, produktion, redigering, motion graphics och distribution. Fråga alltid efter case som liknar det du själv ska göra istället för att fastna i ordvalet.\n\nHos Fenny finns båda världarna under samma tak. Pulse fungerar som videobyrå-light med löpande produktion av kortformat, medan Prime levererar regiledd film när uppdraget kräver det.",
    relatedPage: "/filmbyra",
    relatedService: "Fenny",
    relatedQuestions: [
      "sv_skillnaden_fenny_filmbyra",
      "sv_ska_man_anlita_filmbyra_eller_bygga_inhouse",
      "sv_vad_kostar_videoproduktion",
    ],
    topics: ["filmbyra", "videobyra", "jamforelse"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_en_imagefilm",
    language: "sv",
    question: "Vad kostar en imagefilm?",
    shortAnswer:
      "En imagefilm kostar oftast 80 000–350 000 kr beroende på koncept, antal inspelningsdagar och postproduktion. Enklare imagefilm med befintligt material kan göras från cirka 40 000 kr.",
    longAnswer:
      "Priset på en imagefilm styrs av tre faktorer: hur stort det kreativa greppet är, hur många inspelningsdagar som krävs och hur mycket efterarbete filmen behöver. En enkel imagefilm med en inspelningsdag, en speaker och lättare grafik landar typiskt mellan 80 000 och 150 000 kr.\n\nFler platser, drönarmaterial, skådespelare, originalmusik eller animation drar upp budgeten mot 200 000–350 000 kr. Stora varumärkesproduktioner med flera dagar, full crew och högre produktionsvärde kan kosta över 500 000 kr.\n\nVill du sänka priset utan att tappa kvalitet är ett vanligt grepp att producera imagefilmen som en \"hero\" plus 4–6 kortversioner samtidigt — då fördelas kostnaden över flera kanaler. Hos Fenny görs imagefilm antingen som Prime-projekt eller löpande inom ett Pulse-abonnemang.",
    relatedPage: "/imagefilm",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "sv_vad_kostar_foretagsfilm",
      "sv_vad_kostar_reklamfilm",
      "sv_vad_ar_foretagsfilm",
    ],
    topics: ["imagefilm", "pris", "foretagsfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_skriver_man_ett_filmmanus",
    language: "sv",
    question: "Hur skriver man ett bra filmmanus för en företagsfilm?",
    metaTitle: "Så skriver du ett filmmanus till företagsfilm | Fenny",
    shortAnswer:
      "Börja med målgrupp och en tydlig kärnidé, skriv sedan en kort scenlista innan du formulerar speakertext eller dialog. Håll filmen kort, ha en hook i första fem sekunderna och avsluta med en konkret call-to-action.",
    longAnswer:
      "Ett bra filmmanus för företag startar inte med ord — det startar med en idé. Definiera först vem filmen är till för, vad du vill att tittaren ska göra efteråt och vilket enda budskap som måste landa. Allt annat byggs runt det.\n\nSkriv sedan en scenlista i två kolumner: vänster för bild (vad ser vi?), höger för ljud (vad hörs?). Det hjälper både dig och produktionsbolaget att se rytmen i filmen innan en enda mening är formulerad.\n\nNär du skriver själva texten: håll meningarna korta, tala till en person, inte till \"alla våra kunder\". Lägg den starkaste hooken i de första fem sekunderna eftersom de flesta tittare hoppar av där. Avsluta med en tydlig nästa-steg-uppmaning.\n\nFenny erbjuder manusstöd som standard inom både Pulse och Prime, så du behöver inte skriva manuset själv — men ju tydligare brief du kommer in med, desto snabbare blir slutfilmen.",
    relatedPage: "/process",
    relatedService: "Fenny",
    relatedQuestions: [
      "sv_hur_bestaller_man_film",
      "sv_hur_lang_reklamfilm",
      "sv_hur_gor_man_en_bra_linkedin_video",
    ],
    topics: ["manus", "produktion", "process"],
    schemaType: "HowTo",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_en_foretagsfotograf",
    language: "sv",
    question: "Vad kostar en företagsfotograf?",
    shortAnswer:
      "Fenny arbetar med offert per fotouppdrag i stället för fast prislista. Priset styrs av tid på plats, antal färdiga bilder, ljussättning, retuschnivå och hur bilderna ska användas. Ni får ett fast pris innan uppdraget bokas.",
    longAnswer:
      "Att sätta ett listpris på fotografering ger nästan alltid fel svar, eftersom två uppdrag med samma längd kan skilja sig kraftigt i förberedelse och efterarbete. En porträttdag för tjugo medarbetare i era lokaler kräver mobil studio, schema och enhetlig retusch. En kampanjbild kräver koncept, styling, location och mer omfattande bildbehandling.\n\nDe faktorer som styr offerten är: tid på plats, antal färdiga bilder, om studio eller mobil studio behövs, retuschnivå, om stylist eller modeller ingår, samt användningsrätt i egna kanaler, betald annonsering eller print.\n\nFenny lämnar fast pris per uppdrag med specificerat innehåll, normalt inom en arbetsdag. Fri användningsrätt i egna kanaler ingår. Kontaktperson för foto är Fenny, hey@fenny.se.",
    relatedPage: "/foto/vad-kostar-en-fotograf",
    relatedService: "Foto",
    relatedQuestions: ["sv_foretagsportratt_upplagg", "sv_foto_och_film_samma_dag"],
    topics: ["foto", "pris", "företagsfotografering"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_bildindex_pris_fotografering_2026",
    language: "sv",
    question: "Vad kostar fotografering i Sverige 2026?",
    shortAnswer:
      "Ett företagsporträtt-paket kostar i median 14 000 kr exklusive moms (spann 9 000–22 000 kr för en halvdag), produktfoto för e-handel 150–450 kr per bild vid volym, och kampanjfoto 25 000–85 000 kr. Retuscherade bilder levereras i median på fem arbetsdagar.",
    longAnswer:
      "Siffrorna kommer ur Fennys Bildindex 2026, en öppen sammanställning av 168 levererade fotouppdrag mellan januari 2024 och augusti 2026. Måttet är median samt 10:e–90:e percentilen, exklusive moms, och exklusive resor utanför uppdragsorten samt licenser för utomhusreklam.\n\nDe största posterna i en fotobudget är tid på plats, antal färdiga bilder och retuschnivå. Företagsporträtt och produktfoto står tillsammans för 44 procent av all levererad fotovolym för svenska B2B-kunder, vilket gör dem till de mest prisjämförbara tjänsterna.\n\nGeografin påverkar också: Stockholm ligger 12 procent över riksmedianen, medan norra Sverige ligger 8 procent under men har högre reseandel i offerten. Hela datasetet är fritt att citera under CC BY 4.0 och kan laddas ner som CSV.",
    relatedPage: "/branschstudier/bildindex-2026",
    relatedService: "Foto",
    relatedQuestions: [
      "sv_vad_kostar_en_foretagsfotograf",
      "sv_bildindex_leveranstid_bilder",
    ],
    topics: ["foto", "pris", "branschdata"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_bildindex_leveranstid_bilder",
    language: "sv",
    question: "Hur lång tid tar det att få bilderna efter en fotografering?",
    metaTitle: "Hur lång tid tar det att få bilderna? | Fenny",
    shortAnswer:
      "Medianen är fem arbetsdagar från fotograferingsdagen till retuscherade bilder. Eventfoto levereras snabbast, med ett urval inom 1–2 arbetsdagar, medan kampanjfoto tar 7–15 arbetsdagar.",
    longAnswer:
      "Leveranstiden räknas från fotograferingsdagen till levererade, retuscherade bilder i webb- och tryckformat, inklusive en urvalsrunda och en justeringsrunda. Det är urvalet hos beställaren som oftast avgör om leveransen landar i den snabba eller långsamma delen av spannet.\n\nEnligt Bildindex 2026 levereras headshots på 2–5 arbetsdagar, företagsporträtt på 3–7, produktfoto för e-handel på 4–10 och kampanjfoto på 7–15. Eventfoto har egen logik: ett publicerbart urval går ut samma dag eller dagen efter, resten kompletteras inom en vecka.\n\nBehöver ni snabbare leverans går det att boka expressretusch, där ett prioriterat urval levereras inom 24 timmar mot pristillägg. Kontaktperson för foto är Fenny, hey@fenny.se.",
    relatedPage: "/branschstudier/bildindex-2026",
    relatedService: "Foto",
    relatedQuestions: [
      "sv_bildindex_pris_fotografering_2026",
      "sv_foretagsportratt_upplagg",
    ],
    topics: ["foto", "leverans", "branschdata"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_foretagsportratt_upplagg",
    language: "sv",
    question: "Hur går en fotografering av företagsporträtt till?",
    shortAnswer:
      "Vi bygger mobil studio i era lokaler, kör 5–10 minuter per person enligt schema och levererar retuscherade bilder inom 3–5 arbetsdagar. Ljus och beskärning dokumenteras så att nyanställda kan matchas in senare.",
    longAnswer:
      "Upplägget börjar med ett kort avstämningssamtal om manér: neutral bakgrund eller miljö, formellt eller vardagligt, färgsättning och hur bilderna ska användas. Därefter bokas en dag och ett tidsschema per person.\n\nPå plats behövs ungefär tre gånger tre meter och 45 minuters riggning. Varje person tar 5–10 minuter, vilket innebär att 20–40 medarbetare hinns med på en dag. Alla får se ett par bilder direkt i skärm.\n\nEfter dagen gör ni ett urval i ett digitalt galleri. Retuschen är återhållsam — hud och ljus jämnas ut, men personen ska se ut som sig själv. Ljusuppsättning och beskärning dokumenteras så att kompletteringar för nyanställda matchar originalserien.",
    relatedPage: "/foto/foretagsportratt",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_en_foretagsfotograf", "sv_foto_och_film_samma_dag"],
    topics: ["foto", "företagsporträtt", "process"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_foto_och_film_samma_dag",
    language: "sv",
    question: "Kan man göra foto och film samma dag?",
    shortAnswer:
      "Ja. När stillbild och rörligt planeras ihop delar produktionerna resa, rigg, location och styling, vilket normalt sänker totalkostnaden jämfört med två separata inspelningsdagar.",
    longAnswer:
      "Det som gör kombinationen effektiv är att den dyraste delen av en produktion sällan är kameran — det är förberedelsen: att få rätt personer, rätt plats och rätt ljus på samma ställe samtidigt. När den logistiken redan är betald är marginalkostnaden för att också ta stillbilder låg.\n\nFörutsättningen är att det planeras från början. Fotografen behöver egna korta fönster i schemat, eftersom stillbild och rörligt sällan har exakt samma ljus och posering. Vi lägger därför in fotoblock mellan filmtagningarna i körschemat.\n\nFenny bemannar med både filmteam och fotograf i samma produktion och levererar filmen och bildbanken från ett och samma uppdrag.",
    relatedPage: "/foto/foto-och-film",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_en_foretagsfotograf", "sv_foretagsportratt_upplagg"],
    topics: ["foto", "film", "produktion"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_ett_manus",
    language: "sv",
    question: "Vad kostar ett manus till reklamfilm?",
    shortAnswer:
      "Ett reklamfilmsmanus kostar i Sverige 2026 typiskt 25 000–75 000 kr. Manus till en företagsfilm ligger på 8 000–20 000 kr och kortformat för sociala medier på 4 000–9 000 kr.",
    longAnswer:
      "Priset på ett manus styrs framför allt av om konceptarbetet ingår. Att skriva ut ett redan beslutat koncept är hantverk med förutsägbar tidsåtgång. Att hitta idén — research, idégenerering och presentation av flera spår — är ett eget arbete och står ofta för halva kostnaden.\n\nRiktpriser exklusive moms i Sverige 2026: kortformat till sociala medier 4 000–9 000 kr, företagsfilm 8 000–20 000 kr, reklamfilm med koncept och kanalversioner 25 000–75 000 kr, kampanjkoncept som ska bära flera filmer från 60 000 kr. Storyboard är ett separat steg på 6 000–35 000 kr.\n\nFenny arbetar med fast uppdragspris och inkluderar två revideringsrundor. Vid Prime-produktion ingår manus i projektpriset, och i Pulse-abonnemang från 14 995 kr/mån ingår löpande manus och innehållsplan.",
    relatedPage: "/vad-kostar-ett-manus",
    relatedService: "Manus och koncept",
    relatedQuestions: [
      "sv_hur_skriver_man_ett_filmmanus",
      "sv_vad_kostar_reklamfilm",
      "sv_skillnad_manus_storyboard",
    ],
    topics: ["manus", "pris", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnad_manus_storyboard",
    language: "sv",
    question: "Vad är skillnaden mellan manus och storyboard?",
    shortAnswer:
      "Manuset beskriver vad som sägs och händer i filmen. Storyboard, eller bildmanus, visar hur det ser ut bild för bild med bildutsnitt, kamerarörelse och rytm. Manus kommer först, storyboard är översättningen till bild.",
    longAnswer:
      "Ett manus är ett textdokument, oftast i tvåkolumnsformat med bild till vänster och ljud till höger. Det fastställer struktur, budskap, repliker eller speakertext och filmens längd.\n\nEn storyboard är en serie skisser där varje ruta motsvarar en bild i filmen. Rutan kompletteras med bildutsnitt, kamerarörelse, längd i sekunder och ljudnot. Bildmanus är den svenska termen för samma sak, och ett animatic är boarden klippt till rätt längd med speaker och temp-musik.\n\nAlla filmer behöver manus. Alla filmer behöver inte storyboard: intervjubaserad företagsfilm och dokumentärt material klarar sig med scenlista och shotlist, medan reklamfilm, animation och produktfilm med kamerakoreografi tjänar på en board eftersom varje inspelad sekund är dyr.",
    relatedPage: "/storyboard",
    relatedService: "Storyboard",
    relatedQuestions: [
      "sv_vad_kostar_ett_manus",
      "sv_hur_skriver_man_ett_filmmanus",
      "sv_vad_ingar_i_forproduktion",
    ],
    topics: ["manus", "storyboard", "process"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_ett_bildmanus",
    language: "sv",
    question: "Vad är ett bildmanus?",
    shortAnswer:
      "Ett bildmanus (storyboard) är en serie skisser som visar filmen bild för bild — med bildutsnitt, kamerarörelse, längd och ljudnot per ruta. Det är översättningen från textmanus till bild.",
    longAnswer:
      "Ett bildmanus består av rutor där varje ruta motsvarar en bild i den färdiga filmen. Till varje ruta hör bildutsnitt (total, halvnära, nära), kamerarörelse, ungefärlig längd i sekunder och en ljudnot om speaker, musik eller effektljud.\n\nBildmanus används för att testa filmen innan något filmas: går berättelsen ihop, hinner budskapet fram, sitter övergångarna? Det är långt billigare att ändra en skiss än en inspelad scen.\n\nAlla filmer behöver inte bildmanus. Intervjubaserad företagsfilm klarar sig med scenlista och shotlist, medan reklamfilm, animation och produktfilm med kamerakoreografi nästan alltid boardas — varje inspelad sekund är dyr.\n\nHos Fenny ingår bildmanus i förproduktionen när filmen kräver det, och vi visar boarden för er innan inspelning så att uttrycket är godkänt i förväg.",
    relatedPage: "/storyboard",
    relatedService: "Storyboard",
    relatedQuestions: [
      "sv_skillnad_manus_storyboard",
      "sv_vad_kostar_ett_manus",
      "sv_hur_skriver_man_ett_filmmanus",
    ],
    topics: ["bildmanus", "storyboard", "manus", "process"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_storytelling_film",
    language: "sv",
    question: "Vad är storytelling i företagsfilm?",
    shortAnswer:
      "Storytelling i företagsfilm betyder att berätta en historia med en människa, en konflikt och en förändring — i stället för att rada upp fakta. Det är det som får tittaren att stanna kvar och minnas budskapet.",
    longAnswer:
      "En berättelse har tre delar: en huvudperson tittaren känner igen sig i, en utmaning eller konflikt, och en förändring. I företagsfilm är huvudpersonen oftast kunden, en medarbetare eller användaren — inte företaget självt.\n\nSkillnaden mot informationsfilm är att storytelling visar i stället för att förklara. \"Våra kunder sparar tid\" är information. Att följa en kund en stressig måndag och se vad som ändras är storytelling.\n\nPraktiskt betyder det: börja med en konkret scen, inte en logotyp. Låt riktiga människor prata i eget språk. Klipp bort allt som inte driver historien framåt. En 60-sekundersfilm med en tydlig båge slår en fyrminuters genomgång av tjänster.\n\nFenny arbetar alltid story-first: i briefen letar vi efter den mänskliga vinkeln innan vi pratar format, längd eller kanaler.",
    relatedPage: "/foretagsfilm",
    relatedService: "Företagsfilm",
    relatedQuestions: [
      "sv_skillnad_manus_storyboard",
      "sv_vad_ar_employer_branding_film",
    ],
    topics: ["storytelling", "berättande", "företagsfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_manga_ord_per_minut_film",
    language: "sv",
    question: "Hur många ord speakertext går det på en minut film?",
    shortAnswer:
      "Räkna med 130–150 ord svensk speakertext per minut film. En 30-sekundersfilm rymmer alltså cirka 65–75 ord och en 15-sekundersfilm cirka 35 ord.",
    longAnswer:
      "Tumregeln 130–150 ord per minut gäller ett lugnt men engagerat speakertempo på svenska. Läser rösten snabbare hinner tittaren inte med, särskilt inte om bilden samtidigt bär information.\n\nDra av ord när filmen innehåller grafik som ska hinna läsas, pauser för musik eller bildsekvenser som ska tala för sig själva. En reklamfilm på 30 sekunder landar i praktiken ofta på 55–65 ord eftersom sista sekunderna används till varumärke och uppmaning.\n\nTesta alltid manuset genom att läsa det högt med tidtagning innan inspelning. Är texten för lång märks det i studion, och då är det dyrt att korta.",
    relatedPage: "/manus-och-koncept",
    relatedService: "Manus och koncept",
    relatedQuestions: [
      "sv_hur_skriver_man_ett_filmmanus",
      "sv_vad_kostar_ett_manus",
      "sv_hur_lang_reklamfilm",
    ],
    topics: ["manus", "produktion", "speaker"],
    schemaType: "FAQPage",
    metaDescription:
      "Snabbt svar på hur många ord speakertext som ryms per minut film, plus hur ni räknar om manuset till rätt längd innan inspelning.",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ingar_i_forproduktion",
    language: "sv",
    question: "Vad ingår i förproduktion av film?",
    shortAnswer:
      "Förproduktion omfattar manus och storyboard, produktionsplan och tidplan, budget, casting, location scouting, tillstånd och försäkring, teknikval samt call sheet till alla medverkande.",
    longAnswer:
      "Förproduktionen är allt som måste vara klart innan kameran rullar. Den brukar ta 15–25 procent av filmbudgeten och 1–6 veckor i tid beroende på omfattning: en rak företagsfilm klarar sig på ett par veckor, medan reklamfilm med casting och flera locations kräver tre till sex veckor.\n\nDe delar som oftast underskattas är tillstånd och casting. Inspelning på allmän plats i Sverige kräver polistillstånd när platsen tas i anspråk eller trafiken påverkas, och drönarflygning kräver operatörsregistrering samt särskilt tillstånd i vissa zoner. Båda kan kräva flera veckors framförhållning.\n\nDet är också i förproduktionen de största besparingarna görs: en genomarbetad storyboard och en genomtänkt scengruppering kan korta inspelningen från två dagar till en utan att sänka kvaliteten.",
    relatedPage: "/forproduktion",
    relatedService: "Förproduktion",
    relatedQuestions: [
      "sv_vad_gor_en_linjeproducent",
      "sv_skillnad_manus_storyboard",
      "sv_hur_lang_tid_tar_filmproduktion",
    ],
    topics: ["forproduktion", "process", "produktion"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_gor_en_linjeproducent",
    metaDescription: "Linjeproducenten äger budget, schema, avtal och tillstånd — och avgör vad som stryks när en inspelningsdag drar över. Så skiljer sig rollen från regissör och producent.",
    language: "sv",
    question: "Vad gör en linjeproducent?",
    shortAnswer:
      "En linjeproducent ansvarar för att en filmproduktion går att genomföra praktiskt och ekonomiskt: budget, bemanning, schema, avtal, tillstånd och logistik. Regissören äger det kreativa, linjeproducenten äger genomförbarheten.",
    longAnswer:
      "Linjeproducenten kommer in när ett koncept ska bli en verklig inspelning. Rollen omfattar att bryta ned manus och storyboard i inspelningsdagar, sätta budget post för post, boka team och teknik, teckna avtal med medverkande och locations samt söka nödvändiga tillstånd.\n\nUnder inspelning är linjeproducenten den som håller ihop tid och pengar: om en scen drar över är det linjeproducenten som avgör vad som stryks eller flyttas för att dagen ska gå ihop.\n\nPå mindre produktioner slås rollen ofta ihop med produktionsledarens. På reklamfilm med flera dagar, casting och locations är den däremot en egen funktion, och ofta den enskilt viktigaste anledningen till att en budget håller.",
    relatedPage: "/forproduktion",
    relatedService: "Förproduktion",
    relatedQuestions: [
      "sv_vad_ingar_i_forproduktion",
      "sv_vad_kostar_reklamfilm",
      "sv_hur_lang_tid_tar_filmproduktion",
    ],
    topics: ["forproduktion", "roller", "produktion"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_konceptutveckling_film",
    language: "sv",
    question: "Vad är konceptutveckling för film?",
    shortAnswer:
      "Konceptutveckling är arbetet med att hitta den bärande kreativa idé som gör budskapet värt att titta på — före manus och produktion. Resultatet är ett konceptunderlag med idé, ton, visuell riktning och bärkraft över tid.",
    longAnswer:
      "Konceptet är mekanismen som gör filmen intressant; manuset är utförandet. Ett bra koncept kan bära tio filmer medan ett bra manus utan koncept bara bär en.\n\nEtt bärande koncept klarar fyra test: det kan sammanfattas i en mening, det går att producera inom budget, det tål att upprepas utan att bli tråkigt, och det fungerar även i 9:16 utan ljud. Ett femte test är varumärkeskopplingen — om en konkurrent kunde sätta sin logotyp i slutet och filmen ändå fungerade saknar konceptet fäste i varumärket.\n\nPris i Sverige 2026: 20 000–40 000 kr för ett koncept till en enskild film och från 60 000 kr för en kampanjplattform som ska bära flera filmer och kanaler. Fenny presenterar alltid två till tre idéspår med pris och tidplan per spår.",
    relatedPage: "/konceptutveckling-film",
    relatedService: "Konceptutveckling",
    relatedQuestions: [
      "sv_vad_kostar_ett_manus",
      "sv_hur_skriver_man_ett_filmmanus",
      "sv_vad_kostar_reklamfilm",
    ],
    topics: ["koncept", "strategi", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_en_internkommunikationsfilm",
    language: "sv",
    question: "Vad är en internkommunikationsfilm?",
    shortAnswer:
      "En internkommunikationsfilm är en video producerad för medarbetare snarare än för marknaden — den används för att förmedla strategi, förändring, värderingar eller utbildning på ett tydligare och mer engagerande sätt än text.",
    longAnswer:
      "Internkommunikationsfilm används när ett företag behöver nå ut med information som riskerar att försvinna i mejl eller intranät. Vanliga case är vd-uppdateringar inför kvartalet, kulturfilm vid onboarding, förändringskommunikation vid omorganisationer och utbildningsfilmer kopplade till nya system eller processer.\n\nStilen är ofta mer rak och avskalad än publika reklamfilmer. Personerna i bild är riktiga medarbetare, inte skådespelare, och budskapet prioriteras högre än produktionsvärde. Filmerna distribueras via intranät, Teams, Slack eller LMS.\n\nFördelen är dubbel: medarbetarna tar till sig budskapet snabbare och ledningen får en konsekvent röst som inte tappas i andrahandsåterberättande. Hos Fenny görs internkommunikationsfilm typiskt löpande inom Pulse, eftersom behovet är återkommande snarare än ett enskilt projekt.",
    relatedPage: "/internkommunikation-film",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vad_ar_foretagsfilm",
      "sv_hur_anvanda_video_b2b",
      "sv_hur_fungerar_fenny_pulse",
    ],
    topics: ["internkommunikation", "foretagsfilm", "hr"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnaden_motion_graphics_animation",
    language: "sv",
    question: "Vad är skillnaden mellan motion graphics och animation?",
    metaTitle: "Motion graphics vs animation — skillnaden | Fenny",
    shortAnswer:
      "Motion graphics är animerad grafik, typografi och informationsdesign — typ förklarande loggor, ikoner och text i rörelse. Animation är ett bredare begrepp som även rymmer karaktärsanimation och berättande i 2D eller 3D.",
    longAnswer:
      "Motion graphics handlar om att få grafiska element att röra sig: logotyper som byggs upp, ikoner som förklarar ett flöde, siffror som räknas upp eller text som följer en speaker. Det används flitigt i förklarande företagsfilm, produktdemos, sociala medier och som lower-thirds i intervjufilmer.\n\nAnimation är ett bredare paraply. Här ingår motion graphics, men också karaktärsanimation, 2D-illustrationer som berättar en historia och full 3D-animation av produkter eller miljöer. Animation kräver oftare manus, storyboard och längre produktionstid.\n\nEnkel tumregel: vill du förklara något snabbt och tydligt — välj motion graphics. Vill du bygga en värld eller en karaktär som tittaren ska identifiera sig med — välj animation. Fenny levererar båda, ofta i kombination med live action.",
    relatedPage: "/motion-graphics",
    relatedService: "Fenny",
    relatedQuestions: [
      "sv_vad_ar_foretagsfilm",
      "sv_vad_kostar_videoproduktion",
      "sv_vilka_filmformat_for_sociala_medier",
    ],
    topics: ["motion-graphics", "animation", "teknik"],
    schemaType: "FAQPage",
    priority: "low",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_marker_man_film_for_youtube",
    language: "sv",
    question: "Hur märker man film för YouTube så att den hittas?",
    shortAnswer:
      "Optimera titel, miniatyrbild och de första 125 tecknen i beskrivningen — det är där 80 % av klickbeslutet sker. Lägg sedan in en tydlig hook i de första 10 sekunderna och kapitel-tidsmarkörer för längre videos.",
    longAnswer:
      "YouTube SEO börjar långt innan filmen är klar. Bestäm sökord redan i manusfasen och bygg titeln runt det som målgruppen faktiskt söker på, inte ditt interna kampanjnamn. Korta, konkreta titlar (under 60 tecken) presterar bäst.\n\nMiniatyrbilden är avgörande. Designa en custom thumbnail med stort ansikte, hög kontrast och högst tre ord text. Det är den enda faktor som direkt avgör hur många som klickar.\n\nI beskrivningen är det de första 125 tecknen som syns innan \"visa mer\" — lägg där en mening som upprepar sökordet och en länk till din egen sajt. Använd kapitel via tidsmarkörer (0:00, 0:45 osv.) för videos över 3 minuter, det ger Google chans att indexera enskilda avsnitt.\n\nSlutligen: tro inte på taggar. De har minimal effekt idag. Det som driver visningar är retention — alltså hur länge folk faktiskt tittar. Skriv därför filmen så att de första 10 sekunderna håller kvar tittaren.",
    relatedPage: "/film-for-youtube",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_vilka_filmformat_for_sociala_medier",
      "sv_hur_gor_man_en_bra_linkedin_video",
      "sv_hur_ofta_bor_foretag_publicera_video",
    ],
    topics: ["youtube", "seo", "distribution"],
    schemaType: "HowTo",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  // -------------------------------------------------------------------------
  // Heritage — filmade intervjuer för livsberättelser och kunskapsarv
  // -------------------------------------------------------------------------
  {
    id: "sv_vad_ar_filmad_livsberattelse",
    language: "sv",
    question: "Vad är en filmad livsberättelse?",
    shortAnswer:
      "En filmad livsberättelse är en strukturerad intervju där en person berättar om sitt liv, sina minnen och erfarenheter. Resultatet är en eller flera filmer som familjen kan bevara och dela vidare.",
    longAnswer:
      "En filmad livsberättelse dokumenterar en människas röst, ansiktsuttryck och berättarstil — inte bara orden. Innehållet kretsar oftast kring uppväxt, familj, arbete, viktiga händelser och de erfarenheter personen vill föra vidare.\n\nIntervjun planeras tillsammans i förväg. Frågeområden, struktur och miljö anpassas efter personen som ska intervjuas, och frågorna kan delas i förväg om det skapar trygghet.\n\nProduktionen sker professionellt, med rätt ljus, ljud och kameraarbete, så att filmen håller över tid. Slutfilmen levereras digitalt och delas privat inom familjen.",
    relatedPage: "/heritage/livsberattelser",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_bevara_livsberattelse",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "livsberattelser", "intervju"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_bevara_livsberattelse",
    language: "sv",
    question: "Hur bevarar man en livsberättelse?",
    shortAnswer:
      "Det effektivaste sättet att bevara en livsberättelse är att spela in en strukturerad filmad intervju med personen själv, där röst, ansikte och berättarstil bevaras tillsammans med innehållet.",
    longAnswer:
      "Text och fotografier bär bara en del av en livsberättelse. Det som riskerar att försvinna är tonläget, pausen före ett svar, sättet att berätta. En filmad intervju bevarar det helheten.\n\nBörja medan det finns tid. Förbered frågeområden tillsammans i familjen, gör intervjun i en lugn och välkänd miljö, och låt personen tala fritt i sin egen takt. Komplettera gärna med foton eller dokument som väcker minnen.\n\nKvalitet på ljud, ljus och klippning gör skillnad över tid. En proffsigt producerad film håller över decennier — både tekniskt och berättarmässigt.",
    relatedPage: "/heritage/livsberattelser",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_filmad_livsberattelse",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "livsberattelser", "bevara"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_kunskapsarv",
    language: "sv",
    question: "Vad är kunskapsarv?",
    shortAnswer:
      "Kunskapsarv är strukturerad filmad dokumentation av erfarenhet, arbetssätt och tyst kunskap i en organisation. Materialet används för onboarding, internutbildning och som bevarat företagsminne.",
    longAnswer:
      "I varje organisation finns kunskap som inte står i några dokument — beslutslogik, erfarenhet, kultur och arbetssätt som lärs in över år. När personer går i pension, byter roll eller lämnar organisationen försvinner den kunskapen om den inte fångats.\n\nKunskapsarv är ett sätt att dokumentera det genom filmade intervjuer med nyckelpersoner. Materialet kapitelindelas efter kunskapsområden och blir ett arbetsverktyg för efterträdare, nya medarbetare och hela organisationen.\n\nUpplägget anpassas efter syfte: enskilda intervjuer, intervjuserier eller breda dokumentationer av företagshistoria, kultur och grundarresa.",
    relatedPage: "/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_hur_overfora_kunskap_pension",
      "sv_hur_dokumentera_foretagshistoria",
    ],
    topics: ["heritage", "kunskapsarv", "kunskapsoverforing"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_dokumentera_tyst_kunskap",
    language: "sv",
    question: "Hur dokumenterar företag tyst kunskap?",
    shortAnswer:
      "Tyst kunskap dokumenteras bäst genom strukturerade filmade intervjuer där rätt frågor öppnar för konkreta exempel, beslutslogik och erfarenhet — inte abstrakta principer.",
    longAnswer:
      "Tyst kunskap är svår att skriva ner just för att den bärs i sammanhang, ton och exempel. Skriftliga handböcker missar det. Filmade intervjuer fångar både innehållet och kontexten — kroppsspråk, betoning, berättelsen kring ett beslut.\n\nFörberedelse är avgörande. Intervjuaren behöver veta vilka kunskapsområden som ska täckas, och frågorna ska tvinga fram konkreta situationer snarare än sammanfattningar. Då kommer den verkliga erfarenheten fram.\n\nMaterialet klipps sedan i kapitel per kunskapsområde, vilket gör det användbart i onboarding, intern utbildning och som referens när nya situationer uppstår.",
    relatedPage: "/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_kunskapsarv",
      "sv_hur_overfora_kunskap_pension",
    ],
    topics: ["heritage", "kunskapsarv", "tyst-kunskap"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_overfora_kunskap_pension",
    language: "sv",
    question: "Hur överför man kunskap när någon går i pension?",
    shortAnswer:
      "Genom att intervjua personen i god tid före avgång, dokumentera arbetssätt och nyckelerfarenheter på film, och klippa materialet i kapitel som efterträdare kan ta del av över tid.",
    longAnswer:
      "Kunskapsöverföring inför pension fungerar bäst när den planeras tidigt — gärna 6–12 månader före avgång. Då finns utrymme för flera intervjutillfällen och för att inkludera efterträdaren i samtalen.\n\nFokus ligger på det som inte står i några dokument: beslutslogik, kundrelationer, leverantörshistorik, varför vissa lösningar valts och vad som inte fungerat tidigare. Konkreta exempel är värdefullare än principer.\n\nFilmen kapitelindelas så att efterträdaren kan gå tillbaka till rätt avsnitt vid behov. Materialet blir ett levande arbetsverktyg snarare än ett arkiv som ingen öppnar.",
    relatedPage: "/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "kunskapsarv", "pension", "kunskapsoverforing"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_dokumentera_foretagshistoria",
    language: "sv",
    question: "Hur dokumenterar man företagshistoria?",
    shortAnswer:
      "Företagshistoria dokumenteras genom filmade intervjuer med grundare, seniora medarbetare och nyckelpersoner, kombinerat med arkivmaterial som foton, gamla filmer och dokument.",
    longAnswer:
      "En företagshistoria är summan av människor, beslut och händelser — inte en logo-tidslinje. Filmade intervjuer fångar berättelsen direkt från dem som var med, vilket ger materialet trovärdighet och nerv.\n\nTypiska källor är grundare, tidiga medarbetare, ägare, långa kundrelationer och personer som varit med vid avgörande skiften. Intervjuerna planeras kring kronologi eller teman som passar berättelsen.\n\nMaterialet blir användbart vid jubileum, generationsskiften, rekrytering, kulturarbete och som internt minne. Den färdiga filmen kompletteras ofta med kortare klipp för intranät, sociala kanaler och presentationer.",
    relatedPage: "/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_kunskapsarv",
      "sv_hur_dokumentera_tyst_kunskap",
    ],
    topics: ["heritage", "kunskapsarv", "foretagshistoria"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },

  // -------------------------------------------------------------------------
  // Heritage — filmade intervjuer, livsberättelser och kunskapsarv
  // -------------------------------------------------------------------------
  {
    id: "sv_vad_ar_fenny_heritage",
    language: "sv",
    question: "Vad är Fenny Heritage?",
    metaTitle: "Vad är Fenny Heritage? Livsberättelser på film",
    shortAnswer:
      "Fenny Heritage är en tjänst för filmade intervjuer där privatpersoner, familjer, företag och organisationer kan dokumentera livsberättelser, erfarenhet och kunskap i professionell form.",
    longAnswer:
      "Heritage samlar två besläktade behov: livsberättelser för familjer och kunskapsarv för organisationer. Den gemensamma kärnan är densamma — strukturerade, filmade intervjuer som görs med hantverk och respekt.\n\nFör privatpersoner och familjer dokumenterar vi livsberättelser, släkthistoria och personliga porträtt. För företag och organisationer dokumenterar vi grundarberättelser, erfarenhet, metoder, kulturarbete och generationsskiften.\n\nVi planerar, intervjuar, klipper och levererar digitalt. Mindre format börjar i lägre delen av Prime-spannet; mer omfattande arv är högre. Pris ges efter första samtal.",
    relatedPage: "/heritage",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_skillnad_livsberattelse_memoarbok",
      "sv_hur_gor_man_kunskapsoverforing_pa_film",
    ],
    topics: ["heritage", "intervju", "dokumentation"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_livsberattelse_pa_film",
    language: "sv",
    question: "Vad kostar en livsberättelse på film?",
    heading: "Filmad livsberättelse: vad påverkar priset?",
    shortAnswer:
      "En filmad livsberättelse kostar typiskt 35 000–150 000 kr beroende på omfattning — från en enskild intervju till ett större familjefilmsarv med flera intervjuer, arkivmaterial och kapitelstruktur.",
    longAnswer:
      "Priset styrs av hur många personer som intervjuas, hur lång inspelningen är, om arkivmaterial (foton, dokument, film) ska vävas in och hur omfattande slutmaterialet ska vara — en huvudfilm, kapitel, korta utdrag eller en kombination.\n\nLivsintervjun är vårt minsta format: ett pre-möte, ett frågeunderlag, en filmad intervju och en klippt film. Familjearvet är mer omfattande med längre intervju, kapitelstruktur och möjligheten att fläta in fotografier. Arvspaketet bygger ett mer komplett filmat arv med flera intervjuer och tydlig berättarstruktur.\n\nVi ger en offert efter ett första samtal. Allt material levereras digitalt och stannar privat inom familjen om ni vill det.",
    relatedPage: "/heritage/livsberattelser",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_fenny_heritage",
      "sv_hur_lang_tid_tar_livsberattelse",
      "sv_skillnad_livsberattelse_memoarbok",
    ],
    topics: ["heritage", "livsberättelse", "pris"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_lang_tid_tar_livsberattelse",
    language: "sv",
    question: "Hur lång tid tar det att göra en livsberättelse på film?",
    metaTitle: "Hur lång tid tar en livsberättelse på film? | Fenny",
    shortAnswer:
      "Från första samtal till färdig film tar processen normalt 4–8 veckor, beroende på omfattning, antal intervjuer och hur mycket arkivmaterial som ska bearbetas.",
    longAnswer:
      "Tidplanen följer fyra faser: planering, inspelning, klippning och granskning. Mindre format med en intervju och utan arkivmaterial kan vara klara på fyra veckor. Större familjearv med flera intervjuer och insamlat material tar två månader eller mer.\n\nVi anpassar tempot efter personen som intervjuas — det är aldrig en bra idé att stressa fram en livsberättelse. Vid hälsosituationer där tiden är kort prioriterar vi och kan komma igång med inspelning inom någon vecka.\n\nDen som intervjuas får alltid granska materialet innan leverans.",
    relatedPage: "/heritage/livsberattelser",
    relatedQuestions: [
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_vem_passar_heritage",
    ],
    topics: ["heritage", "livsberättelse", "process"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_gor_man_kunskapsoverforing_pa_film",
    language: "sv",
    question: "Hur fungerar kunskapsöverföring på film?",
    shortAnswer:
      "Kunskapsöverföring på film är strukturerade, filmade intervjuer med nyckelpersoner där erfarenhet, metoder och tyst kunskap dokumenteras i kapitlat material som kan användas för onboarding, intern utbildning och kulturarbete.",
    longAnswer:
      "Vi börjar med att kartlägga vad som faktiskt ska bevaras — vilka beslut, situationer, metoder och perspektiv som riskerar att försvinna när en person lämnar organisationen. Det är sällan ett dokument räcker.\n\nIntervjuerna struktureras i tematiska kapitel: bakgrund, metod, typiska situationer, fallgropar, principer och råd till efterträdaren. Materialet klipps i en huvudfilm plus kortare kapitelklipp som är enklare att använda i onboarding och intern utbildning.\n\nVanliga användningsområden är pensionsavgångar, generationsskiften, grundarbyten och dokumentation av kritiska roller. Materialet ägs av organisationen och hanteras under tystnadsplikt.",
    relatedPage: "/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_fenny_heritage",
      "sv_vem_passar_heritage",
    ],
    topics: ["heritage", "kunskapsarv", "onboarding"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_skillnad_livsberattelse_memoarbok",
    language: "sv",
    question: "Vad är skillnaden mellan en livsberättelse på film och en memoarbok?",
    metaTitle: "Livsberättelse på film vs memoarbok | Fenny",
    shortAnswer:
      "En filmad livsberättelse bevarar rösten, ansiktet och berättarsättet — en memoarbok bevarar texten. Filmen gör att kommande generationer faktiskt får möta personen, inte bara läsa om hen.",
    longAnswer:
      "Memoarboken kräver att personen själv (eller en skribent) skriver fram ett kronologiskt narrativ. Det är ett stort åtagande och ofta krävande för den äldre.\n\nEn filmad livsberättelse bygger på samtal. Vi ställer frågorna; personen svarar i sin egen takt. Resultatet bevarar kroppsspråk, röst, humor och de minnen som dyker upp spontant — sånt som sällan ryms i text.\n\nMånga familjer kombinerar: en filmad livsberättelse som primär dokumentation, kompletterad med en transkription eller bok om man vill ha läsbart material också.",
    relatedPage: "/heritage/livsberattelser",
    relatedComparison: "/jamfor/livsberattelse-pa-film-vs-memoarbok",
    relatedQuestions: [
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_vad_ar_fenny_heritage",
    ],
    topics: ["heritage", "livsberättelse", "jämförelse"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vem_passar_heritage",
    language: "sv",
    question: "Vem passar Fenny Heritage för?",
    metaTitle: "Vem passar Heritage för? Familjer och bolag",
    shortAnswer:
      "Heritage passar familjer som vill bevara en närståendes berättelse och företag eller organisationer som vill dokumentera erfarenhet, företagshistoria, kultur och tyst kunskap innan den försvinner.",
    longAnswer:
      "På privatsidan vänder sig tjänsten till familjer som vill bevara en förälders, mor- eller farförälders, eller annan närståendes livshistoria. Det är ofta aktuellt vid jubileum, hälsosituationer eller när någon går i pension och får tid att berätta.\n\nPå företagssidan vänder sig tjänsten till organisationer med kritisk erfarenhet i ett fåtal personer: pensionsavgångar, generationsskiften, grundarbyten, specialistroller och kulturarbete. Materialet används för onboarding, intern utbildning, jubileer och intranät.\n\nGemensamt: när det är värdefullt att bevara berättelser, kunskap eller kultur i en form där rösten och personen faktiskt finns kvar.",
    relatedPage: "/heritage",
    relatedQuestions: [
      "sv_vad_ar_fenny_heritage",
      "sv_hur_gor_man_kunskapsoverforing_pa_film",
    ],
    topics: ["heritage", "målgrupp"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_linkedin_video",
    language: "sv",
    question: "Vad kostar en LinkedIn-video 2026?",
    shortAnswer:
      "En enkel LinkedIn-video (1 inspelningsdag, 1 huvudklipp + 3–5 versioner) kostar 25 000–60 000 kr. Löpande LinkedIn-flöde via abonnemang börjar på 14 995 kr/mån.",
    longAnswer:
      "Prisspannet drivs av två saker: hur mycket ni redan har färdigt (manus, talangprofiler, klädkod, plats) och hur många versioner ni behöver (9:16 för feed, 1:1 för organisk, 16:9 för webb, undertexter på svenska och engelska).\n\nProjekt: enkel talking-head eller kundcase-video landar 25 000–60 000 kr. Story-driven video med flera locations, motion graphics eller animerad text kostar 60 000–180 000 kr. Reklamfilm med produktionsvärde för paid distribution — 150 000–400 000 kr.\n\nAbonnemang (Pulse) från 14 995 kr/mån ger löpande flöde med strategi, produktion, klipp och versionering — normalt 2–4 färdiga videor per månad. Det brukar bli billigare per klipp när ni behöver mer än 6 videor per år.\n\nGömda kostnader att lägga till: talangarvode (om ni inte använder egen personal), reklamscript-copy, undertextning och paid-tester. Fenny inkluderar allt det ovan i fast pris.",
    relatedPage: "/priser",
    relatedQuestions: [
      "sv_vad_kostar_reklamfilm",
      "sv_hur_gor_man_en_bra_linkedin_video",
      "sv_vad_kostar_videoproduktion",
    ],
    topics: ["pris", "linkedin", "b2b"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_30_sek_reklamfilm",
    language: "sv",
    question: "Vad kostar en 30-sekunders reklamfilm 2026?",
    shortAnswer:
      "En 30-sekunders reklamfilm i Sverige kostar 150 000–500 000 kr som projekt. Enklare paid-social-versioner: 60 000–150 000 kr. Broadcast-nivå med talang och location: 400 000–900 000 kr.",
    longAnswer:
      "Priset avgörs av produktionsnivå, inte längd. En 30-sekundare kan vara en enkel motion-graphics-annons eller en full broadcast-produktion — samma längd, tio gånger prisskillnad.\n\nEnklare paid-social-format (1 dag, minimal crew, befintlig talang eller kundintervjuer): 60 000–150 000 kr. Full reklamfilm med regisserad scen, hyrd talang, styling, professionell ljud- och ljussättning: 150 000–500 000 kr. Broadcast-nivå med flera locations, VFX eller stjärntalang: 400 000–900 000 kr och uppåt.\n\nExtra kostnader att räkna med: musiklicens (5 000–50 000 kr), rättigheter för skådespelare vid mediaköp, produktionsförsäkring och versionering (9:16, 1:1, 16:9, textade varianter).\n\nRåd: bestäm distributionen först. Ska filmen bara köras på paid social behöver ni inte broadcast-produktion. Ska den in i linjär TV höjs kraven på ljus, ljud och postproduktion — och därmed budget.",
    relatedPage: "/vad-kostar-en-reklamfilm",
    relatedQuestions: [
      "sv_vad_kostar_reklamfilm",
      "sv_hur_lang_reklamfilm",
      "sv_vad_kostar_filmproduktion",
    ],
    topics: ["pris", "reklamfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_produktfilm",
    language: "sv",
    question: "Vad kostar en produktfilm 2026?",
    shortAnswer:
      "En enklare produktfilm i studio (1 dag, 1 produkt, 60–90 sek): 35 000–90 000 kr. Story-driven produktfilm med användningsmiljöer: 90 000–300 000 kr. 3D-animerad produktfilm: 60 000–450 000 kr.",
    longAnswer:
      "Tre nivåer att välja mellan:\n\n1) **Rent studio-produktion**: produkten på svart eller ljus bakgrund, roterande turnbord, close-ups och detaljer. 1 inspelningsdag räcker för de flesta enskilda produkter. 35 000–90 000 kr.\n\n2) **Produkt-i-miljö**: produkten används av en talang i realistiska scenarion (kök, kontor, verkstad). Två inspelningsdagar, 1–2 talanger. 90 000–300 000 kr.\n\n3) **3D-animerad produktfilm**: produkten byggs och renderas i 3D — bra för produkter som är svåra att filma (inuti maskiner, kemi, mikroskopisk skala) eller när det behövs flera färger/varianter från samma film. 60 000–450 000 kr beroende på detaljnivå.\n\nFör e-handel med många SKU:er sänker abonnemang (Pulse) kostnaden per film markant — vi bygger då en produktionsmall och kör flera produkter per inspelningsdag.",
    relatedPage: "/produktfilm",
    relatedQuestions: [
      "sv_vad_kostar_videoproduktion",
      "sv_skillnaden_motion_graphics_animation",
    ],
    topics: ["pris", "produktfilm", "e-handel"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_explainer_video",
    language: "sv",
    question: "Vad kostar en explainer-video 2026?",
    shortAnswer:
      "Animerad explainer-video (60–90 sek): 45 000–180 000 kr. Live-action explainer med talang: 60 000–250 000 kr. Whiteboard/kinetic typography: 35 000–90 000 kr.",
    longAnswer:
      "En explainer-video förklarar en produkt, tjänst eller idé på 60–120 sekunder. Priset drivs av animationsteknik och detaljrikedom.\n\n**Kinetic typography / whiteboard**: text och enkla ikoner som animeras i takt med en voice-over. Snabb produktion, 35 000–90 000 kr. Bra för interna utbildningar eller enklare pitchar.\n\n**Motion graphics / 2D-animerad karaktär**: illustrerade scener, karaktärer, ikoner. Kräver design + animation + voice-over + ljuddesign. 45 000–180 000 kr. Vanligast för SaaS och B2B-lansering.\n\n**Live-action explainer**: verklig talang som förklarar plus insertade skärmdumpar eller motion graphics. 60 000–250 000 kr. Skapar mest förtroende men tar längre tid.\n\n**Full 3D-animerad explainer** (tekniska produkter, medicin, hårdvara): 120 000–450 000 kr beroende på komplexitet.\n\nTumregel: varje sekund färdig animation kräver ca 1 timmes animationsarbete på 2D-nivå, 3–5 timmar på 3D.",
    relatedPage: "/animerad-reklamfilm",
    relatedQuestions: [
      "sv_skillnaden_motion_graphics_animation",
      "sv_vad_kostar_reklamfilm",
    ],
    topics: ["pris", "explainer", "animation"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_en_explainer_film",
    language: "sv",
    question: "Vad är en explainer film?",
    shortAnswer:
      "En explainer film är en kort film (30–90 sekunder) som förklarar en produkt, tjänst eller idé på ett begripligt sätt — oftast animerad med voice-over och tydlig uppmaning. Pris: 40 000–250 000 kr beroende på animationsteknik.",
    longAnswer:
      "En explainer film (explainer-video) kokar ner ett komplext erbjudande till en mening och en tittarvinst. Den används som sajtfilm ovanför folden, som intro i säljmöten, i annonsering och i onboarding.\n\n**Tre huvudformat:**\n- Animerad explainer (motion graphics eller 2D-karaktärer): 40 000–250 000 kr, 4–8 veckor. Bäst för abstrakta produkter som SaaS och finansiella tjänster.\n- Live-action explainer: 60 000–180 000 kr, 3–4 veckor. Bäst när en fysisk produkt eller person ska förklara.\n- Hybrid: filmad bas med animerade overlays.\n\nNyckeln är manuset: tål det inte att läsas högt funkar inte filmen. Därför börjar en professionell produktion alltid med manus och storyboard innan en enda bild produceras.",
    relatedPage: "/explainer-video",
    relatedQuestions: [
      "sv_vad_kostar_explainer_video",
      "sv_skillnaden_motion_graphics_animation",
    ],
    topics: ["explainer", "animation", "definition"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_hook_rate",
    metaDescription: "30–55 % på TikTok och Reels, 25–45 % på LinkedIn. Formeln, siffrorna per kanal och greppet som sänker CPA med 30–120 %.",
    language: "sv",
    question: "Vad är hook rate?",
    shortAnswer:
      "Hook rate är andelen tittare som tittar minst 3 sekunder av alla som filmen visas för — nyckeltal för om videon fångar uppmärksamhet. Bra hook rate 2026: 30–55 % på TikTok/Reels, 25–45 % på LinkedIn, 35–60 % på YouTube in-stream.",
    longAnswer:
      "Formel: **hook rate = tittare i 3+ sekunder ÷ totala impressions**. Meta kallar det \"3-second video views ÷ impressions\", TikTok \"video views ÷ impressions\", LinkedIn samma.\n\nHook rate mäter första sekunderna av filmen — själva \"kroken\". Om hook rate är låg spelar inget annat roll: ingen ser CTA:n, ingen mäter completion rate, ingen klickar.\n\nBenchmark 2026:\n- TikTok organisk: 25–35 % medel, 35–55 % bra.\n- Reels organisk: 22–32 % medel, 32–50 % bra.\n- LinkedIn paid: 18–28 % medel, 28–45 % bra.\n- Meta paid feed: 20–30 % medel, 30–45 % bra.\n- YouTube in-stream skippable: 30–45 % medel, 45–60 % bra.\n\nStörsta hävstången på hook rate är att spela in 4 hook-varianter per film, testa dem parallellt i 48 h och dubbla budget på vinnaren. Kostar 5–15 % extra i produktion, ger 30–120 % lägre CPA.",
    relatedPage: "/kunskap/vad-ar-en-hook-video",
    relatedQuestions: [
      "sv_hur_gor_man_en_bra_linkedin_video",
      "sv_vilka_filmformat_for_sociala_medier",
    ],
    topics: ["metrics", "sociala-medier", "paid"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_ar_b_roll",
    language: "sv",
    question: "Vad är B-roll?",
    shortAnswer:
      "B-roll är kompletterande bildmaterial som klipps in ovanpå huvudljudspåret (A-roll, oftast en intervju eller talkinghead) för att göra filmen visuellt levande och bygga kontext.",
    longAnswer:
      "Termen kommer från 1900-talets filmklippning, då huvudljudet klipptes från \"A-rullen\" och kompletterande bilder från \"B-rullen\". Idag används begreppet främst i redaktionell film, dokumentär, företagsfilm och sociala medier.\n\nExempel: i en kundcase-film är A-roll intervjun med kunden framför kameran. B-roll är alla bilder som klipps in ovanpå intervjuljudet — kunden i sin arbetsmiljö, produkten i användning, medarbetare, close-ups, drönar-bilder.\n\nBra B-roll fyller tre funktioner:\n1) **Kontextualiserar** vad som sägs (\"vi jobbade med tunga maskiner\" + bild på maskin).\n2) **Ger klipptekniska möjligheter** att korta ner intervjun utan hopp.\n3) **Höjer produktionsvärdet** — statisk talking head känns billigt, B-roll gör det till en film.\n\nTumregel: för en 2-minutersvideo behöver ni 15–30 min B-roll att välja från. Planera B-roll som en egen deltagare i inspelningen, inte som en eftertanke.",
    relatedPage: "/foretagsfilm",
    relatedQuestions: [
      "sv_hur_skriver_man_ett_filmmanus",
      "sv_hur_lang_tid_tar_filmproduktion",
    ],
    topics: ["produktion", "terminologi"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_stor_budget_marknadsavdelning_video",
    language: "sv",
    question: "Hur stor del av marknadsbudgeten bör läggas på video 2026?",
    metaTitle: "Hur stor del av marknadsbudgeten bör gå till video?",
    shortAnswer:
      "B2B-marknadsavdelningar bör lägga 20–35 % av contentbudgeten på video 2026. DTC/e-handel: 40–60 %. Nystartade varumärken: minst 30 % av total marknadsbudget första året för att bygga igenkänning.",
    longAnswer:
      "Videos andel av total marknadsbudget har ökat varje år sedan 2018. 2026 är en sund riktnivå:\n\n- **B2B (SaaS, tjänster, industri)**: 20–35 % av contentbudgeten på video (både produktion och distribution). LinkedIn video är den enskilt viktigaste kanalen — 2–3 färdiga videor per månad räcker för de flesta.\n- **DTC / e-handel**: 40–60 % på video. Meta, TikTok och YouTube Shorts driver merparten av trafiken; utan produktvideo och paid-creative i pipeline stannar tillväxten.\n- **Nystartade varumärken första året**: minst 30 % av total marknadsbudget. Video är det enda formatet som bygger både räckvidd och igenkänning snabbt.\n\nInom videobudgeten är fördelningen ungefär: 35–50 % produktion, 30–45 % paid distribution, 10–20 % strategi/kreativ testning.\n\nMisstag att undvika: att lägga hela budgeten på 1–2 stora produktioner per år. En jämn takt av mindre produktioner (2–4 per månad) ger mer data, snabbare inlärning och bättre resultat än ett dyrt hero-projekt.",
    relatedPage: "/b2b-video",
    relatedQuestions: [
      "sv_hur_ofta_bor_foretag_publicera_video",
      "sv_vilka_filmer_behover_ett_tillvaxtbolag",
    ],
    topics: ["strategi", "budget", "b2b"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_dronarfilm",
    language: "sv",
    question: "Vad kostar en drönarfilm 2026?",
    shortAnswer:
      "Drönar-uppdrag som insert i annan produktion: 8 000–20 000 kr per dag. Fristående drönarfilm för fastighet, event eller anläggning: 15 000–60 000 kr. Komplexa uppdrag med tillstånd i CTR-zoner: 25 000 kr och uppåt.",
    longAnswer:
      "Pris drivs av tre saker: tillståndsnivå, utrustning och postproduktion.\n\n**Tillstånd**: drönare under 250 g i öppen kategori är enklast. Tyngre drönare (DJI Mavic, Inspire, cinema-drönare) kräver A1/A2/A3-behörighet och ofta enskild flygtillståndsansökan i CTR-zoner (kring flygplatser). Fenny har A2 och pågående operatörsgodkännande.\n\n**Utrustning**: DJI Mini/Mavic-klass för de flesta fastigheter och event. Cinema-drönare (Inspire 3, Alta X) för reklamfilm och broadcast — dyrare crew (pilot + operatör).\n\n**Postproduktion**: rågradering och grade kostar tid. En 2-minuters drönarfilm för fastighet är typiskt 15 000–30 000 kr all-inclusive.\n\nAkut behov: säkerställ att er drönaroperatör har giltigt tillstånd för zonen ni vill filma i. Sena tillståndsansökningar (< 5 arbetsdagar) i CTR-zoner går ofta inte att lösa.",
    relatedPage: "/videoproduktion",
    relatedQuestions: [
      "sv_vad_kostar_filmproduktion",
      "sv_vad_kostar_videoproduktion",
    ],
    topics: ["pris", "dronare", "fastighet"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_ofta_publicera_linkedin_video",
    language: "sv",
    question: "Hur ofta bör ett B2B-företag publicera video på LinkedIn 2026?",
    metaTitle: "Hur ofta ska B2B publicera video på LinkedIn? | Fenny",
    shortAnswer:
      "2–4 videor per månad är sweet spot för de flesta B2B-företag 2026. Under 2/mån ger algoritmen inte tillräckligt att jobba med; över 8/mån urvattnar räckvidden per klipp.",
    longAnswer:
      "LinkedIn-algoritmen belönar konsekvent publicering av samma format. Ett B2B-företag som publicerar 2–4 videor per månad från sitt företagskonto plus 1–3 personliga videor från nyckelpersoner (VD, försäljningschef, CTO) ligger i sweet spot.\n\nLägre frekvens (1/mån eller mer sällan) gör att algoritmen inte lär sig vem er målgrupp är — räckvidden per klipp blir låg.\n\nHögre frekvens (mer än 8/mån) leder oftast till att räckvidden per klipp sjunker eftersom ni tävlar mot er själva. Undantag: när flera olika medarbetare postar från personliga konton med olika vinklar.\n\nFormatmix: 60 % korta videor (30–90 sek, talking head eller B-roll), 30 % kundcase eller storydrivna klipp (1–3 min), 10 % längre thought leadership (4–8 min).\n\nAtt tänka på: två videor per månad kräver oftast 4–6 timmars redaktionell tid från er sida (planering, inspelning, godkännande). Det är därför de flesta väljer abonnemangslösning för att slippa friktionen.",
    relatedPage: "/b2b-video",
    relatedQuestions: [
      "sv_hur_gor_man_en_bra_linkedin_video",
      "sv_vad_kostar_linkedin_video",
      "sv_hur_stor_budget_marknadsavdelning_video",
    ],
    topics: ["strategi", "linkedin", "b2b"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vilken_kamera_foretagsfilm",
    language: "sv",
    question: "Vilken kamera används för företagsfilm och reklamfilm 2026?",
    metaTitle: "Vilken kamera används till företagsfilm? | Fenny",
    shortAnswer:
      "Vanligast i Sverige 2026: Sony FX3/FX6/FX9 för företagsfilm och sociala medier, ARRI Alexa Mini eller Sony Venice för reklamfilm i broadcast-kvalitet, RED Komodo för specialuppdrag.",
    longAnswer:
      "Val av kamera drivs av tre saker: färgvetenskap, dynamic range och post-workflow.\n\n**Företagsfilm och sociala medier**: Sony FX3 (kompakt, snabb), FX6 (semi-cinema, S-Log3, dual base ISO) och FX9 (större sensor, full-frame). Bra dynamic range, snabb autofokus, jobbar smidigt med gimbals och drönare. Sony är standard hos svenska produktionsbolag för dessa format.\n\n**Reklamfilm i broadcast-kvalitet**: ARRI Alexa Mini eller Alexa Mini LF, Sony Venice/Venice 2, RED Komodo eller V-Raptor. ARRI-färgvetenskap är fortfarande industristandard för hudtoner. Sony Venice används mycket när man behöver S-Log3-workflow parallellt med FX-kameror.\n\n**Specialbehov**: RED Komodo för high-frame-rate slow motion, Blackmagic URSA för budgetprojekt med Raw-workflow, DJI Ronin 4D för gimbal-driven flödesbild.\n\nFör kunden spelar kameran mindre roll än ljus- och ljudkedjan. Två filmer inspelade på samma kamera kan se helt olika ut beroende på ljussättning, optik och grade. Ställ hellre frågan \"vem gör grade och ljus?\" än \"vilken kamera?\".",
    relatedPage: "/foretagsfilm",
    relatedQuestions: [
      "sv_hur_lang_tid_tar_filmproduktion",
      "sv_skillnaden_foretagsfilm_och_reklamfilm",
    ],
    topics: ["teknik", "produktion"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_lang_ska_foretagsfilm_vara",
    language: "sv",
    question: "Hur lång ska en företagsfilm vara?",
    shortAnswer:
      "Rätt längd 2026: 30–60 sek för sociala medier och paid, 90–120 sek för hemsida/förstasidan, 2–4 min för kundcase och rekrytering, 5–8 min för intern kommunikation eller djupare storytelling.",
    longAnswer:
      "Längden ska följa distributionen och tittarintentionen, inte tvärtom.\n\n**Paid social (Meta, LinkedIn, TikTok)**: 30–60 sek. Kortare räknas oftast som mer effektivt per krona, men 60 sek går bra så länge de första 5 sekunderna hookar hårt.\n\n**Hemsida-hero eller \"Om oss\"-video**: 90–120 sek. Besökaren har redan visat intresse (kom till sidan) men gör en snabb utvärdering.\n\n**Kundcase-film**: 2–4 min. Behöver tid att bygga kontext, presentera kund, beskriva utmaning och lösning, landa i resultat.\n\n**Rekryteringsfilm / employer branding**: 2–4 min för storydrivna case, 30–60 sek för snabba klipp om enskilda medarbetare.\n\n**Intern kommunikation eller djupare storytelling**: 5–8 min funkar när innehållet är genuint intressant och distributionen sker via mail, intranet eller en YouTube-kanal där algoritmen belönar tittartid.\n\nMisstag att undvika: en 4-minutersvideo som är egentligen 90 sek innehåll utspätt. Klippa hårt och släppa släppet — bättre att levera 60 tighta sekunder än 4 minuter halvljuma.",
    relatedPage: "/foretagsfilm",
    relatedQuestions: [
      "sv_hur_lang_reklamfilm",
      "sv_vilka_filmformat_for_sociala_medier",
    ],
    topics: ["strategi", "företagsfilm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_gor_man_bra_manus_foretagsfilm",
    language: "sv",
    question: "Hur skriver man ett bra manus till en företagsfilm?",
    shortAnswer:
      "Börja med målgruppens problem, inte er lösning. Håll en enda huvudpoäng per film. Skriv för örat (kortare meningar, spoken word). Sätt en tydlig CTA i sista 5 sekunderna.",
    longAnswer:
      "Fem principer som skiljer ett fungerande manus från en tråkig informationsfilm:\n\n1) **Öppna med målgruppens problem eller värld, inte er själva**. \"Ni har 12 sekunder att fånga en säljchefs uppmärksamhet\" är starkare öppning än \"Vi på Fenny hjälper er med video\".\n\n2) **En huvudpoäng per film**. Om filmen försöker säga tre saker minns tittaren ingen av dem. Vad är EN sak ni vill att tittaren ska tänka efter filmen?\n\n3) **Skriv för örat, inte för ögat**. Läs manuset högt medan ni skriver. Om ni snubblar på en mening — kortare, enklare. Bra företagsfilm låter som en verklig människa talar, inte som en företagsbroschyr uppläst.\n\n4) **Storyboard eller scenlista parallellt med manuset**. Text som funkar på papper kan vara omöjlig att filma på ett engagerande sätt. Bildidéer parallellt sparar dagar i inspelning.\n\n5) **Sista 5 sekunderna = CTA + varumärke**. \"Besök fenny.se\" räcker inte — säg vad tittaren ska göra härnäst och varför. Ett tydligt löfte, en tydlig länk.\n\nVanligt misstag: manus skrivet av produktchef som räknar upp alla features. Bättre: en journalist eller manusförfattare intervjuar produktchefen, plockar det som är intressant för målgruppen, kastar resten.",
    relatedPage: "/foretagsfilm",
    relatedQuestions: [
      "sv_hur_lang_ska_foretagsfilm_vara",
      "sv_vad_ar_b_roll",
    ],
    topics: ["manus", "produktion"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_vad_kostar_film_pa_abonnemang",
    language: "sv",
    question: "Vad kostar film på abonnemang 2026?",
    shortAnswer:
      "Fennys Pulse-abonnemang startar på 14 995 kr/mån för 3–4 färdiga videor och skalar upp till ~60 000 kr/mån för 15–20 videor. Ingen bindning — rullar månadsvis och sägs upp när som helst.",
    longAnswer:
      "Ett filmabonnemang prissätts efter antal färdiga videor per månad, inte efter timmar. Fennys tre nivåer 2026: Pulse Start (14 995 kr/mån, 3–4 videor), Pulse Grow (~29 000 kr/mån, 6–8 videor) och Pulse Scale (~55 000 kr/mån, 15–20 videor).\n\nJämfört med enskilda projektproduktioner blir styckpriset per video 60–75 % lägre vid volym över fem videor per månad, eftersom pre-prod och crew-kostnad delas över hela månadens material.\n\nAlla nivåer inkluderar strategi, manus, foto, klipp, grade, ljud och versionering för alla format ni behöver. Ni betalar aldrig timarvode ovanpå — månadspriset är taket. Ni kan skala upp eller ner mellan månader, och säga upp när som helst.",
    relatedPage: "/priser",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_pulse_vs_prime_val",
    ],
    topics: ["priser", "pulse", "abonnemang"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_pulse_vs_prime_val",
    language: "sv",
    question: "Ska jag välja Pulse eller Prime?",
    shortAnswer:
      "Välj Pulse om ni behöver löpande videoflöde till fast månadspris — LinkedIn, Reels, kundcase, sociala. Välj Prime när ni gör en enstaka stor produktion med högt produktionsvärde — reklamfilm, brandfilm eller dokumentär.",
    longAnswer:
      "Pulse är för marknadsavdelningar som behöver publicera minst 3–4 videor per månad utan att förhandla varje produktion. Ni får samma team över tid, fast månadspris och första klippet inom 10–14 dagar. Passar LinkedIn-content, kundcase, employer branding-serier och kampanjcutdowns.\n\nPrime är för enskilda större produktioner där konceptet, ambitionen och produktionsvärdet motiverar en projektbudget på 150 000 kr och uppåt. Reklamfilm 15–30 sek, brandfilm 60–90 sek, dokumentär, image-film. Egen offert per projekt, fast pris, leverans 4–8 veckor.\n\nMånga kunder kombinerar båda — Prime för hero-produktionen som lanseras stort, Pulse för det löpande flödet som håller uppmärksamheten kvar mellan kampanjerna. Det ger 10× fler kontaktytor per krona än enbart engångsproduktioner.",
    relatedPage: "/jamfor/pulse-vs-prime",
    relatedComparison: "/jamfor/pulse-vs-prime",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_hur_fungerar_fenny_prime",
      "sv_vad_kostar_film_pa_abonnemang",
    ],
    topics: ["priser", "pulse", "prime", "jamforelse"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_hur_snabbt_leverans_pulse",
    language: "sv",
    question: "Hur snabbt får jag första videon med Pulse?",
    shortAnswer:
      "Första klippet är ute inom 10–14 dagar från signerat avtal. Efter första månaden håller vi en jämn takt med 1–2 nya videor i veckan beroende på nivå.",
    longAnswer:
      "Onboarding tar 5 arbetsdagar: strateg-möte, tone-of-voice-workshop, produktionsplan för första månaden och bokning av inspelningsdag. Inspelning sker dag 7–10, första leverans dag 12–14.\n\nFrån månad två håller vi en jämn publiceringstakt: Pulse Start 1 video/vecka, Grow 2 videor/vecka, Scale 3–5 videor/vecka. Ni godkänner alltid manus och grovklipp innan finalisering — vi låser aldrig något som ni inte sett.\n\nAkuta kampanjer med fast deadline hanteras via Prime-flödet där vi sätter dedikerat team inom 5 arbetsdagar och kan leverera reklamfilm på 3 veckor istället för 4–6.",
    relatedPage: "/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_kostar_film_pa_abonnemang",
    ],
    topics: ["pulse", "leverans", "process"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_kan_jag_pausa_pulse",
    language: "sv",
    question: "Kan jag pausa eller säga upp mitt filmabonnemang?",
    shortAnswer:
      "Ja. Pulse rullar månadsvis utan bindning — pausa eller säg upp när ni vill. Ingen exit-avgift, inget dolt.",
    longAnswer:
      "Pulse har ingen bindningstid — ni kan pausa eller säga upp när som helst. Vi tycker att abonnemang ska vinnas varje månad, inte låsas in med långa kontrakt.\n\nAllt raw-material lagras 12 månader efter uppsägning och ni kan alltid licensiera det för återanvändning. Mastrade filer i alla format tillhör er från leveransdagen.\n\n94 % av våra Pulse-kunder förnyar automatiskt efter första 12 månaderna — inte för att de måste, utan för att flödet fungerar.",
    relatedPage: "/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "sv_hur_fungerar_fenny_pulse",
      "sv_vad_kostar_film_pa_abonnemang",
    ],
    topics: ["pulse", "villkor", "avtal"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_filmproduktion_stockholm_pris",
    language: "sv",
    question: "Vad kostar filmproduktion i Stockholm 2026?",
    shortAnswer:
      "Social-content 40 000–120 000 kr. Reklamfilm 15–30 sek: 150 000–450 000 kr. Brandfilm 60–90 sek: 250 000–700 000 kr. Pulse-abonnemang från 14 995 kr/mån.",
    longAnswer:
      "Prislistan är rikstäckande: samma spann i Stockholm som i resten av Sverige, utan ortstillägg eller resepåslag. Det som styr priset är omfattning — inspelningsdagar, crewstorlek och efterarbete. Fenny lämnar fast pris inom 48 h efter brief.\n\nTypiska prislappar 2026: social-content-paket (5–10 vertikala klipp, 1 inspelningsdag) 40 000–120 000 kr. Reklamfilm 15–30 sek med manus, regi, foto och post: 150 000–450 000 kr. Brandfilm 60–90 sek med koncept, hero + cutdowns: 250 000–700 000 kr. Kundcase och dokumentär: 45 000–260 000 kr. Employer branding-serie: 170 000–500 000 kr.\n\nAlternativt Pulse-abonnemang från 14 995 kr/mån för löpande output — passar Stockholm-baserade marknadsteam som publicerar minst en video i veckan. Vi producerar i hela Stockholmsregionen inklusive Solna, Kista, Södertälje och Uppsala.",
    relatedPage: "/filmproduktion/stockholm",
    relatedQuestions: [
      "sv_vad_kostar_film_pa_abonnemang",
      "sv_vad_kostar_30_sek_reklamfilm",
    ],
    topics: ["priser", "stockholm", "filmproduktion"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_filmproduktion_goteborg_pris",
    language: "sv",
    question: "Vad kostar filmproduktion i Göteborg 2026?",
    shortAnswer:
      "Social-content 40 000–120 000 kr. Reklamfilm 15–30 sek: 150 000–450 000 kr. Brand-/industrifilm 60–90 sek: 250 000–700 000 kr. Pulse-abonnemang från 14 995 kr/mån.",
    longAnswer:
      "I Göteborg gäller samma rikstäckande prislista som i Stockholm — inga ortstillägg och inga resepåslag. Priset styrs av omfattning, inte av var produktionen sker. Fenny har producerat 120+ filmer på västkusten sedan 2019.\n\nTypiska prislappar 2026: social-content 40 000–120 000 kr, reklamfilm 15–30 sek 150 000–450 000 kr, brand-/industrifilm 250 000–700 000 kr, kundcase 45 000–260 000 kr, employer branding 60 000–180 000 kr. Fast pris inom 48 h.\n\nVi har särskild vana av västkustens industri- och hamn-miljöer: SSG-direktiv, PPE, tunga maskiner, rena rum och drönar-tillstånd för regionens CTR-zoner. Långvariga kunder inkluderar Renova, Garageportexperten och Wimeck.",
    relatedPage: "/filmproduktion/goteborg",
    relatedQuestions: [
      "sv_vad_kostar_film_pa_abonnemang",
      "sv_filmproduktion_stockholm_pris",
    ],
    topics: ["priser", "goteborg", "filmproduktion"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_dolda_kostnader_filmproduktion",
    language: "sv",
    question: "Vilka dolda kostnader kan tillkomma i en filmproduktion?",
    metaTitle: "Dolda kostnader i filmproduktion — 7 poster | Fenny",
    shortAnswer:
      "Hos Fenny finns inga dolda kostnader — offerten är fast pris inklusive resa inom regionen, revisionsrundor, format-versionering och rättigheter. Extra kostnader tillkommer bara vid utökad scope, som talent-arvoden, musiklicensiering utanför standard, location-hyra i särskilda studios eller flyg/hotell vid produktioner utanför Sverige.",
    longAnswer:
      "Branschen har historiskt lagt in dolda poster: overtime på inspelningsdag, extra revisionsrundor, format-avgifter, arkivmusik-licenser, transportkostnader mellan location och studio. Fennys standard är att allt det ingår i fast pris — annars är det inte fast pris.\n\nDet som alltid ligger utanför offerten (och alltid noteras separat): (1) betald talent/skådespelare från agentur, (2) originalmusik komponerad specifikt för filmen, (3) location-hyra i särskilda studios eller privata fastigheter, (4) resekostnader utanför Sverige, (5) buyout av rättigheter för TV-broadcast eller kinovisning. Alla dessa noteras som separata rader i offerten så ni ser exakt vad de kostar innan ni beslutar.\n\nRevisionsrundor: två fulla rundor ingår per produktion. Behöver ni fler löser vi det utan extra kostnad om det är rimligt — vi räknar inte varje kommentar.",
    relatedPage: "/priser",
    relatedQuestions: [
      "sv_vad_kostar_film_pa_abonnemang",
      "sv_hur_fungerar_fenny_prime",
    ],
    topics: ["priser", "villkor", "transparent"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "sv_faktura_betalning_film",
    language: "sv",
    question: "Hur fungerar betalning och fakturering för filmproduktion?",
    metaTitle: "Betalning och fakturering för filmproduktion | Fenny",
    shortAnswer:
      "Prime-projekt: 50 % vid produktionsstart, 50 % vid leverans. Större projekt (>500 kkr) delas i tre delbetalningar. Pulse-abonnemang: månadsvis förskott. Fakturavillkor 30 dagar netto.",
    longAnswer:
      "Prime-projekt faktureras 50/50: första fakturan går ut när avtalet signeras och produktionen startar, andra vid leverans av mastrade filer. För större projekt över 500 000 kr delas det i tre: 40 % vid start, 40 % vid inspelningsdag, 20 % vid leverans — så cashflow blir jämnare på båda sidor.\n\nPulse-abonnemang faktureras månadsvis i förskott, den 1:a varje månad. Första fakturan går ut vid signerat avtal och täcker onboarding + första månadens produktion.\n\nStandard 30 dagar netto. Vi accepterar bank-, plusgiro och Bankgirot. E-faktura via PEPPOL för offentlig sektor och större bolag. För internationella kunder fakturerar vi i EUR eller USD på begäran.",
    relatedPage: "/priser",
    relatedQuestions: [
      "sv_vad_kostar_film_pa_abonnemang",
      "sv_dolda_kostnader_filmproduktion",
    ],
    topics: ["priser", "villkor", "faktura"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },

  // -------------------------------------------------------------------------
  // Heritage — ämneskluster (privat + företag), Etapp 2026-08
  // -------------------------------------------------------------------------
  {
    id: "sv_hur_dokumentera_foralders_livsberattelse",
    language: "sv",
    question: "Hur dokumenterar man en förälders livsberättelse?",
    shortAnswer:
      "Genom en planerad, filmad intervju hemma hos personen: frågeområden tas fram tillsammans med familjen, intervjun spelas in i personens eget tempo och materialet klipps till en film som familjen äger privat.",
    longAnswer:
      "Initiativet kommer nästan alltid från barn eller barnbarn, och det svåra är sällan tekniken utan att ta första steget. Formuleringen som fungerar bäst är att filmen är till barnbarnen och stannar inom familjen — då säger de flesta ja.\n\nFörberedelsen avgör kvaliteten. Bestäm 5–8 frågeområden i förväg, låt personen se dem om det skapar trygghet, och välj en miljö där hen känner sig hemma. En intervjudag är oftast 2–4 timmar med pauser, inte en heldag.\n\nEn filmad livsberättelse kostar typiskt 35 000–150 000 kr beroende på omfattning, och tar normalt 4–8 veckor från beslut till färdig film. Vid sjukdom eller hög ålder prioriteras inspelningen och kan oftast ske inom någon vecka.",
    relatedPage: "/heritage/livsberattelse-till-foralder",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_filmad_livsberattelse",
      "sv_vilka_fragor_livsberattelseintervju",
      "sv_hur_bevara_livsberattelse",
    ],
    topics: ["heritage", "livsberattelser", "foralder"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_fraga_foralder_om_livsberattelse",
    metaDescription: "Så tar ni samtalet med en förälder om att spela in en livsberättelse — utan press. Konkreta formuleringar och vad som brukar övertyga.",
    language: "sv",
    question: "Hur frågar man en förälder om att spela in en livsberättelse?",
    metaTitle: "Så frågar du en förälder om en livsberättelse | Fenny",
    shortAnswer:
      "Berätta att filmen är till barnbarnen och att den stannar inom familjen. Det tar bort känslan av att \"ställa upp\" och gör det till en fråga om att berättelsen ska finnas kvar.",
    longAnswer:
      "Den vanligaste invändningen är inte ovilja utan blygsamhet: \"mitt liv är inget att göra film av\". Motmedlet är att flytta fokus från personen till mottagarna — barnbarn som annars aldrig får höra berättelsen i egna ord.\n\nVar konkret om vad som ska hända: ett förmöte, en inspelningsdag på 2–4 timmar med pauser, ett litet team och diskret teknik. Osäkerheten handlar oftast om det okända, inte om innehållet.\n\nErbjud också kontroll. Personen kan se frågeområdena i förväg, säga nej till områden, och allt som känns fel kan klippas bort. Filmen publiceras inte någonstans utan familjens beslut.",
    relatedPage: "/heritage/livsberattelse-till-foralder",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_foralders_livsberattelse",
      "sv_vilka_fragor_livsberattelseintervju",
    ],
    topics: ["heritage", "livsberattelser", "foralder"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_gora_film_av_slakthistoria",
    language: "sv",
    question: "Hur gör man film av sin släkthistoria?",
    shortAnswer:
      "Kombinera filmade intervjuer med de äldsta i släkten och digitaliserat arkivmaterial — foton, brev, dokument och äldre film — i en berättelse strukturerad kronologiskt eller kring teman.",
    longAnswer:
      "Släktforskning ger namn, årtal och platser. Det som saknas är rösten: varför flytten skedde, hur det kändes, vad som aldrig sades högt. Den delen finns bara hos dem som fortfarande minns.\n\nBygg filmen i två lager. Intervjua först de personer som bär berättelsen, gärna i flera generationer. Väv sedan in arkivmaterialet — fotografier, brev, kartor, hemmafilmer — så att pärmarna får sammanhang.\n\nStrukturen kan vara kronologisk, generation för generation, eller tematisk kring gården, emigrationen eller familjeföretaget. Huvudfilmen är oftast 20–45 minuter, med kortare kapitel som är lättare att visa på en släktträff.",
    relatedPage: "/heritage/slakthistoria-pa-film",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_filmad_livsberattelse",
      "sv_hur_dokumentera_foralders_livsberattelse",
    ],
    topics: ["heritage", "slakthistoria", "arkiv"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_minnesfilm",
    language: "sv",
    question: "Vad är en minnesfilm?",
    shortAnswer:
      "En minnesfilm är en kortare film som sammanfattar en människas liv eller en familjs minnen — byggd av filmade hälsningar, intervjuklipp och fotografier — och gjord för att visas vid en högtidsdag, ett jubileum eller en minnesstund.",
    longAnswer:
      "Minnesfilmen skiljer sig från en full livsberättelse genom sitt syfte: den ska visas för en samlad grupp vid ett bestämt tillfälle. Därför är den kortare och mer komprimerad, byggd för att bära känsla i ett rum med människor.\n\nVanliga byggstenar är korta hälsningar från barn och barnbarn, klipp ur en intervju med huvudpersonen, fotografier från olika årtionden och musik. Hälsningar kan spelas in på plats eller på distans.\n\n5–12 minuter fungerar bäst vid visning. Många beställer minnesfilm och full livsberättelse i samma produktion — intervjumaterialet räcker till båda och blir betydligt billigare än två separata inspelningar.",
    relatedPage: "/heritage/minnesfilm",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_kostar_minnesfilm",
      "sv_vad_ar_filmad_livsberattelse",
    ],
    topics: ["heritage", "minnesfilm", "jubileum"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_kostar_minnesfilm",
    language: "sv",
    question: "Vad kostar en minnesfilm?",
    heading: "Minnesfilm: så påverkar material och intervju priset",
    shortAnswer:
      "En minnesfilm kostar från cirka 35 000 kr när den byggs på insamlat material, och typiskt 45 000–75 000 kr om en filmad intervju med huvudpersonen ingår.",
    longAnswer:
      "Priset styrs av tre saker: om vi spelar in nytt material eller bara bearbetar befintligt, hur mycket foto- och arkivmaterial som ska digitaliseras, och hur många hälsningar som ska samlas in och ljudsättas.\n\nEn film byggd på familjens egna foton och hälsningar ligger i den lägre delen. Med en filmad intervju med huvudpersonen hamnar den typiskt på 45 000–75 000 kr — och då kan samma inspelning också ge en full livsberättelse.\n\nPlanera minst sex veckor före visningsdatum när det går. Vid minnesstund arbetar vi med kortare varsel.",
    relatedPage: "/pris/minnesfilm",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_minnesfilm",
      "sv_vad_kostar_livsberattelse_pa_film",
    ],
    topics: ["heritage", "minnesfilm", "pris"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vilka_fragor_livsberattelseintervju",
    language: "sv",
    question: "Vilka frågor ska man ställa i en livsberättelseintervju?",
    metaTitle: "60 frågor till en livsberättelseintervju | Fenny",
    shortAnswer:
      "Ställ öppna frågor som ber om konkreta situationer i stället för sammanfattningar: gå kronologiskt genom barndom, uppväxt, arbete, familj och vändpunkter, och avsluta med vad personen vill föra vidare.",
    longAnswer:
      "Frågan \"hur var din barndom?\" ger en sammanfattning. Frågan \"berätta om köket i huset där du växte upp\" ger en scen — och scener är vad som bär en film. Byt alltid abstrakt mot konkret.\n\nOrdningen spelar roll. Börja i det tidiga och ofarliga där minnena är starkast. Svårare områden — förluster, kriser, konflikter — tas i mitten, aldrig i början och aldrig sist. Avsluta i något varmt.\n\nRäkna med 5–8 frågeområden snarare än en lista: barndom och plats, föräldrar och släkt, skola och första jobbet, arbete, kärlek och familj, vändpunkter, tid och samhälle, samt råd och arv. Var tyst efter svaret — de bästa meningarna kommer i pausen.",
    relatedPage: "/heritage/intervjufragor-livsberattelse",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_spela_in_livsberattelse_sjalv",
      "sv_hur_dokumentera_foralders_livsberattelse",
    ],
    topics: ["heritage", "livsberattelser", "intervju", "guide"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_spela_in_livsberattelse_sjalv",
    language: "sv",
    question: "Hur spelar man in en livsberättelse själv?",
    shortAnswer:
      "Använd en extern mikrofon nära personen, sitt i ett rum med mjuka ytor, placera fönstret framför personen, ställ kameran på stativ och låt den gå — och säkerhetskopiera direkt på två ställen.",
    longAnswer:
      "Ljudet är viktigare än bilden. En inspelning med bra ljud och enkel bild går att titta på i decennier; motsatsen gör det inte. Mobilens inbyggda mikrofon räcker sällan — en enkel mygga eller riktmikrofon nära personen gör störst skillnad.\n\nRummet påverkar mer än utrustningen. Hårda ytor ger eko som inte går att rädda i efterhand. Välj ett rum med gardiner, mattor och möbler, och stäng av fläktar och kylskåp under inspelningen.\n\nSpela in mer än ni tror behövs — två timmar råmaterial blir kanske 30 minuter film. Klipp inte under tiden, och kopiera filerna till två platser direkt efter inspelningen. Ljudkvaliteten avgör om materialet senare går att klippa professionellt.",
    relatedPage: "/heritage/intervjufragor-livsberattelse",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vilka_fragor_livsberattelseintervju",
      "sv_vad_ar_filmad_livsberattelse",
    ],
    topics: ["heritage", "livsberattelser", "guide"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_dokumentera_kunskap_generationsskifte",
    language: "sv",
    question: "Hur dokumenterar man kunskap vid ett generationsskifte?",
    metaTitle: "Dokumentera kunskap vid generationsskifte | Fenny",
    shortAnswer:
      "Genom strukturerade filmade intervjuer med den avträdande generationen, planerade 6–12 månader före överlämningen och kapitlade per kunskapsområde så att tillträdaren kan gå tillbaka till rätt avsnitt.",
    longAnswer:
      "Generationsskiften hanteras oftast juridiskt och finansiellt med hög precision, medan kunskapsöverföringen lämnas åt tillfälliga samtal. Det som inte hinner sägas försvinner när den tidigare ägaren lämnar.\n\nVärdet ligger sällan i redan dokumenterade processer, utan i beslutslogiken: varför en viss kund alltid ringer direkt, varför ett leverantörsbyte gjordes, vilka lösningar som testats och misslyckats, och vad som håller ihop kulturen.\n\nLägg upp arbetet som en intervjuserie över tid, gärna med tillträdaren i rummet så att frågorna kommer från den som ska ta över. Kapitla materialet per område — då blir det ett arbetsverktyg i stället för ett arkiv. Kostnaden ligger typiskt på 60 000–250 000 kr beroende på antal nyckelpersoner.",
    relatedPage: "/heritage/generationsskifte-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_overfora_kunskap_pension",
      "sv_vad_ar_kunskapsarv",
      "sv_hur_dokumentera_tyst_kunskap",
    ],
    topics: ["heritage", "kunskapsarv", "generationsskifte"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_nar_ska_generationsskifte_dokumenteras",
    language: "sv",
    question: "När i ett generationsskifte bör kunskapen dokumenteras?",
    metaTitle: "När i generationsskiftet ska kunskapen filmas? | Fenny",
    shortAnswer:
      "6–12 månader före överlämningen. Då finns tid för flera intervjutillfällen och för att den tillträdande hinner formulera sina egna frågor.",
    longAnswer:
      "Tidsfönstret är avgörande. Görs dokumentationen sista månaden blir den en avskedsintervju: översiktlig, sentimental och svår att använda operativt. Med 6–12 månaders framförhållning hinner frågorna bli konkreta.\n\nDen tillträdande behöver dessutom hinna stöta på problemen själv. De skarpaste intervjuerna sker när efterträdaren redan har arbetat en tid i rollen och vet vad hen inte förstår.\n\nEn praktisk modell är tre tillfällen: ett tidigt kartläggande, ett fördjupande kring kritiska områden, och ett sista där tillträdaren ställer sina egna frågor.",
    relatedPage: "/heritage/generationsskifte-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_kunskap_generationsskifte",
      "sv_hur_overfora_kunskap_pension",
    ],
    topics: ["heritage", "kunskapsarv", "generationsskifte"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_langt_fore_pension_kunskapsoverforing",
    language: "sv",
    question: "Hur långt före pensionen bör kunskapsöverföringen göras?",
    metaTitle: "När ska kunskapsöverföringen börja? | Fenny",
    shortAnswer:
      "6–12 månader före avgången. Då finns utrymme för flera intervjutillfällen och för att efterträdaren hinner formulera sina frågor utifrån verkliga situationer.",
    longAnswer:
      "En pensionsavgång är den enda kunskapsförlust en organisation känner till i förväg — och därmed den enda som går att förbereda sig på. Ändå hanteras den ofta med en avskedslunch.\n\nPrioritera personer som är ensamma om en kritisk kompetens, har långa kundrelationer i eget förtroende eller kan systemens historik. Frågorna ska handla om situationer, undantag och tidigare misslyckanden, inte om rutinbeskrivningar.\n\nRäkna med 2–4 timmar intervju per person, ofta uppdelat på flera tillfällen. Materialet kapitlas per kunskapsområde och används i onboarding när frågan faktiskt uppstår, hellre än som en lång film vid introduktionen.",
    relatedPage: "/heritage/pensionsavgang-kunskapsoverforing",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_overfora_kunskap_pension",
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_hur_dokumentera_kunskap_generationsskifte",
    ],
    topics: ["heritage", "kunskapsarv", "pension"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_tyst_kunskap",
    language: "sv",
    question: "Vad är tyst kunskap?",
    shortAnswer:
      "Tyst kunskap är kunskap som bärs i erfarenhet och bedömning snarare än i dokument — det en van medarbetare gör rätt utan att i förväg kunna beskriva metoden.",
    longAnswer:
      "Begreppet beskriver mönsterigenkänning, omdöme och en känsla för när något är fel. Den byggs upp över år av situationer och är sällan formulerad ens för personen själv.\n\nDärför misslyckas skriftlig dokumentation ofta: den ber om en sammanfattning av något som bara existerar i konkreta situationer. En handbok kan beskriva stegen, men inte varför en erfaren person avviker från dem i vissa lägen.\n\nFilmad intervju fungerar bättre eftersom frågan kan riktas mot en specifik händelse och svaret bär tonfall, tvekan och exempel. Kvaliteten avgörs av frågedesignen — \"berätta om en gång när det gick fel\" ger mer än \"hur gör du?\".",
    relatedPage: "/heritage/tyst-kunskap-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "kunskapsarv", "tyst-kunskap"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_varfor_racker_inte_skriftlig_kunskapsdokumentation",
    language: "sv",
    question: "Varför räcker inte skriftlig dokumentation för att bevara kunskap?",
    metaTitle: "Varför räcker inte skriftlig dokumentation? | Fenny",
    shortAnswer:
      "För att skriftlig dokumentation ber om en sammanfattning av kunskap som bara framträder i konkreta situationer — och för att den tappar tonfall, exempel och de undantag där erfarenheten faktiskt sitter.",
    longAnswer:
      "Processbeskrivningar fångar hur arbetet ska göras när allt är normalt. Erfarenhet handlar om undantagen: när ska man avvika, vad brukar gå fel, vilka signaler betyder något.\n\nDet andra problemet är användningen. Handböcker läses sällan, medan ett kapitelklipp på åtta minuter tittas på när frågan uppstår. Materialets format avgör om kunskapen faktiskt överförs.\n\nDet betyder inte att dokument är onödiga. Kombinationen fungerar bäst: skriftlig process för det repeterbara, filmad intervju för bedömningen bakom processen.",
    relatedPage: "/heritage/tyst-kunskap-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_tyst_kunskap",
      "sv_hur_dokumentera_tyst_kunskap",
    ],
    topics: ["heritage", "kunskapsarv", "tyst-kunskap"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_kostar_foretagshistoria_pa_film",
    language: "sv",
    question: "Vad kostar en företagshistoriefilm?",
    shortAnswer:
      "En företagshistoria på film kostar typiskt 60 000–250 000 kr beroende på antal intervjuer, mängden arkivmaterial och hur många klipp som ska ut ur produktionen.",
    longAnswer:
      "Ett grundarporträtt med en intervju och enklare arkivbearbetning ligger i den lägre delen. En bredare dokumentation med flera intervjuer, digitaliserat arkiv och en serie korta klipp hamnar högre.\n\nRäkna med 6–12 veckor från start till leverans. Inför ett jubileum planerar vi bakåt från visningsdatumet, eftersom det datumet sällan går att flytta.\n\nSamma inspelning används oftast till flera saker: huvudfilm till jubileet, kortare klipp till intranät, rekrytering och sociala kanaler. Det är där kostnadseffektiviteten uppstår.",
    relatedPage: "/heritage/foretagshistoria-pa-film",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_foretagshistoria",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "foretagshistoria", "pris"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_successionsplanering",
    language: "sv",
    question: "Vad är successionsplanering?",
    shortAnswer:
      "Successionsplanering är det förberedande arbetet inför att ägande, roller och ansvar lämnas över i ett bolag: ägarstruktur, tidplan, tillträdare, finansiering — och hur kunskapen hos de avträdande dokumenteras.",
    longAnswer:
      "Successionsplanering omfattar normalt fyra delar: vem som tar över, i vilken form ägandet överlåts, hur det finansieras och när tillträdet sker. Den delen hanteras av jurist, revisor och rådgivare.\n\nDen del som oftast saknas är kunskapen. Aktier och roller går att överlåta i avtal; beslutslogik, kundrelationer och erfarenhet gör det inte. Många bolag upptäcker det först ett år efter tillträdet, när frågorna dyker upp och personen som kunde svara har lämnat.\n\nLägg därför in ett kunskapsspår i planen: identifiera vilka nyckelpersoner som bär kritisk kunskap och dokumentera den 6–12 månader före tillträdet, medan båda parter finns kvar. Filmade, kapitlade intervjuer kostar typiskt 60 000–250 000 kr beroende på antal personer.",
    relatedPage: "/heritage/successionsplanering-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_kunskap_generationsskifte",
      "sv_vad_ar_intern_aktieoverlatelse",
      "sv_vad_ar_kunskapsarv",
    ],
    topics: ["heritage", "kunskapsarv", "successionsplanering"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_intern_aktieoverlatelse",
    metaDescription: "Aktierna byter ägare, men beslutslogiken följer inte med. Så kompletterar ni överlåtelsen med kunskapsöverföring innan avträdaren går.",
    language: "sv",
    question: "Vad är en intern aktieöverlåtelse?",
    shortAnswer:
      "En intern aktieöverlåtelse innebär att aktier överlåts till någon som redan finns i bolaget eller ägarkretsen — nästa generation, ledningen eller nyckelpersoner — i stället för till en extern köpare.",
    longAnswer:
      "Intern aktieöverlåtelse är en vanlig form av generationsskifte och succession. Aktierna byter ägare inom den befintliga kretsen, ofta i steg över tid och med en finansieringslösning som gör det möjligt för tillträdaren att köpa in sig. Villkor, värdering och skattefrågor hanteras av jurist och revisor.\n\nDet överlåtelsen inte hanterar är kunskapen. En tillträdare som redan arbetar i bolaget kan verksamheten på sin nivå, men sällan den avträdande ägarens beslutslogik: varför prissättningen ser ut som den gör, vilka kundrelationer som kräver särskild hantering och vilka vägval som prövats tidigare.\n\nDärför bör en intern aktieöverlåtelse kompletteras med strukturerad kunskapsöverföring — helst filmade intervjuer planerade före tillträdet och kapitlade per kunskapsområde.",
    relatedPage: "/heritage/successionsplanering-dokumentation",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_successionsplanering",
      "sv_hur_dokumentera_kunskap_generationsskifte",
    ],
    topics: ["heritage", "kunskapsarv", "successionsplanering"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_kompetensinventering",
    language: "sv",
    question: "Vad är en kompetensinventering?",
    shortAnswer:
      "En kompetensinventering är en systematisk kartläggning av vilken kompetens som finns i organisationen, var den sitter och var den är sårbar — den synliggör ofta roller där bara en person kan jobbet.",
    longAnswer:
      "En kompetensinventering utgår från rollerna, inte från individerna: vilka kunskapsområden kräver varje roll, vem behärskar dem faktiskt, och vad skulle hända om personen slutade? Resultatet används för rekrytering, kompetensutveckling och riskbedömning.\n\nDet vanligaste fyndet är enpersonsberoenden — kunskap som bara finns hos en individ och aldrig lämnat den personens huvud. Matrisen visar problemet men löser det inte.\n\nNästa steg är att prioritera efter risk (konsekvens gånger sannolikhet) och dokumentera de mest kritiska rollerna innan personen slutar, går i pension eller byter roll. Filmade intervjuer fångar bedömningen mellan stegen — undantagen och avvägningarna — som skriftliga rutiner sällan rymmer.",
    relatedPage: "/heritage/kompetensinventering",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_gor_man_kompetensinventering",
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_hur_overfora_kunskap_pension",
    ],
    topics: ["heritage", "kunskapsarv", "kompetens"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_gor_man_kompetensinventering",
    language: "sv",
    question: "Hur gör man en kompetensinventering?",
    shortAnswer:
      "Utgå från rollerna: lista vilka kunskapsområden varje roll kräver, vem som faktiskt behärskar dem och vad som händer om personen försvinner. Prioritera sedan där både konsekvens och sannolikhet är höga.",
    longAnswer:
      "Börja smalt. En inventering som ska täcka hela organisationen på en gång blir sällan färdig. Ta en avdelning eller de roller där ni redan anar en risk.\n\nFör varje roll: vilka kunskapsområden krävs, vem behärskar dem, och finns det en andra person som skulle kunna ta över? Markera de områden där svaret är nej. Väg sedan konsekvensen av ett bortfall mot sannolikheten — pensionsavgångar inom några år, roller med hög rörlighet eller specialistkompetens som är svår att rekrytera.\n\nDe prioriterade rollerna är där dokumentationen ska göras. Skriftliga rutiner täcker stegen; filmade intervjuer täcker bedömningen, undantagen och vad som brukar gå fel. Kostnaden för att dokumentera en kritisk roll på film börjar kring 60 000 kr.",
    relatedPage: "/heritage/kompetensinventering",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_vad_ar_kompetensinventering",
      "sv_hur_dokumentera_tyst_kunskap",
    ],
    topics: ["heritage", "kunskapsarv", "kompetens", "guide"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_generationsskifte_gard_skogsfastighet",
    language: "sv",
    question: "Hur gör man ett generationsskifte på gård eller skogsfastighet?",
    metaTitle: "Generationsskifte gård & skog — kunskapen kvar | Fenny",
    shortAnswer:
      "Den juridiska och skattemässiga delen hanteras av rådgivare. Vid sidan av den behöver den praktiska kunskapen om mark, skog, djur och maskiner dokumenteras — helst på plats på fastigheten medan båda generationerna finns där.",
    longAnswer:
      "Ett generationsskifte i lantbruk eller skogsbruk har två spår. Det ena är formellt: överlåtelseform, värdering, finansiering, skatt och tidplan. Det hanteras av jurist och rådgivare och är oftast väl genomarbetat.\n\nDet andra spåret är den platsbundna kunskapen, och det är där bortfallet blir störst. Vilket skifte som blir blött efter regn, var dikningen gjordes om, vilka bestånd som gallrats när, hur en viss maskin beter sig och vad som prövats genom åren — det finns sällan i skogsbruksplanen eller driftpärmen.\n\nDen kunskapen behöver visas, inte bara beskrivas. Filma intervjun ute på fastigheten, kapitla materialet per skifte eller ämne, och gör det medan den avträdande generationen finns kvar. Många familjer dokumenterar samtidigt släkthistorien bakom gården i samma inspelning.",
    relatedPage: "/heritage/generationsskifte-lantbruk-skog",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "sv_hur_dokumentera_kunskap_generationsskifte",
      "sv_vad_ar_successionsplanering",
    ],
    topics: ["heritage", "kunskapsarv", "generationsskifte", "lantbruk"],
    schemaType: "QAPage",
    metaDescription:
      "Juristen löser skatt och överlåtelse. Men kunskapen om skiften, dikning, gallring och maskiner försvinner med generationen — så dokumenterar ni den på plats i tid.",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_ar_ett_produktionsbolag",
    language: "sv",
    question: "Vad är ett produktionsbolag?",
    metaTitle: "Vad är ett produktionsbolag? Så fungerar det | Fenny",
    metaDescription: "Ett produktionsbolag gör film från idé till leverans: regi, foto, ljud, klipp och format. Så skiljer det sig från filmbolag och filmbyrå — och så väljer ni rätt.",
    shortAnswer: "Ett produktionsbolag är ett företag som producerar film från idé eller manus till färdig leverans — regi, foto, ljud, klipp, färgsättning och formatanpassning.",
    longAnswer: "Produktionsbolag, filmbolag och filmproduktionsbolag används i praktiken om samma typ av företag. Kärnan är själva produktionen: ett team som planerar inspelningen, filmar och efterbearbetar.\n\nEtt produktionsbolag kan arbeta direkt åt ett varumärke eller åt en reklambyrå som äger den kreativa idén. En filmbyrå börjar ett steg tidigare med strategi och kanalplan.\n\nNär ni väljer produktionsbolag: be om fast pris med inspelningsdagar, versioner och rättigheter specificerade, fråga vem som är producent genom hela projektet och titta på case i samma format som ni behöver.\n\nFenny är ett produktionsbolag och en filmbyrå i samma tjänst och gör bara film. Med abonnemang levereras första klippet inom 10–14 dagar.",
    relatedPage: "/filmproduktionsbolag",
    relatedQuestions: ["sv_skillnad_filmbolag_filmbyra", "sv_vad_gor_en_filmregissor"],
    topics: ["produktionsbolag", "filmbolag", "upphandling"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-10-03",
  },
  {
    id: "sv_vad_gor_en_filmregissor",
    language: "sv",
    question: "Vad gör en filmregissör?",
    metaTitle: "Vad gör en filmregissör? Ansvar i en produktion | Fenny",
    metaDescription: "Filmregissören ansvarar för filmens berättelse och uttryck — från manus och casting till inspelning och klipp. Så skiljer sig rollen från producentens.",
    shortAnswer: "En filmregissör ansvarar för filmens kreativa uttryck: hur berättelsen gestaltas, hur skådespelare eller medverkande leds och hur bild, ljud och klipp samverkar.",
    longAnswer: "Före inspelning arbetar regissören med manus, bildmanus (storyboard), casting och val av inspelningsplatser. Under inspelning leder regissören teamet på plats och de som medverkar framför kameran.\n\nEfter inspelning följer regissören klippet och färgsättningen så att filmen blir som tänkt. Producenten ansvarar för budget, tidplan och team; regissören för vad filmen blir.\n\nI reklam- och företagsfilm är det vanligt att regissören även är med när idén tas fram, så att uttrycket passar varumärket och kanalen.\n\nHos Fenny har varje produktion en namngiven producent och en regissör från eget team, från brief till leverans.",
    relatedPage: "/filmproducent",
    relatedQuestions: ["sv_vad_ar_ett_produktionsbolag", "sv_vad_ar_post_production"],
    topics: ["regi", "produktion", "roller"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-10-03",
  },
  {
    id: "sv_vad_ar_post_production",
    language: "sv",
    question: "Vad är post production?",
    metaTitle: "Vad är post production inom film? Steg för steg | Fenny",
    metaDescription: "Post production är allt som händer med filmen efter inspelning: klipp, färgsättning, ljudmix, grafik, undertexter och formatversioner. Så går det till.",
    shortAnswer: "Post production (efterproduktion) är arbetet efter inspelning: klipp, färgsättning, ljudmix, grafik, musik, undertexter och export till alla format som filmen ska publiceras i.",
    longAnswer: "Först görs ett grovklipp där berättelsen byggs upp. Efter godkännande följer finklipp, sedan färgsättning (grade) och ljudmix. Grafik och animation läggs in, och till sist exporteras filmen i de format som behövs, till exempel 16:9, 9:16 och 1:1.\n\nEfterproduktionen tar ofta lika lång tid som inspelningen eller längre. Antalet versioner och revideringsrundor påverkar tiden mest.\n\nSe till att offerten anger hur många revideringsrundor och format som ingår, så slipper ni överraskningar.\n\nFenny gör hela efterproduktionen i eget team. Med abonnemang levereras första klippet inom 10–14 dagar.",
    relatedPage: "/videoproduktion",
    relatedQuestions: ["sv_vad_gor_en_filmregissor", "sv_vad_ar_mediaproduktion"],
    topics: ["post production", "klipp", "produktion"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-10-03",
  },
  {
    id: "sv_vad_ar_mediaproduktion",
    language: "sv",
    question: "Vad är mediaproduktion?",
    metaTitle: "Vad är mediaproduktion? Film, ljud och innehåll | Fenny",
    metaDescription: "Mediaproduktion är att skapa innehåll för olika kanaler — film, ljud, bild och grafik. Så skiljer sig det från videoproduktion och när ni behöver ett produktionsbolag.",
    shortAnswer: "Mediaproduktion är ett samlingsnamn för att producera innehåll för olika kanaler: film, rörlig grafik, ljud och podd, bild och text som publiceras digitalt eller i tv.",
    longAnswer: "Videoproduktion är den del av mediaproduktion som handlar om rörlig bild. För företag är film oftast den största delen, eftersom samma inspelning kan bli reklamfilm, korta klipp för sociala medier och material till webben.\n\nEn mediaproduktion följer samma steg oavsett format: planering, produktion (inspelning) och efterproduktion.\n\nFör löpande behov lönar det sig att planera innehållet i serie, så att varje inspelningsdag ger material till flera kanaler.\n\nFenny är en specialiserad filmbyrå och gör videoproduktion för företag i hela Sverige.",
    relatedPage: "/videoproduktion",
    relatedQuestions: ["sv_vad_ar_post_production", "sv_vad_ar_ett_produktionsbolag"],
    topics: ["mediaproduktion", "videoproduktion"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-10-03",
  },
  {
    id: "sv_skillnad_filmbolag_filmbyra",
    language: "sv",
    question: "Vad är skillnaden mellan ett filmbolag och en filmbyrå?",
    metaTitle: "Skillnaden mellan filmbolag och filmbyrå | Fenny",
    shortAnswer:
      "Ett filmbolag producerar film per projekt \u2014 regi, foto, ljud, klipp och leverans. En filmbyr\u00e5 tar dessutom ansvar f\u00f6r strategi, distribution och l\u00f6pande inneh\u00e5llsfl\u00f6de \u00f6ver tid.",
    longAnswer:
      "Filmbolag och produktionsbolag anv\u00e4nds i praktiken synonymt: verksamheten \u00e4r byggd kring sj\u00e4lva produktionen. Ni kommer med en brief, de levererar en f\u00e4rdig film enligt spec.\n\nEn filmbyr\u00e5 b\u00f6rjar ett steg tidigare och slutar ett steg senare. Fr\u00e5gan \u00e4r inte bara vilken film som ska g\u00f6ras utan vilken aff\u00e4rsfr\u00e5ga den ska l\u00f6sa, var den ska publiceras och hur utfallet m\u00e4ts.\n\nPrism\u00e4ssigt skiljer modellerna sig: projektpris 25 000\u2013150 000 kr per film mot abonnemang 14 995\u201339 995 kr per m\u00e5nad utan bindningstid. Vid fler \u00e4n tre filmer per \u00e5r blir abonnemang normalt billigare per levererad film.\n\nEnkel tumregel: en till tv\u00e5 filmer per \u00e5r med f\u00e4rdig idé \u2014 anlita ett filmbolag. \u00c5terkommande behov d\u00e4r n\u00e5gon beh\u00f6ver \u00e4ga helheten \u2014 v\u00e4lj byr\u00e5 med abonnemang.",
    relatedPage: "/filmbolag",
    relatedComparison: "/jamfor/filmbolag-vs-filmbyra",
    relatedQuestions: ["sv_vad_ar_film_pa_abonnemang"],
    topics: ["filmbolag", "filmbyra", "upphandling"],
    schemaType: "QAPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_kostar_green_screen",
    language: "sv",
    question: "Vad kostar en green screen-inspelning?",
    shortAnswer:
      "En halvdag i studio med green screen ligger p\u00e5 25 000\u201345 000 kr inklusive team, ljuss\u00e4ttning och grundklipp. En heldag med flera talare och grafikpaket landar p\u00e5 45 000\u201390 000 kr.",
    longAnswer:
      "Priset styrs av tre saker: hur m\u00e5nga personer som ska spelas in, hur mycket grafik som ska byggas i efterbehandlingen och om inspelningen sker i studio eller p\u00e5 plats hos er.\n\nStudio ing\u00e5r oftast i halvdagspriset. Portabel green screen hos er kr\u00e4ver cirka 4 x 4 meter fri yta och 2,5 meters takh\u00f6jd, plus tid f\u00f6r rigg \u2014 r\u00e4kna en extra timme i vardera \u00e4nden.\n\nGreen screen l\u00f6nar sig n\u00e4r samma inspelning ska \u00e5teranv\u00e4ndas i flera versioner, spr\u00e5k eller kampanjbakgrunder. Ska filmen bara finnas i en version \u00e4r inspelning p\u00e5 plats ofta b\u00e5de billigare och mer trov\u00e4rdig.\n\nL\u00f6pande behov t\u00e4cks av abonnemang fr\u00e5n 14 995 kr per m\u00e5nad, d\u00e4r inspelningsdagar ing\u00e5r i planeringen i st\u00e4llet f\u00f6r att offereras styckvis.",
    relatedPage: "/green-screen",
    relatedComparison: "/jamfor/green-screen-vs-location",
    relatedQuestions: ["sv_skillnad_filmbolag_filmbyra"],
    topics: ["green screen", "produktion", "pris"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_kostar_voice_over",
    language: "sv",
    question: "Vad kostar en voice-over p\u00e5 svenska?",
    shortAnswer:
      "En professionell svensk speaker kostar typiskt 3 500\u20139 000 kr f\u00f6r en film p\u00e5 upp till tv\u00e5 minuter, inklusive studio och regi. AI-r\u00f6st kostar n\u00e5gra hundralappar i licens men kr\u00e4ver uttalskorrektur.",
    longAnswer:
      "Priset beror p\u00e5 manusl\u00e4ngd, anv\u00e4ndningsomr\u00e5de och hur k\u00e4nd r\u00f6sten \u00e4r. Internt material och webbfilm ligger i det l\u00e4gre spannet, bred mediaanv\u00e4ndning i TV och betald annonsering i det h\u00f6gre.\n\nR\u00e4kna 130\u2013150 ord per minut i normalt svenskt tempo. En 60-sekundersfilm rymmer allts\u00e5 cirka 140 ord \u2014 f\u00e4rre om bilden ska f\u00e5 utrymme att andas.\n\nAI-r\u00f6st \u00e4r ett rimligt val f\u00f6r interna utbildningar, snabb spr\u00e5kversionering och material som uppdateras ofta. F\u00f6r varum\u00e4rkesb\u00e4rande film rekommenderar vi mänsklig r\u00f6st: betoning, andning och mikropauser \u00e4r fortfarande sk\u00e4let till att en mening landar.\n\nKontrollera alltid licensen \u2014 medieslag, geografi och tidsperiod \u2014 innan filmen publiceras, oavsett om r\u00f6sten \u00e4r m\u00e4nsklig eller syntetisk.",
    relatedPage: "/voice-over",
    relatedComparison: "/jamfor/voice-over-vs-ai-rost",
    relatedQuestions: ["sv_vad_kostar_green_screen"],
    topics: ["voice-over", "speaker", "pris"],
    schemaType: "QAPage",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_hur_stor_ar_en_filmbudget",
    language: "sv",
    question: "Hur stor filmbudget har svenska företag 2026?",
    shortAnswer:
      "Medianbudgeten för rörlig bild är 120 000 kr per år hos bolag med 10–49 anställda, 320 000 kr vid 50–199 anställda och 1 450 000 kr hos bolag med 500–1 000 anställda (Filmproduktionsbarometern, Q3 2026).",
    longAnswer:
      "Filmproduktionsbarometern bygger på 132 enkätsvar från marknads- och kommunikationsansvariga samt aggregerade data från 214 produktioner. Medianbudgeten för rörlig bild är 120 000 kr per år vid 10–49 anställda, 320 000 kr vid 50–199 anställda, 680 000 kr vid 200–499 anställda och 1 450 000 kr vid 500–1 000 anställda.\n\nBudgeten avser produktionskostnad exklusive mediaköp och interna personalkostnader. 41 procent av bolagen har numera en löpande filmbudget i stället för enstaka projekt, vilket är en ökning med 6 procentenheter mot föregående kvartal.\n\nHela datasetet är öppet under CC BY 4.0 och kan laddas ner som CSV.",
    relatedPage: "/branschstudier/filmproduktionsbarometern",
    relatedQuestions: ["sv_vad_kostar_foretagsfilm"],
    topics: ["pris", "budget", "branschdata"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_anvander_filmbranschen_ai",
    language: "sv",
    question: "Hur mycket AI används i svensk filmproduktion 2026?",
    shortAnswer:
      "47 procent av svenska bolag använder AI i minst ett produktionssteg 2026 — främst manusutkast, transkribering och textning. Det är en ökning med 11 procentenheter på ett kvartal.",
    longAnswer:
      "Enligt Filmproduktionsbarometern (Q3 2026) använder 47 procent av bolagen AI någonstans i produktionskedjan. Vanligast är manus- och konceptutkast, automatisk transkribering, textning och versionering till fler format.\n\nDäremot är AI ovanligt i själva bildproduktionen för varumärkesfilm: förtroendekritiskt material som kundcase, employer branding och heritage spelas fortfarande in med riktiga människor och riktiga miljöer.\n\nFenny använder AI för research, transkribering och textning — aldrig för att fabricera medverkande, citat eller resultat.",
    relatedPage: "/branschstudier/filmproduktionsbarometern",
    relatedQuestions: ["sv_hur_stor_ar_en_filmbudget"],
    topics: ["ai", "produktion", "branschdata"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_far_jag_citera_fennys_prisindex",
    language: "sv",
    question: "Får jag citera Fennys prisindex och branschdata?",
    metaTitle: "Citera vårt prisindex — regler och källhänvisning",
    shortAnswer:
      "Ja. Prisindex 2026, Formatbenchmark 2026 och Filmproduktionsbarometern är öppna under CC BY 4.0 — du får publicera och bearbeta siffrorna, även kommersiellt, om du anger Fenny som källa med en klickbar länk.",
    longAnswer:
      "Samtliga tre dataset publiceras som öppen data under Creative Commons Erkännande 4.0. Du får citera, visualisera och bearbeta siffrorna i redaktionellt och kommersiellt material.\n\nRekommenderad källhänvisning: \"Fenny (2026). Prisindex filmproduktion Sverige 2026. https://fenny.se/branschstudier/prisindex-2026\".\n\nRådata finns som CSV på /api/public/prisindex-2026.csv, /api/public/formatbenchmark-2026.csv och /api/public/filmproduktionsbarometern.csv. Prisindexet finns även som inbäddningsbar tabell för artiklar.",
    relatedPage: "/branschstudier/prisindex-2026",
    relatedQuestions: ["sv_hur_stor_ar_en_filmbudget"],
    topics: ["oppen-data", "press", "branschdata"],
    schemaType: "FAQPage",
    metaDescription:
      "Prisindex 2026, Formatbenchmark 2026 och Filmproduktionsbarometern är öppna under CC BY 4.0. Ladda ner rådata som CSV och citera med källhänvisning.",
    priority: "medium",
    lastReviewed: "2026-08-17",
  },
  {
    id: "sv_vad_kostar_ett_headshot",
    language: "sv",
    question: "Vad kostar ett professionellt headshot?",
    shortAnswer:
      "Headshots prissätts per fotograferingspass, inte per bild. En halvdag räcker till 10–20 personer och en heldag till 20–40, vilket gör att kostnaden per medarbetare sjunker kraftigt med antalet.",
    longAnswer:
      "Ett headshot tar 5–10 minuter per person när fotograferingen sker på plats med mobil studio. Eftersom riggning, resa och ljussättning kostar detsamma oavsett hur många som ska fotograferas, blir passpriset den relevanta enheten: en halvdag räcker normalt till 10–20 personer, en heldag till 20–40.\n\nDet som driver priset uppåt är miljöporträtt utöver headshot, flera platser samma vecka, tyngre retusch som frilägg samt användning i köpt media.\n\nFenny lämnar fast pris per uppdrag innan dagen bokas, levererar retuscherade bilder inom 3–5 arbetsdagar och dokumenterar ljusuppsättningen så att nyanställda kan fotograferas in i samma serie senare. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/headshots",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_foretagsportratt", "sv_vad_kostar_en_foretagsfotograf"],
    topics: ["foto", "headshots", "pris"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_vad_kostar_foretagsportratt",
    language: "sv",
    question: "Vad kostar en porträttdag för företaget?",
    shortAnswer:
      "En porträttdag prissätts per pass. Halvdag täcker 10–20 personer, heldag 20–40. Kostnaden per medarbetare sjunker därmed med antalet, och kompletteringspass för nyanställda har lägre passpris.",
    longAnswer:
      "Porträtt prissätts effektivast per fotograferingsdag, eftersom riggning och resa kostar lika mycket oavsett om tio eller trettio personer ska igenom. En halvdag räcker till 10–20 personer med ett headshot vardera; en heldag till 20–40 inklusive urval på plats.\n\nSka varje person också ha ett miljöporträtt halveras antalet per dag. Flera kontor kräver flera pass även om uppsättningen är densamma.\n\nI priset ingår fotografering, mobil studio, digitalt urvalsgalleri, varsam retusch av valda bilder, leverans i webb- och tryckformat samt fri användningsrätt i egna kanaler. Kompletteringspass för nyanställda har lägre passpris eftersom uppsättningen redan är dokumenterad. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/foretagsportratt-pris",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_ett_headshot", "sv_foretagsportratt_upplagg"],
    topics: ["foto", "företagsporträtt", "pris"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_vad_kostar_produktfotografering",
    language: "sv",
    question: "Vad kostar produktfotografering per artikel?",
    shortAnswer:
      "Produktfoto prissätts per artikel och vinkel. Packshots i serie mot vit botten har lägst styckpris eftersom uppsättningen byggs en gång, medan stylade miljöbilder byggs per bild och kostar mer.",
    longAnswer:
      "Kostnaden styrs av antal artiklar, antal vinklar per artikel och efterarbetets omfattning. Ett sortiment på hundra artiklar med en vinkel går snabbare än tjugo artiklar med fem vinklar.\n\nMaterialet spelar roll: blanka, genomskinliga och speglande produkter som glas och metall kräver mer ljussättning och mer retusch än matta ytor. Frilägg mot vit eller transparent bakgrund debiteras per bild, liksom skuggkonstruktion och rengöring av repor och damm.\n\nStyckpriset sänks genom att samla sortimentet till ett tillfälle, begränsa antalet vinklar till det e-handeln faktiskt visar och leverera produkterna rena och kompletta. Fenny offererar fast pris per uppdrag och levererar inom 3–5 arbetsdagar. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/produktfoto-pris",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_en_foretagsfotograf", "sv_retuschnivaer"],
    topics: ["foto", "produktfoto", "pris"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_fotoagentur_eller_frilans",
    language: "sv",
    question: "Ska vi anlita frilansfotograf eller fotoagentur?",
    shortAnswer:
      "Frilansfotograf passar enstaka, avgränsade uppdrag. Fotoagentur passar när flera bildtyper ska hänga ihop över tid, eftersom manér, planering, bildbank och rättigheter då ingår.",
    longAnswer:
      "En frilansfotograf har lägst tröskel och ofta lägst pris för ett enskilt uppdrag. Risken ligger i kontinuiteten: manéret finns i en persons arbetsflöde, och bildbank, rättighetsdokumentation och planering ingår sällan.\n\nEn fotoagentur ansvarar för hela bildflödet — manér, planering, bemanning, efterarbete, bildbank och rättigheter — och kan skala bemanningen efter uppdrag. Det kostar mer per tillfälle än den billigaste frilansaren, men minskar antalet omfotograferingar som beror på att bilder inte hänger ihop eller inte hittas.\n\nEtt löpande upplägg passar bolag som publicerar kontinuerligt snarare än kampanjvis. Hos Fenny är löpande upplägg månadsvis utan bindningstid. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/jamfor",
    relatedService: "Foto",
    relatedQuestions: ["sv_bygga_bildbank", "sv_vad_kostar_en_foretagsfotograf"],
    topics: ["foto", "fotoagentur", "upphandling"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_bygga_bildbank",
    language: "sv",
    question: "Hur bygger man en bildbank för ett företag?",
    shortAnswer:
      "Inventera och gallra befintligt material, strukturera efter användning i stället för fotodatum, namnge filerna enhetligt med datum, motiv och rättighetsstatus, och planera påfyllning två till fyra gånger per år.",
    longAnswer:
      "Gallringen är det viktigaste steget. En bildbank med 4 000 filer där 300 är godkända används inte; en med 300 godkända gör det. Bilder med utgången nyttjanderätt eller personer som slutat plockas ur aktivt bruk.\n\nStrukturen ska följa användningen — sajt, sociala kanaler, sälj, rekrytering, press — inte fotograferingstillfället. Filnamnet bär datum, motiv, ort och rättighetsstatus, och färdiga format per kanal levereras direkt så att ingen behöver beskära i efterhand.\n\nEn bildplan med två till fyra tillfällen per år täcker normalt behovet: en porträttdag, ett kampanjpass och ett till två event. En vanlig utgångspunkt är 150–400 godkända bilder. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/bildbank",
    relatedService: "Foto",
    relatedQuestions: ["sv_fotoagentur_eller_frilans", "sv_retuschnivaer"],
    topics: ["foto", "bildbank", "process"],
    schemaType: "HowTo",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_retuschnivaer",
    language: "sv",
    question: "Vilka retuschnivåer finns och vad ingår i priset?",
    shortAnswer:
      "Fyra nivåer: färgsättning som alltid ingår, varsam porträttretusch som ingår i porträttuppdrag, frilägg som debiteras per bild, samt komposition och tung produktretusch som offereras per bild eller timme.",
    longAnswer:
      "Nivå 1 är färgsättning: vitbalans, exponering, kontrast och enhetlighet över serien. Nivå 2 är varsam porträttretusch där tillfälligheter, glans och veck jämnas ut utan att ansiktsdrag ändras. Nivå 3 är frilägg mot vit eller transparent bakgrund för e-handel och layout, som tar 5–15 minuter per bild. Nivå 4 är komposition: sammanfogning av exponeringar, byte av bakgrund och borttagning av större objekt, som kan ta timmar per bild.\n\nFenny ändrar inte kroppsform eller ansiktsdrag på medarbetare och retuscherar inte bort sådant som gör en produktbild osann, eftersom det genererar reklamationer.\n\nEn justeringsrunda på levererat urval ingår. Leverans sker i högupplöst JPEG, plus TIFF eller PNG med transparens vid frilägg. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/retusch",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_produktfotografering", "sv_bygga_bildbank"],
    topics: ["foto", "retusch", "efterarbete"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_dronarfoto_regler",
    language: "sv",
    question: "Vad gäller för drönarfoto i Sverige?",
    shortAnswer:
      "Operatören ska vara registrerad hos Transportstyrelsen och piloten ha rätt behörighet för flygningens kategori. Nära flygplatser, över folksamlingar och inom skyddsområden krävs särskilt tillstånd.",
    longAnswer:
      "Innan datum spikas kontrolleras luftrum, närhet till flygplats, skyddsobjekt och eventuella restriktioner. Är platsen restriktionsbelagd söks tillstånd i god tid. Markägarens medgivande behövs för start och landning.\n\nVäder är den vanligaste orsaken till ombokning — hård vind eller regn flyttar passet, varför ett reservdatum bör läggas in redan i planeringen.\n\nEtt flygpass tar normalt en till tre timmar på plats och kan kombineras med markfotografering samma dag, vilket är den mest kostnadseffektiva ordningen. Vanliga användningsområden är anläggningsöversikter, byggprojekt i etapper och fastighetsmaterial. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/dronarfoto",
    relatedService: "Foto",
    relatedQuestions: ["sv_foto_och_film_samma_dag", "sv_vad_kostar_en_foretagsfotograf"],
    topics: ["foto", "drönare", "regler"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_vad_gor_en_portrattfotograf",
    language: "sv",
    question: "Vad gör en porträttfotograf och vad kostar det?",
    shortAnswer:
      "En porträttfotograf ansvarar för ljus, bakgrund och beskärning så att personen blir igenkännbar i de format bilden ska användas i. Priset sätts per pass: en halvdag räcker till 10–20 personer, en heldag till 20–40.",
    longAnswer:
      "Ett porträtt tar 5–10 minuter per person för ett tight headshot och 10–15 minuter när ett miljöporträtt ingår. Ett enskilt porträttpass i studio tar 45–90 minuter.\n\nEftersom riggning, resa och ljussättning kostar detsamma oavsett antal blir passpriset den relevanta enheten. Kostnaden per person sjunker därför snabbt med antalet medarbetare som fotograferas samma dag.\n\nFenny fotograferar i studio i Stockholm eller med mobil studio hos er i hela Sverige, levererar retuscherade bilder inom 3–5 arbetsdagar och dokumenterar ljusuppsättningen så att nyanställda kan matchas in senare. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/portrattfotograf",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_ett_headshot", "sv_vad_kostar_foretagsportratt"],
    topics: ["foto", "porträtt", "pris"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_fotostudio_stockholm",
    language: "sv",
    question: "Vad ingår i en fotostudio i Stockholm?",
    shortAnswer:
      "Hos Fenny ingår fotograf, ljussättning, bakgrunder och bildhantering i studiouppdraget — studion hyrs inte ut tom. En halvdag räcker till 10–20 porträtt eller 15–30 packshots.",
    longAnswer:
      "En studiofotografering hos Fenny innehåller fotograf, ljussättning, bakgrunder i vitt, grått eller kulör, tethering till skärm så att bilderna kan godkännas på plats, samt urval och retusch efteråt.\n\nStudio passar bäst för porträtt som ska ha samma manér, produkter och packshots för e-handel samt kampanjbilder där ljuset måste vara exakt. Miljöbilder, reportage och event fotograferas i stället på plats.\n\nBehöver hela organisationen fotograferas är mobil studio i era lokaler ofta effektivare — uppsättningen riggas på cirka 45 minuter och bilderna hänger ihop med studiomaterialet. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/fotostudio-stockholm",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_gor_en_portrattfotograf", "sv_vad_kostar_produktfotografering"],
    topics: ["foto", "studio", "stockholm"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_personalfoto_upplagg",
    language: "sv",
    question: "Hur går personalfotografering till?",
    shortAnswer:
      "Fotografen bygger mobil studio i ett konferensrum på cirka 45 minuter och fotograferar enligt tidsschema med 5–10 minuter per person. En dag räcker normalt till 20–40 medarbetare.",
    longAnswer:
      "Inför dagen sätts ett tidsschema, oftast i femminutersluckor per avdelning, och en kort klädguide skickas ut. Det som behövs på plats är ett rum på ungefär tre gånger tre meter och ett eluttag.\n\nUnder dagen får varje person se ett par bilder direkt i skärmen, vilket gör att även de som ogärna blir fotograferade slappnar av. Både tight headshot och friare miljöporträtt ryms i samma lucka med cirka fem minuters påslag per person.\n\nEfter dagen väljer ni bilder i ett digitalt galleri och urvalet retuscheras varsamt. Leverans sker inom 3–5 arbetsdagar i kvadratiskt format för intranät och LinkedIn, stående för sajten och högupplöst för tryck. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/personalfoto",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_ett_headshot", "sv_vad_gor_en_portrattfotograf"],
    topics: ["foto", "personalfoto", "process"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_vad_gor_en_reklamfotograf",
    language: "sv",
    question: "Vad gör en reklamfotograf?",
    shortAnswer:
      "En reklamfotograf ansvarar för koncept, ljus, casting, styling och beskärningar i de format kampanjen ska köras i — så att samma bild fungerar i liggande annons, stående story och kvadrat.",
    longAnswer:
      "Arbetet börjar före kameran: vad kampanjen ska få mottagaren att göra, vilka ytor bilden ska köras i och var text ska ligga. Moodboard och shotlist godkänns innan produktionsdagen.\n\nUnder produktionen fotograferas med kampanjens beskärningar inritade och med luft där text ska placeras, och alternativa beskärningar tas av varje nyckelbild.\n\nRetusch sker i två steg — grovretusch på urvalet, finretusch på de bilder som går i produktion — och leverans sker i alla efterfrågade format. Användningsrätt anges i offerten med kanal, medietyp, geografi och tid. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/reklamfotograf",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_kostar_en_foretagsfotograf"],
    topics: ["foto", "reklam", "kampanj"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_brollopsfotograf_foto_och_film",
    language: "sv",
    question: "Kan man boka bröllopsfotograf och bröllopsfilm samtidigt?",
    metaTitle: "Bröllopsfotograf och bröllopsfilm i ett paket | Fenny",
    shortAnswer:
      "Ja. Fenny fotograferar och filmar bröllop med samma team på samma dag, vilket blir billigare än två separata bokningar eftersom planering, recce och resa görs en gång.",
    longAnswer:
      "När foto och film bokas separat konkurrerar teamen om samma ögonblick och samma positioner. Bokas de ihop planeras dagen en gång och rollerna fördelas i förväg, så att stillbilder och rörligt kompletterar varandra.\n\nUpplägget är dokumentärt: förberedelser, vigsel, mingel och fest, med ett kort porträttpass för brudparet när ljuset är som bäst och ett fåtal planerade gruppbilder.\n\nBilderna levereras i ett digitalt galleri i format för skärm och tryck, och filmen levereras separat enligt överenskommet upplägg. Priset offereras per bröllop utifrån timmar, antal fotografer och resa. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/brollopsfotograf",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_gor_en_portrattfotograf"],
    topics: ["foto", "bröllop", "film"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: "2026-08-31",
  },
  {
    id: "sv_vem_ager_bilderna",
    language: "sv",
    question: "Vem äger bilderna efter en fotografering?",
    shortAnswer:
      "Upphovsrätten ligger kvar hos fotografen, medan uppdragsgivaren får en avtalad användningsrätt. Hos Fenny ingår fri användning i egna kanaler; köpt media och stora tryckupplagor specificeras separat.",
    longAnswer:
      "Enligt svensk upphovsrätt uppstår rätten hos fotografen och överlåts inte automatiskt vid beställning. Det som regleras i offerten är därför nyttjanderätten: vilka kanaler, vilken medietyp, vilken geografi och hur länge.\n\nHos Fenny ingår fri användning i kundens egna kanaler — sajt, sociala medier, intranät, presentationer och pressrum — utan tidsbegränsning. Användning i köpt media, utomhusreklam eller stora tryckupplagor prissätts separat eftersom exponeringen är en annan.\n\nRåfiler ingår normalt inte i leveransen men kan avtalas. Medverkar personer krävs dessutom medgivande, och nyttjandeperioden för modeller redovisas i leveransen. Kontakt: Fenny, hey@fenny.se.",
    relatedPage: "/foto/vad-kostar-en-fotograf",
    relatedService: "Foto",
    relatedQuestions: ["sv_vad_gor_en_reklamfotograf", "sv_vad_kostar_en_foretagsfotograf"],
    topics: ["foto", "rättigheter", "avtal"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: "2026-08-31",
  },
];




// ---------------------------------------------------------------------------
// English answers (priority A subset)
// ---------------------------------------------------------------------------

const EN_ANSWERS: FennyAnswer[] = [
  {
    id: "en_what_is_fenny",
    language: "en",
    question: "What is Fenny?",
    metaTitle: "What is Fenny? Film production on subscription",
    shortAnswer:
      "Fenny is a Swedish video production company that delivers premium video either as a monthly subscription (Pulse) or as bespoke project productions (Prime).",
    longAnswer:
      "Fenny helps marketing teams keep a steady production rhythm without agency friction. Strategy, direction, cinematography, editing, grading, sound and format adaptation all happen within the same service, with the same team over time.\n\nWith Pulse you get a video subscription at a fixed monthly price and the first cut within 14 days. With Prime we deliver standalone high-craft productions — commercials, documentary, employer branding films, brand films.\n\nFenny is part of the Norrhavet group, with offices in Stockholm, Västerås and Malmö, and produces across Sweden and the Nordics.",
    relatedPage: "/en",
    relatedQuestions: ["en_what_is_video_subscription", "en_how_pulse_works"],
    topics: ["fenny", "about"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_what_is_video_subscription",
    language: "en",
    question: "What is a video subscription?",
    shortAnswer:
      "A video subscription means a company gets ongoing production of video content at a fixed monthly price, rather than buying each film as a separate project.",
    longAnswer:
      "Instead of negotiating a quote per film, you decide once on volume, ambition and channels, and the production team works in a recurring rhythm.\n\nWith Fenny Pulse, strategy, scripting, filming, editing, grading, sound and format adaptation are all included in one monthly fee. You get a dedicated team that learns your brand and a steady delivery cadence planned quarterly.\n\nIt fits marketing teams that need continuous content for web, social, sales and advertising — and that are tired of waiting weeks for a first cut.",
    relatedPage: "/en/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: ["en_how_pulse_works", "en_corporate_video_cost"],
    topics: ["pulse", "subscription"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_pulse_works",
    language: "en",
    question: "How does Fenny Pulse work?",
    metaTitle: "How does Fenny Pulse work? The model explained",
    shortAnswer:
      "Pulse is a video subscription. Fenny produces and delivers video on a continuous basis at a fixed monthly fee, with the first cut delivered within 14 days from the strategy meeting.",
    longAnswer:
      "We start with a short strategy workshop where we set goals, audience, channels and a content plan for the first quarter. Then we film in blocks — usually one or two days per month — and deliver finished films in the formats you need: 16:9 for web and YouTube, 9:16 for Reels and TikTok, 1:1 for LinkedIn.\n\nThe contract runs quarterly. Pulse Lite starts at 14,995 SEK/month, Pulse Core at 34,995 SEK/month and Pulse Pro at 45,995 SEK/month. The exact tier is set by monthly volume and formats.\n\nYou own all final material.",
    relatedPage: "/en/pulse",
    relatedService: "Fenny Pulse",
    relatedQuestions: ["en_what_is_video_subscription", "en_pulse_vs_prime"],
    topics: ["pulse", "process"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_prime_works",
    language: "en",
    question: "How does Fenny Prime work?",
    metaTitle: "How does Fenny Prime work? The model explained",
    shortAnswer:
      "Prime is Fenny's bespoke project production for larger campaigns, commercials, documentary and brand films — fixed price per brief.",
    longAnswer:
      "Prime is used when you need a higher production value than a monthly delivery can carry: a hero campaign, a flagship film, a recruitment concept meant to last a year.\n\nWe start from a creative brief and quote a fixed price per production. The team includes director, cinematographer, producer, editor, colorist, sound and any specialists the project requires.\n\nMany clients combine Prime with a Pulse subscription — Prime carries the big moments, Pulse keeps the rhythm in between.",
    relatedPage: "/en/prime",
    relatedService: "Fenny Prime",
    relatedQuestions: ["en_pulse_vs_prime", "en_commercial_cost"],
    topics: ["prime", "project"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_pulse_vs_prime",
    language: "en",
    question: "What is the difference between Pulse and Prime?",
    shortAnswer:
      "Pulse is an ongoing video subscription at a fixed monthly price. Prime is bespoke project production for single, larger campaigns.",
    longAnswer:
      "Pulse is built for marketing teams that need continuous content across multiple channels every month. You get a dedicated team, a quarterly content plan and a predictable cost — no per-film negotiation.\n\nPrime is built for single, larger productions: commercial, brand film, campaign, documentary. Larger crew, more pre-production, higher production value, fixed price per project.\n\nMany clients run both in parallel. Prime delivers the flagship film; Pulse repurposes, versions and maintains the flow around it.",
    relatedPage: "/en/pulse-vs-prime",
    relatedQuestions: ["en_how_pulse_works", "en_how_prime_works"],
    topics: ["pulse", "prime", "compare"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_corporate_video_cost",
    language: "en",
    question: "How much does a corporate video cost?",
    shortAnswer:
      "A standalone corporate video typically costs SEK 40,000–250,000. With Fenny Pulse you get ongoing corporate video from SEK 14,995/month.",
    longAnswer:
      "Price is driven by the number of shoot days, the depth of pre-production, casting and voiceover needs, and the complexity of editing and versioning.\n\nA single-day corporate video in one channel format typically lands in the SEK 40,000–80,000 range. A more produced film with multiple days, set design and several versions typically lands in the SEK 120,000–250,000 range.\n\nWith Fenny Pulse you swap a per-project quote for a fixed monthly fee. Pulse Lite from SEK 14,995/month covers continuous corporate and social video in a recurring rhythm.",
    relatedPage: "/en/what-does-a-corporate-video-cost",
    relatedService: "Fenny Pulse",
    relatedQuestions: ["en_commercial_cost", "en_what_is_video_subscription"],
    topics: ["price", "corporate-video"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_commercial_cost",
    language: "en",
    question: "How much does a commercial cost?",
    shortAnswer:
      "A commercial typically costs SEK 150,000–800,000 depending on concept, crew, casting and shoot days. Larger campaigns can run significantly higher.",
    longAnswer:
      "Commercial production has the widest price range of any video category. A simple digital commercial with a single shoot day can land around SEK 150,000. A TV campaign with a director, casting, set design, multi-day shoot and full post can reach SEK 600,000–1,500,000 or more.\n\nPrice is not driven by on-screen minutes but by ambition — how big the concept is, how many people on set, how much craft in editing, grading, sound and VFX.\n\nFenny Prime quotes a fixed price per production after brief.",
    relatedPage: "/en/what-does-a-commercial-cost",
    relatedService: "Fenny Prime",
    relatedQuestions: ["en_corporate_video_cost", "en_how_prime_works"],
    topics: ["price", "commercial"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_fenny_vs_agency",
    language: "en",
    question: "How is Fenny different from a traditional video agency?",
    shortAnswer:
      "Fenny replaces per-film quoting with a fixed monthly price, a dedicated team and continuous delivery. A traditional agency quotes project by project.",
    longAnswer:
      "Traditional video agencies operate project by project: brief, quote, production, invoice. That works for one-off films but creates friction when you need a steady stream of content.\n\nFenny Pulse is built for continuous video: dedicated team, quarterly plan, predictable monthly cost. When you also need a flagship production, Prime sits on top — same team, higher production value, fixed price per project.\n\nThe result is both continuity and hero campaigns within the same relationship.",
    relatedPage: "/en/video-agency",
    relatedQuestions: ["en_what_is_fenny", "en_pulse_vs_prime"],
    topics: ["compare", "positioning"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_employer_branding_cost",
    metaDescription: "SEK 40,000–250,000 per film depending on shoot days, locations and post. Here is what moves the price — and when a monthly cadence beats quoting film by film.",
    language: "en",
    question: "How much does an employer branding video cost?",
    shortAnswer:
      "A standalone employer branding video typically costs SEK 40,000–250,000 depending on scope. Run continuously via Pulse, the cost levels out to a fixed monthly fee.",
    longAnswer:
      "Employer branding video is priced like any other film: number of shoot days, number of people and locations, scripting, and how produced the post is. A simple series of employee portraits sits at the lower end, while a polished recruitment film with multiple locations and casting lands higher.\n\nFor companies that recruit continuously, a per-film price is often misleading — the real need is a steady stream of culture and recruitment content. A Pulse subscription fits better: you get a planned cadence of employer branding video at a predictable monthly cost instead of quoting each film separately.\n\nFor a flagship film on the careers page, this is often combined with a single Prime production.",
    relatedPage: "/en/employer-branding-video",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "en_corporate_video_cost",
      "en_what_is_video_subscription",
      "en_pulse_vs_prime",
    ],
    topics: ["employer-branding", "price", "recruitment"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_video_production_time",
    language: "en",
    question: "How long does a video production take?",
    shortAnswer:
      "A simple corporate video typically takes 2–4 weeks from brief to delivery, while a commercial with a full crew takes 4–8 weeks. Short social clips in an ongoing flow can ship in days.",
    longAnswer:
      "Timeline is driven by three phases: pre-production (brief, script, planning, casting), the shoot itself, and post-production (editing, color, sound, graphics). The more there is to plan and finish, the longer it takes.\n\nA straightforward corporate or product video without heavy casting normally lands at 2–4 weeks. A commercial with script, actors, multiple locations and advanced post takes 4–8 weeks. Short social clips inside an existing Pulse flow are much faster because the team already knows the brand and often films several deliverables in a single day.\n\nThe single most common thing that stretches a timeline is internal approval loops — which is why we set clear checkpoints early so production keeps its pace.",
    relatedPage: "/en/process",
    relatedQuestions: [
      "en_corporate_video_cost",
      "en_how_to_order_video",
      "en_how_pulse_works",
    ],
    topics: ["process", "delivery", "timeline"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_to_order_video",
    language: "en",
    question: "How do you order a video step by step?",
    shortAnswer:
      "Order a video in five steps: define the goal and audience, set budget and format, choose a production partner, approve the script and plan, and follow up on results after publishing.",
    longAnswer:
      "Ordering a video doesn't have to be complicated. The most important thing is to start with the goal, not the format — that makes every later decision easier.\n\n1. Define the goal and audience: what should the video achieve (sales, recruitment, brand) and who is it for? 2. Set budget and format: decide roughly what you want to invest and where the video will run, so length and production level match the channel. 3. Choose a production partner: a single agency for one project, or a subscription like Pulse if the need is ongoing. 4. Approve the script and production plan: align on idea, script and shoot plan before the camera rolls. 5. Follow up: measure how the video performs and let the results guide the next production.\n\nAt Fenny the process starts with a short strategy call where we translate the goal into a concrete plan — either as a single Prime production or an ongoing Pulse flow.",
    relatedPage: "/en/process",
    relatedQuestions: [
      "en_video_production_time",
      "en_corporate_video_cost",
      "en_how_pulse_works",
    ],
    topics: ["process", "guide", "ordering"],
    schemaType: "HowTo",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_social_media_video_formats",
    language: "en",
    question: "Which video formats work best on social media?",
    shortAnswer:
      "Short, vertical videos (9:16) of 6–30 seconds work best on Instagram, TikTok and LinkedIn. The message should land in the first seconds and work without sound.",
    longAnswer:
      "Social media rewards video made for the feed, not repurposed TV ads. That means vertical or square format, short durations and a clear message right at the open before the viewer scrolls past.\n\nCommon winning formats are: hooks and bumpers of 6–15 seconds, product and demo clips of 15–30 seconds, and customer voices or behind-the-scenes that build trust. Captions are essentially mandatory since a large share watches without sound.\n\nBecause social channels demand volume and high frequency, a Pulse subscription fits especially well — you get a steady flow of clips tailored per channel instead of one-off expensive productions that quickly go stale.",
    relatedPage: "/en/pulse",
    relatedQuestions: [
      "en_how_pulse_works",
      "en_what_is_video_subscription",
      "en_corporate_video_cost",
    ],
    topics: ["social-media", "format", "distribution"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_video_production_cost",
    language: "en",
    question: "How much does video production cost?",
    shortAnswer:
      "Video production typically costs SEK 30,000–250,000 per film depending on scope. With a Pulse subscription you instead get ongoing video production at a fixed monthly price from SEK 14,995/month.",
    longAnswer:
      "Video production is a broad term, but the price is driven by the same factors regardless of film type: number of shoot days, pre-production and scripting, whether casting or voice-over is needed, and how extensive editing, grading and versioning becomes.\n\nA simpler production with one shoot day and a single channel format usually lands in the SEK 30,000–80,000 range. A more produced film with several days, set design and multiple versions often lands in the SEK 120,000–250,000 range. Commercials with a large concept can run considerably higher.\n\nThe key difference versus a subscription is that project pricing is billed per film, while Fenny Pulse swaps quotes for a fixed monthly cost that includes strategy, production and format adaptation. For companies that need video continuously, the per-film cost drops and planning gets simpler.",
    relatedPage: "/en/video-production-pricing",
    relatedService: "Fenny Pulse",
    relatedComparison: "/en/subscription-vs-project",
    relatedQuestions: [
      "en_corporate_video_cost",
      "en_commercial_cost",
      "en_what_is_video_subscription",
    ],
    topics: ["pricing", "video-production"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_video_production_as_a_subscription",
    language: "en",
    question: "What is video production as a subscription?",
    shortAnswer:
      "Video production as a subscription means a company pays a fixed monthly fee and gets video produced on a recurring basis, instead of quoting and ordering each film as a separate project.",
    longAnswer:
      "The model flips the traditional agency process. Instead of a quote per film, you decide volume, ambition level and channels once — then the production team works in a rolling rhythm of planning, shooting and delivery.\n\nWith Fenny Pulse we set a quarterly content plan, book shoot days every month and deliver finished films in a shared folder in the formats you need. You keep the same project lead and the same team over time, which keeps tone of voice and visual language consistent.\n\nIt suits marketing teams that need video continuously for web, social, sales and advertising. It fits less well for a one-off large production — for that, a project-quoted film via Prime is usually the right route.",
    relatedPage: "/en/video-subscription",
    relatedService: "Fenny Pulse",
    relatedComparison: "/en/subscription-vs-project",
    relatedQuestions: [
      "en_what_is_video_subscription",
      "en_how_pulse_works",
      "en_video_production_cost",
    ],
    topics: ["subscription", "vaas", "model"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_subscription_vs_agency",
    language: "en",
    question: "Video subscription vs traditional video agency — which is better?",
    metaTitle: "Video subscription vs video agency | Fenny",
    shortAnswer:
      "A subscription is usually better for companies that need video continuously, since the per-film cost drops and planning is simpler. A traditional agency can be a better fit for one-off, high-craft productions.",
    longAnswer:
      "The right answer depends on how often you need video. If you need new content every month for social, web and sales, a subscription wins: no negotiating price per film, a steady delivery cadence and a team that learns your brand over time.\n\nA traditional agency typically quotes per production, which makes sense for a single, ambitious film — a commercial or brand film once a year — where the cost is tied to that specific production.\n\nMany companies combine the two: a Pulse subscription for the ongoing flow and Prime productions for the big bets. That way you get both continuity and production value without paying for capacity you don't use.",
    relatedPage: "/en/subscription-vs-project",
    relatedService: "Fenny Pulse",
    relatedComparison: "/en/subscription-vs-project",
    relatedQuestions: [
      "en_what_is_video_subscription",
      "en_pulse_vs_prime",
      "en_video_production_cost",
    ],
    topics: ["subscription", "comparison", "model"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_film_agency_vs_video_agency",
    metaDescription: "In practice both produce moving image for businesses. The difference is tone and focus — here is how to pick the right partner for your brief.",
    language: "en",
    question: "What's the difference between a film agency and a video agency?",
    shortAnswer:
      "In practice they do the same thing — produce moving image for businesses. \"Film agency\" carries a more cinematic, directed connotation, while \"video agency\" usually implies a focus on social, web and short-form content.",
    longAnswer:
      "The terms overlap almost completely. Both produce commercials, corporate video, employer branding and social content. A film agency is often associated with bigger productions, scripts, direction and broadcast-grade formats. A video agency is more often linked to faster flows of short videos for digital channels.\n\nFor most buyers the label doesn't matter. What matters is what the agency actually delivers: concept, production, editing, motion graphics and distribution. Ask to see cases that resemble what you want to make rather than getting stuck on the word choice.\n\nAt Fenny both worlds live under one roof. Pulse works as a video-agency-light for ongoing short-form, while Prime handles directed film when the brief calls for it.",
    relatedPage: "/en/film-agency",
    relatedService: "Fenny",
    relatedQuestions: [
      "en_fenny_vs_agency",
      "en_subscription_vs_agency",
      "en_video_production_cost",
    ],
    topics: ["film-agency", "video-agency", "comparison"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_image_film_cost",
    language: "en",
    question: "How much does an image film cost?",
    shortAnswer:
      "An image film typically costs SEK 80,000–350,000 depending on concept, shoot days and post-production. A simpler version using existing footage can be made from around SEK 40,000.",
    longAnswer:
      "Three things drive the price of an image film: the size of the creative idea, the number of shoot days and the amount of post-production. A simple image film with one shoot day, a voice-over and light graphics typically lands at SEK 80,000–150,000.\n\nMultiple locations, drone footage, actors, original music or animation push the budget toward SEK 200,000–350,000. Major brand productions with several days, a full crew and high production value can exceed SEK 500,000.\n\nA common way to lower the cost without losing quality is to shoot one \"hero\" plus 4–6 short versions in the same production — the cost then spreads across multiple channels. At Fenny, image films are produced either as Prime projects or continuously inside a Pulse subscription.",
    relatedPage: "/en/image-film",
    relatedService: "Fenny Prime",
    relatedQuestions: [
      "en_corporate_video_cost",
      "en_commercial_cost",
      "en_video_production_cost",
    ],
    topics: ["image-film", "price", "corporate-video"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_to_write_a_video_script",
    language: "en",
    question: "How do you write a good script for a corporate video?",
    metaTitle: "How to write a corporate video script | Fenny",
    shortAnswer:
      "Start with the audience and a single core idea, then write a brief scene list before you write any voice-over or dialogue. Keep it short, lead with a hook in the first five seconds and end with a concrete call-to-action.",
    longAnswer:
      "A good corporate video script doesn't start with words — it starts with an idea. First define who the film is for, what you want the viewer to do afterwards and the one message that absolutely has to land. Everything else is built around that.\n\nNext, write a two-column scene list: left for picture (what do we see?), right for sound (what do we hear?). This helps both you and the production company see the rhythm of the film before a single sentence is written.\n\nWhen you write the actual lines: keep sentences short, speak to one person, not to \"all our customers\". Put the strongest hook in the first five seconds because most viewers drop off there. End with a clear next step.\n\nFenny includes script support as standard in both Pulse and Prime, so you don't have to write the script yourself — but the clearer the brief you come in with, the faster the final film lands.",
    relatedPage: "/en/process",
    relatedService: "Fenny",
    relatedQuestions: [
      "en_how_to_order_video",
      "en_social_media_video_formats",
      "en_video_production_time",
    ],
    topics: ["script", "production", "process"],
    schemaType: "HowTo",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_what_is_internal_comms_video",
    language: "en",
    question: "What is an internal communications video?",
    shortAnswer:
      "An internal communications video is a video produced for employees rather than the market — used to convey strategy, change, values or training more clearly and engagingly than text.",
    longAnswer:
      "Internal communications video is used when a company needs to get a message through that would otherwise get lost in email or the intranet. Common use cases are CEO quarterly updates, culture film for onboarding, change communication during reorganisations, and training videos tied to new systems or processes.\n\nThe style is usually more direct and stripped-back than public commercials. The people on screen are real employees, not actors, and the message is prioritised over production value. The films are distributed via intranet, Teams, Slack or LMS.\n\nThe benefit is twofold: employees absorb the message faster, and leadership gets a consistent voice that doesn't get lost in second-hand retelling. At Fenny, internal communications video is typically produced continuously inside Pulse, since the need is recurring rather than a one-off project.",
    relatedPage: "/en/internal-communication-video",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "en_what_is_video_subscription",
      "en_how_pulse_works",
      "en_corporate_video_cost",
    ],
    topics: ["internal-comms", "corporate-video", "hr"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_motion_graphics_vs_animation",
    metaDescription: "Motion graphics is animated graphic design; animation also covers characters and storytelling. Here is when to use which, and what each costs.",
    language: "en",
    question: "What's the difference between motion graphics and animation?",
    shortAnswer:
      "Motion graphics is animated graphic design, typography and information design — think explainer logos, icons and text in motion. Animation is a broader term that also includes character animation and storytelling in 2D or 3D.",
    longAnswer:
      "Motion graphics is about putting graphic elements in motion: logos that build, icons that explain a flow, numbers that count up or text that follows a voice-over. It's used heavily in explainer corporate video, product demos, social media and as lower-thirds in interview films.\n\nAnimation is a wider umbrella. It includes motion graphics, but also character animation, 2D illustrations that tell a story, and full 3D animation of products or environments. Animation more often requires script, storyboard and a longer production timeline.\n\nSimple rule of thumb: if you want to explain something quickly and clearly — pick motion graphics. If you want to build a world or a character the viewer should identify with — pick animation. Fenny delivers both, often combined with live action.",
    relatedPage: "/en/motion-graphics",
    relatedService: "Fenny",
    relatedQuestions: [
      "en_corporate_video_cost",
      "en_video_production_cost",
      "en_social_media_video_formats",
    ],
    topics: ["motion-graphics", "animation", "technique"],
    schemaType: "FAQPage",
    priority: "low",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_youtube_seo_for_video",
    language: "en",
    question: "How do you optimise a video so it gets found on YouTube?",
    metaTitle: "YouTube SEO — get your video found | Fenny",
    shortAnswer:
      "Optimise the title, thumbnail and first 125 characters of the description — that's where 80% of the click decision is made. Then put a clear hook in the first 10 seconds and add chapter timestamps for longer videos.",
    longAnswer:
      "YouTube SEO starts long before the film is done. Decide on keywords already in the script phase and build the title around what your audience actually searches for, not your internal campaign name. Short, concrete titles (under 60 characters) perform best.\n\nThe thumbnail is decisive. Design a custom thumbnail with a large face, high contrast and at most three words of text. It's the single factor that most directly determines click-through.\n\nIn the description, only the first 125 characters show before \"show more\" — put a sentence there that repeats the keyword and a link to your own site. Use chapter timestamps (0:00, 0:45, etc.) for videos over 3 minutes; that lets Google index individual segments.\n\nFinally: don't rely on tags. They have minimal impact today. What actually drives views is retention — how long people stay. So write the film so the first 10 seconds hold the viewer.",
    relatedPage: "/en/video-for-youtube",
    relatedService: "Fenny Pulse",
    relatedQuestions: [
      "en_social_media_video_formats",
      "en_video_production_time",
      "en_what_is_video_subscription",
    ],
    topics: ["youtube", "seo", "distribution"],
    schemaType: "HowTo",
    priority: "medium",
    lastReviewed: REVIEWED,
  },

  // -------------------------------------------------------------------------
  // Heritage (EN)
  // -------------------------------------------------------------------------
  {
    id: "en_what_is_fenny_heritage",
    language: "en",
    question: "What is Fenny Heritage?",
    metaTitle: "What is Fenny Heritage? Life stories on film",
    shortAnswer:
      "Fenny Heritage is a service for filmed interviews where individuals, families, companies and organisations can document life stories, experience and knowledge in professional form.",
    longAnswer:
      "Heritage covers two related needs: life stories for families and knowledge legacy for organisations. The shared core is structured, filmed interviews made with craft and respect.\n\nFor individuals and families we document life stories, family history and personal portraits. For companies and organisations we document founder stories, experience, methods, culture and generational handovers.\n\nWe plan, interview, edit and deliver digitally. Smaller formats start in the lower Prime range; more comprehensive legacies are higher. Pricing is provided after a first conversation.",
    relatedPage: "/en/heritage",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "en_what_does_a_filmed_life_story_cost",
      "en_difference_life_story_memoir",
      "en_how_does_filmed_knowledge_transfer_work",
    ],
    topics: ["heritage", "interview", "documentation"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_what_does_a_filmed_life_story_cost",
    language: "en",
    question: "What does a filmed life story cost?",
    shortAnswer:
      "A filmed life story typically costs SEK 35,000–150,000 depending on scope — from a single interview to a larger family legacy with multiple interviews, archive material and chapter structure.",
    longAnswer:
      "Pricing is driven by how many people are interviewed, how long the recording is, whether archive material (photos, documents, film) is woven in, and how extensive the final material is — a main film, chapters, short excerpts or a combination.\n\nThe Life Interview is our smallest format: a pre-meeting, a question outline, one filmed interview and an edited film. The Family Legacy is more extensive with a longer interview and chapter structure. The Legacy package builds a more complete filmed legacy with multiple interviews and a clear narrative structure.\n\nWe provide a quote after a first conversation. All material is delivered digitally and can remain private within the family if you prefer.",
    relatedPage: "/en/heritage/livsberattelser",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "en_what_is_fenny_heritage",
      "en_how_long_does_a_filmed_life_story_take",
      "en_difference_life_story_memoir",
    ],
    topics: ["heritage", "life-story", "pricing"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_long_does_a_filmed_life_story_take",
    language: "en",
    question: "How long does a filmed life story take to produce?",
    shortAnswer:
      "From first conversation to finished film, the process usually takes 4–8 weeks, depending on scope, number of interviews and how much archive material is processed.",
    longAnswer:
      "The timeline has four phases: planning, recording, editing and review. Smaller formats with a single interview and no archive material can be finished in four weeks. Larger family legacies with multiple interviews and gathered material take two months or more.\n\nWe match the pace to the person being interviewed — rushing a life story is never a good idea. In health situations where time is short, we prioritise and can start recording within a week.\n\nThe person being interviewed always reviews the material before delivery.",
    relatedPage: "/en/heritage/livsberattelser",
    relatedQuestions: [
      "en_what_does_a_filmed_life_story_cost",
      "en_who_is_heritage_for",
    ],
    topics: ["heritage", "life-story", "process"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_how_does_filmed_knowledge_transfer_work",
    language: "en",
    question: "How does filmed knowledge transfer work?",
    shortAnswer:
      "Filmed knowledge transfer is structured interviews with key people that document experience, methods and tacit knowledge into chaptered material usable for onboarding, internal training and culture work.",
    longAnswer:
      "We start by mapping what actually needs to be preserved — which decisions, situations, methods and perspectives risk being lost when a person leaves the organisation. A document is rarely enough.\n\nInterviews are structured into thematic chapters: background, method, typical situations, pitfalls, principles and advice for a successor. The material is edited into a main film plus shorter chapter clips that are easier to use in onboarding and internal training.\n\nCommon use cases are retirements, generational handovers, founder transitions and documentation of critical roles. The material is owned by the organisation and handled under confidentiality.",
    relatedPage: "/en/heritage/kunskapsarv",
    relatedService: "Fenny Heritage",
    relatedQuestions: [
      "en_what_is_fenny_heritage",
      "en_who_is_heritage_for",
    ],
    topics: ["heritage", "knowledge-legacy", "onboarding"],
    schemaType: "FAQPage",
    priority: "high",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_difference_life_story_memoir",
    language: "en",
    question: "What is the difference between a filmed life story and a memoir book?",
    metaTitle: "Filmed life story vs memoir book | Fenny",
    shortAnswer:
      "A filmed life story preserves the voice, face and way of telling — a memoir book preserves the text. The film lets future generations actually meet the person, not just read about them.",
    longAnswer:
      "A memoir book requires the person (or a writer) to construct a chronological narrative in text. It is a large undertaking and often demanding for an older person.\n\nA filmed life story is built on conversation. We ask the questions; the person answers at their own pace. The result preserves body language, voice, humour and the memories that surface spontaneously — things that rarely fit on the page.\n\nMany families combine both: a filmed life story as the primary documentation, complemented by a transcript or book if readable material is also wanted.",
    relatedPage: "/en/heritage/livsberattelser",
    relatedQuestions: [
      "en_what_does_a_filmed_life_story_cost",
      "en_what_is_fenny_heritage",
    ],
    topics: ["heritage", "life-story", "comparison"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
  {
    id: "en_who_is_heritage_for",
    metaDescription: "Fenny Heritage is for families and companies that want to preserve a life story, tacit knowledge or a company history on film before it disappears.",
    language: "en",
    question: "Who is Fenny Heritage for?",
    metaTitle: "Who is Fenny Heritage for? Families and firms",
    shortAnswer:
      "Heritage is for families who want to preserve a loved one's story, and for companies or organisations that want to document experience, company history, culture and tacit knowledge before it is lost.",
    longAnswer:
      "On the personal side, the service is for families who want to preserve a parent's, grandparent's or other loved one's life story. It often becomes relevant around milestones, health situations or when someone retires and has the time to tell their story.\n\nOn the business side, it is for organisations with critical experience concentrated in a few people: retirements, generational handovers, founder transitions, specialist roles and culture work. The material is used for onboarding, internal training, anniversaries and intranet content.\n\nThe common thread: when it matters to preserve stories, knowledge or culture in a form where the voice and person actually remain.",
    relatedPage: "/en/heritage",
    relatedQuestions: [
      "en_what_is_fenny_heritage",
      "en_how_does_filmed_knowledge_transfer_work",
    ],
    topics: ["heritage", "audience"],
    schemaType: "FAQPage",
    priority: "medium",
    lastReviewed: REVIEWED,
  },
];



// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Keep photo answers in source for a future relaunch, but do not publish or index them.
const activeAnswer = (a: FennyAnswer) =>
  !/^\/(?:foto|en\/photography)(?:\/|$)/.test(a.relatedPage) &&
  a.relatedPage !== "/branschstudier/bildindex-2026" &&
  !/(?:fotograf|fotografering|photo|photography|bildindex|headshot|studiofoto|produktfoto|dronarfoto)/i.test(a.question);

export const FENNY_ANSWERS: ReadonlyArray<FennyAnswer> = [
  ...SV_ANSWERS,
  ...EN_ANSWERS,
].filter(activeAnswer);

const BY_ID = new Map(FENNY_ANSWERS.map((a) => [a.id, a]));

export function getAnswer(id: string): FennyAnswer | undefined {
  return BY_ID.get(id);
}

export function getAnswers(ids: string[]): FennyAnswer[] {
  return ids
    .map((id) => BY_ID.get(id))
    .filter((a): a is FennyAnswer => Boolean(a));
}

export function getAnswersByLanguage(language: Language): FennyAnswer[] {
  return FENNY_ANSWERS.filter((a) => a.language === language);
}

export function getAnswersByService(servicePath: string): FennyAnswer[] {
  return FENNY_ANSWERS.filter((a) => a.relatedPage === servicePath);
}

export function getAnswersByTopic(topic: string): FennyAnswer[] {
  return FENNY_ANSWERS.filter((a) => a.topics.includes(topic));
}

/** Build a FAQPage JSON-LD block from a list of answer ids. */
export function toFaqJsonLd(ids: string[]): Record<string, unknown> {
  const answers = getAnswers(ids);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: answers.map((a) => ({
      "@type": "Question",
      name: a.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: a.shortAnswer,
      },
    })),
  };
}

/** Build a QAPage JSON-LD block for a single answer (for /fragor/* routes). */
export function toQaPageJsonLd(id: string, url: string): Record<string, unknown> | null {
  const a = getAnswer(id);
  if (!a) return null;
  return {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: a.question,
      text: a.question,
      answerCount: 1,
      dateCreated: a.lastReviewed,
      url,
      acceptedAnswer: {
        "@type": "Answer",
        text: a.longAnswer,
        dateCreated: a.lastReviewed,
        url,
        author: { "@type": "Organization", name: "Fenny", url: "https://fenny.se" },
      },
    },
    // Speakable: hjälper röstassistenter och AI-svar att citera rätt block.
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-quick-answer]"],
    },
    dateModified: a.lastReviewed,
  };
}

/**
 * Build a HowTo JSON-LD block for a single answer (for HowTo /fragor/* routes).
 * Steps are parsed from a numbered list inside longAnswer ("1. ... 2. ...").
 * Returns null when the answer isn't a HowTo or has no parseable steps.
 */
export function toHowToJsonLd(id: string, url: string): Record<string, unknown> | null {
  const a = getAnswer(id);
  if (!a || a.schemaType !== "HowTo") return null;
  // Find the paragraph that contains the numbered steps.
  const stepParagraph = a.longAnswer
    .split("\n\n")
    .find((p) => /(^|\s)1\.\s/.test(p));
  if (!stepParagraph) return null;
  const steps = stepParagraph
    .split(/(?=\b\d+\.\s)/)
    .map((s) => s.trim())
    .filter((s) => /^\d+\.\s/.test(s))
    .map((s) => s.replace(/^\d+\.\s/, "").trim());
  if (steps.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: a.question,
    description: a.shortAnswer,
    url,
    step: steps.map((text, i) => {
      const [namePart] = text.split(":");
      return {
        "@type": "HowToStep",
        position: i + 1,
        name: namePart.trim(),
        text,
        url: `${url}#step-${i + 1}`,
      };
    }),
  };
}

/**
 * Stable URL slug for an answer's /fragor/<slug> page.
 * Derived deterministically from the id: strip the language prefix and
 * convert underscores to hyphens (e.g. "sv_vad_ar_fenny" -> "vad-ar-fenny").
 */
export function getAnswerSlug(a: FennyAnswer): string {
  return a.id.replace(/^(sv|en)_/, "").replace(/_/g, "-");
}

const BY_SLUG = new Map(
  FENNY_ANSWERS.map((a) => [`${a.language}:${getAnswerSlug(a)}`, a]),
);

/** Resolve an answer from its slug + language (defaults to Swedish). */
export function getAnswerBySlug(
  slug: string,
  language: Language = "sv",
): FennyAnswer | undefined {
  return BY_SLUG.get(`${language}:${slug}`);
}

/** All Swedish answer slugs — used for /fragor hub + sitemap generation. */
export function getQuestionSlugs(language: Language = "sv"): string[] {
  return getAnswersByLanguage(language).map(getAnswerSlug);
}

/* ------------------------------------------------------------------ *
 * SEO-titel
 *
 * Rubriken på sidan är frågan. Om sidtiteln är exakt samma sträng får
 * sidan ingen extra signal i SERP:en (byrån flaggar "Page title same as
 * H1"). Därför får varje fråga utan manuell metaTitle ett kort tillägg
 * som beskriver svarets nytta, härlett ur ämnestaggarna.
 * ------------------------------------------------------------------ */

const TITLE_QUALIFIER_SV: Array<[string, string]> = [
  ["pris", "Priser och prisspann 2026"],
  ["kostnad", "Priser och prisspann 2026"],
  ["heritage", "Fenny Heritage"],
  ["foto", "Fotografering för företag"],
  ["process", "Så går det till"],
  ["abonnemang", "Film på abonnemang"],
  ["pulse", "Film på abonnemang"],
  ["prime", "Projektproduktion"],
  ["format", "Format och längder"],
  ["manus", "Manus och förproduktion"],
  ["dronare", "Drönarfilm"],
  ["b2b", "Film för B2B"],
];

const TITLE_QUALIFIER_EN: Array<[string, string]> = [
  ["pris", "Prices and ranges 2026"],
  ["kostnad", "Prices and ranges 2026"],
  ["heritage", "Fenny Heritage"],
  ["foto", "Corporate photography"],
  ["process", "How it works"],
  ["abonnemang", "Video on subscription"],
  ["pulse", "Video on subscription"],
  ["prime", "Project production"],
  ["format", "Formats and lengths"],
];

function qualifierFor(a: FennyAnswer): string {
  const table = a.language === "en" ? TITLE_QUALIFIER_EN : TITLE_QUALIFIER_SV;
  const haystack = [...a.topics, a.id].join(" ").toLowerCase();
  for (const [needle, qualifier] of table) {
    if (haystack.includes(needle)) return qualifier;
  }
  return a.language === "en" ? "Video production in Sweden" : "Filmproduktion i Sverige";
}

/** Sidtitel för /fragor/<slug> och /en/questions/<slug>, aldrig lika med H1. */
export function answerSeoTitle(a: FennyAnswer): string {
  if (a.metaTitle) return a.metaTitle;
  const question = a.question.trim();
  const rawQualifier = qualifierFor(a);
  const short = a.language === "en" ? "answered" : "svar 2026";
  // Frågan nämner redan varumärket — upprepa det inte i tillägg eller suffix.
  const hasBrand = /\bfenny/i.test(question);
  const qualifier = hasBrand ? rawQualifier.replace(/\s*Fenny\s*/gi, " ").trim() : rawQualifier;
  // Testa i tur och ordning: fullt tillägg + varumärke, fullt tillägg,
  // kort tillägg + varumärke, kort tillägg. Titeln får aldrig bli exakt
  // samma sträng som sidans H1 (frågan).
  const candidates = (
    hasBrand
      ? [`${question} ${qualifier}`, `${question} — ${short}`]
      : [
          `${question} ${qualifier} | Fenny`,
          `${question} ${qualifier}`,
          `${question} — ${short} | Fenny`,
          `${question} — ${short}`,
        ]
  ).map((value) => value.replace(/\s+/g, " ").trim());
  for (const candidate of candidates) {
    if (candidate.length <= 60) return candidate;
  }
  // Frågan är i sig längre än SERP-gränsen — behåll den och markera svaret.
  return `${question} — ${short}`;
}

